/* global kakao */

import React, { useEffect } from 'react';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { TransformControls } from 'three/examples/jsm/controls/TransformControls.js';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import {
  CSS3DRenderer,
  CSS3DObject,
} from 'three/examples/jsm/renderers/CSS3DRenderer.js';

/*
 * 1:1 React conversion of the working seller.html.
 * - No redirect to public HTML.
 * - Existing DOM ids/classes and 3D logic are preserved.
 * - Three.js and addons are resolved by the React/Webpack project.
 * - The old relative GLB URL is preserved as its effective /farm3d/ URL.
 */

const PAGE_CSS = '* {\n  box-sizing: border-box;\n}\n\nhtml,\nbody {\n  margin: 0;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n\n  font-family:\n    -apple-system,\n    BlinkMacSystemFont,\n    "Segoe UI",\n    sans-serif;\n\n  background: #e9eef2;\n}\n\n#viewer {\n  position: relative;\n  width: 100%;\n  height: 100%;\n  overflow: hidden;\n}\n\n\n/* =========================================================\n   TOP TITLE\n========================================================= */\n\n#titleBox {\n  position: absolute;\n  top: 16px;\n  left: 16px;\n  z-index: 10;\n\n  width: 143px;\n  padding: 11px 15px;\n\n  background: rgba(255,255,255,0.94);\n\n  border-radius: 12px;\n\n  box-shadow:\n    0 3px 15px rgba(0,0,0,0.14);\n}\n\n#titleBox .title {\n  font-size: 17px;\n  font-weight: 700;\n}\n\n#systemStatus {\n  margin-top: 3px;\n\n  font-size: 12px;\n  color: #666;\n  white-space: nowrap;\n  overflow: hidden;\n  text-overflow: ellipsis;\n}\n\n\n/* =========================================================\n   SELECTED ZONE PANEL\n========================================================= */\n\n#zonePanel {\n  display: none;\n\n  position: absolute;\n\n  top: 16px;\n  right: 16px;\n\n  z-index: 20;\n\n  width: 290px;\n\n  background:\n    rgba(255,255,255,0.97);\n\n  border-radius: 14px;\n\n  box-shadow:\n    0 4px 20px rgba(0,0,0,0.18);\n\n  overflow: hidden;\n}\n\n\n/* =========================================================\n   PANEL HEADER\n========================================================= */\n\n#zoneHeader {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  padding: 14px 16px;\n\n  border-bottom:\n    1px solid #eee;\n}\n\n#zoneTitle {\n  font-size: 18px;\n  font-weight: 700;\n}\n\n#closePanel {\n  width: 30px;\n  height: 30px;\n\n  border: 0;\n\n  border-radius: 50%;\n\n  background: #f1f3f5;\n\n  cursor: pointer;\n\n  font-size: 18px;\n}\n\n\n/* =========================================================\n   PANEL CONTENT\n========================================================= */\n\n#zoneContent {\n  padding: 16px;\n}\n\n\n/* =========================================================\n   STATE\n========================================================= */\n\n.stateBox {\n  display: flex;\n  justify-content: space-between;\n  align-items: center;\n\n  margin-bottom: 15px;\n\n  padding-bottom: 13px;\n\n  border-bottom:\n    1px solid #eee;\n}\n\n.stateLabel {\n  font-size: 13px;\n  color: #666;\n}\n\n#zoneState {\n  font-size: 15px;\n  font-weight: 700;\n}\n\n#zoneState.on {\n  color: #198754;\n}\n\n#zoneState.off {\n  color: #777;\n}\n\n\n/* =========================================================\n   SENSOR INFORMATION\n========================================================= */\n\n.infoRow {\n  display: flex;\n  justify-content: space-between;\n\n  padding: 8px 0;\n\n  font-size: 14px;\n}\n\n.infoLabel {\n  color: #666;\n}\n\n.infoValue {\n  font-weight: 600;\n}\n\n\n/* =========================================================\n   CONTROL BUTTONS\n========================================================= */\n\n.controlButtons {\n  display: flex;\n\n  gap: 8px;\n\n  margin-top: 16px;\n}\n\n.controlButtons button {\n  flex: 1;\n\n  border: 0;\n\n  border-radius: 9px;\n\n  padding: 12px 8px;\n\n  font-size: 14px;\n\n  font-weight: 700;\n\n  cursor: pointer;\n}\n\n#startWater {\n  background: #198754;\n  color: white;\n}\n\n#stopWater {\n  background: #dc3545;\n  color: white;\n}\n\n\n/* =========================================================\n   MESSAGE\n========================================================= */\n\n#message {\n  margin-top: 12px;\n\n  min-height: 17px;\n\n  font-size: 12px;\n\n  color: #666;\n\n  text-align: center;\n}\n\n\n/* =========================================================\n   HELP\n========================================================= */\n\n#help {\n  position: absolute;\n\n  left: 50%;\n  bottom: 18px;\n\n  transform:\n    translateX(-50%);\n\n  z-index: 10;\n\n  padding: 8px 14px;\n\n  background:\n    rgba(0,0,0,0.65);\n\n  color: white;\n\n  border-radius: 20px;\n\n  font-size: 12px;\n\n  pointer-events: none;\n}\n\n\n/* =========================================================\n   MOBILE\n========================================================= */\n\n@media (max-width: 600px) {\n\n  #titleBox {\n    top: 8px;\n    left: 8px;\n\n    padding: 9px 12px;\n  }\n\n  #titleBox .title {\n    font-size: 15px;\n  }\n\n  #zonePanel {\n    top: auto;\n\n    right: 8px;\n    left: 8px;\n    bottom: 8px;\n\n    width: auto;\n  }\n\n  #zoneContent {\n    padding: 13px;\n  }\n\n  .infoRow {\n    padding: 5px 0;\n  }\n\n  #help {\n    bottom: auto;\n    top: 75px;\n\n    white-space: nowrap;\n  }\n\n}\n\n    /* 3D ESP32 배치 편집기 */\n    #farmEditButton{position:absolute;top:82px;left:20px;z-index:30;border:1px solid rgba(255,255,255,.24);background:rgba(15,23,42,.88);color:#fff;border-radius:10px;padding:9px 14px;font-size:14px;font-weight:700;cursor:pointer}\n    #farmEditor{position:absolute;top:126px;left:20px;z-index:29;width:min(360px,calc(100vw - 40px));max-height:calc(100vh - 92px);overflow-y:auto;display:none;background:rgba(15,23,42,.94);color:#fff;border:1px solid rgba(255,255,255,.16);border-radius:14px;padding:14px;box-shadow:0 16px 44px rgba(0,0,0,.32)}\n    #farmEditor.open{display:block}.farm-editor-title{font-size:16px;font-weight:800;margin-bottom:4px}.farm-editor-help{font-size:12px;line-height:1.45;color:rgba(255,255,255,.68);margin-bottom:12px}.farm-editor-section-title{margin:14px 0 7px;font-size:12px;font-weight:800;color:rgba(255,255,255,.72)}.farm-device-list{display:grid;gap:8px}.farm-device-card,.farm-binding-card{width:100%;border:1px solid rgba(255,255,255,.14);border-radius:10px;background:rgba(255,255,255,.06);color:#fff;padding:10px;text-align:left}button.farm-device-card{cursor:pointer}button.farm-device-card.selected{border-color:#38bdf8;background:rgba(56,189,248,.18)}.farm-device-name{font-size:13px;font-weight:800}.farm-device-meta{margin-top:3px;font-size:11px;color:rgba(255,255,255,.62)}.farm-binding-row{display:flex;gap:8px;align-items:center;justify-content:space-between}.farm-binding-remove{border:1px solid rgba(248,113,113,.5);color:#fecaca;background:rgba(127,29,29,.28);border-radius:8px;padding:5px 8px;cursor:pointer;font-size:11px}#farmEditorStatus{margin-top:12px;min-height:18px;font-size:12px;color:#bae6fd}.farm-empty{border:1px dashed rgba(255,255,255,.16);border-radius:10px;padding:10px;font-size:12px;color:rgba(255,255,255,.58)}body.farm-edit-mode canvas{cursor:crosshair}\n    @media(max-width:700px){#farmEditButton{top:76px;left:12px}#farmEditor{top:120px;left:12px;width:calc(100vw - 24px);max-height:52vh}}\n\n\n    /* =========================================================\n       3D OBJECT POSITION EDITOR\n    ========================================================= */\n    #objectMovePanel{\n      margin-top:12px;padding-top:12px;border-top:1px solid rgba(255,255,255,.14);\n    }\n    .object-editor-title{font-size:12px;font-weight:800;color:rgba(255,255,255,.72);margin-bottom:7px}\n    #movableObjectList{display:grid;gap:7px}\n    .movable-object-card{width:100%;border:1px solid rgba(255,255,255,.14);border-radius:9px;background:rgba(255,255,255,.06);color:#fff;padding:9px;text-align:left;cursor:pointer}\n    .movable-object-card.selected{border-color:#38bdf8;background:rgba(56,189,248,.18)}\n    .object-move-actions{display:grid;grid-template-columns:repeat(3,1fr);gap:6px;margin-top:8px}\n    .object-move-actions button{border:1px solid rgba(255,255,255,.16);border-radius:8px;background:rgba(255,255,255,.08);color:#fff;padding:8px 4px;cursor:pointer;font-size:12px}\n    .object-move-actions button:disabled{opacity:.35;cursor:not-allowed}\n    .object-move-info{font-size:11px;line-height:1.5;color:rgba(255,255,255,.62);margin-top:7px}\n\n\n    /* =========================================================\n       FARM SATELLITE / BOUNDARY EDITOR\n    ========================================================= */\n    #farmMapButton{\n      position:absolute;top:54px;left:170px;z-index:30;\n      width:118px;height:38px;display:flex;align-items:center;justify-content:center;\n      border:1px solid rgba(255,255,255,.24);background:rgba(15,23,42,.88);\n      color:#fff;border-radius:10px;padding:0 8px;font-size:14px;font-weight:700;cursor:pointer;white-space:nowrap\n    }\n    #farmMapModal{\n      position:fixed;inset:0;z-index:1000;display:none;background:rgba(2,6,23,.82);\n      align-items:center;justify-content:center;padding:18px\n    }\n    #farmMapModal.open{display:flex}\n    .farm-map-shell{\n      width:min(1180px,100%);height:min(820px,94vh);background:#0f172a;border:1px solid rgba(255,255,255,.18);\n      border-radius:16px;overflow:hidden;display:flex;flex-direction:column;box-shadow:0 25px 80px rgba(0,0,0,.45)\n    }\n    .farm-map-toolbar{display:flex;gap:8px;align-items:center;flex-wrap:wrap;padding:12px;background:#111827;color:#fff}\n    .farm-map-toolbar strong{margin-right:8px}\n    .farm-map-toolbar input{\n      min-width:230px;flex:1;border:1px solid rgba(255,255,255,.18);border-radius:8px;padding:8px 10px;background:#fff;color:#111827\n    }\n    .farm-map-toolbar button{\n      border:1px solid rgba(255,255,255,.18);border-radius:8px;padding:8px 10px;background:#1f2937;color:#fff;cursor:pointer\n    }\n    .farm-map-toolbar button.primary{background:#0369a1}\n    .farm-map-toolbar button.danger{background:#7f1d1d}\n    #farmKakaoMap{flex:1;min-height:420px}\n    #farmMapInfo{padding:9px 12px;background:#111827;color:#bae6fd;font-size:12px}\n    @media(max-width:700px){\n      #farmMapButton{top:76px;left:92px}\n      #farmMapModal{padding:6px}\n      .farm-map-shell{height:96vh}\n      .farm-map-toolbar input{min-width:100%;order:2}\n    }\n\n\n    #viewer{\n      position:fixed;\n      left:0;\n      top:0;\n      width:100vw;\n      height:var(--konggomul-vvh, 100dvh);\n      overflow:hidden;\n      background:#dfe7ec;\n    }\n    #mainSatelliteMap{\n      position:absolute;\n      width:1024px;\n      height:1024px;\n      left:0;\n      top:0;\n      display:block;\n      background:transparent;\n      backface-visibility:visible;\n      pointer-events:none !important;\n    }\n    #mainSatelliteMap *{\n      pointer-events:none !important;\n    }\n    #viewer .css3d-layer{\n      position:absolute !important;\n      left:0 !important;\n      top:0 !important;\n      width:100% !important;\n      height:100% !important;\n      z-index:0;\n      pointer-events:none !important;\n      transform:none !important;\n      transform-origin:0 0 !important;\n    }\n    #viewer .css3d-layer *{\n      pointer-events:none !important;\n    }\n    #viewer > canvas{\n      position:absolute !important;\n      inset:0 !important;\n      width:100% !important;\n      height:100% !important;\n      z-index:1 !important;\n      background:transparent !important;\n      pointer-events:auto !important;\n      touch-action:none !important;\n    }\n    body.real-farm-mode{background:#dfe7ec !important}\n\n\n\n/* =========================================================\n   SMART FARM INITIAL DESIGN WIZARD\n========================================================= */\n#farmDesignStartModal{position:fixed;inset:0;z-index:3000;display:none;align-items:center;justify-content:center;padding:18px;background:rgba(2,6,23,.82);backdrop-filter:blur(5px)}\n#farmDesignStartModal.open{display:flex}\n.farm-design-start-card{width:min(720px,100%);background:#fff;border-radius:20px;padding:26px;box-shadow:0 28px 90px rgba(0,0,0,.38);color:#111827}\n.farm-design-start-title{font-size:25px;font-weight:900;margin-bottom:8px}\n.farm-design-start-desc{font-size:14px;line-height:1.65;color:#64748b;margin-bottom:20px}\n.farm-design-choice-grid{display:grid;grid-template-columns:1fr 1fr;gap:14px}\n.farm-design-choice{border:1px solid #dbe3ea;border-radius:16px;padding:20px;background:#f8fafc;text-align:left;cursor:pointer;transition:.16s}\n.farm-design-choice:hover{transform:translateY(-2px);border-color:#38bdf8;box-shadow:0 10px 26px rgba(15,23,42,.10)}\n.farm-design-choice strong{display:block;font-size:18px;margin-bottom:7px}\n.farm-design-choice span{display:block;font-size:13px;line-height:1.55;color:#64748b}\n.farm-design-choice.primary{background:#eff6ff;border-color:#93c5fd}\n#farmDesignBadge{position:absolute;left:170px;top:16px;z-index:28;display:none;padding:7px 10px;border-radius:9px;background:rgba(3,105,161,.92);color:#fff;font-size:12px;font-weight:800}\n#farmDesignResetButton{position:absolute;top:54px;left:296px;z-index:30;width:118px;height:38px;display:flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.24);background:rgba(127,29,29,.88);color:#fff;border-radius:10px;padding:0 8px;font-size:14px;font-weight:700;cursor:pointer;white-space:nowrap}\n#farmDesignResetButton:hover{background:rgba(153,27,27,.96)}\nbody.custom-design-mode #farmEditButton{display:none}\nbody.custom-design-mode #zonePanel{display:none !important}\n@media(max-width:650px){.farm-design-choice-grid{grid-template-columns:1fr}.farm-design-start-card{padding:20px}.farm-design-start-title{font-size:21px}#farmDesignBadge{left:12px;top:118px}#farmDesignResetButton{top:118px;right:12px;left:auto;padding:7px 10px;font-size:12px}}\n\n\n/* =========================================================\n   CUSTOM SMART FARM 3D DESIGNER V3\n========================================================= */\n#customDesignerPanel{position:absolute;top:126px;left:16px;z-index:35;width:min(370px,calc(100vw - 32px));max-height:calc(100vh - 150px);overflow:auto;display:none;background:rgba(15,23,42,.95);color:#fff;border:1px solid rgba(255,255,255,.16);border-radius:15px;padding:14px;box-shadow:0 18px 50px rgba(0,0,0,.34)}\nbody.custom-design-mode.custom-design-editing #customDesignerPanel{display:block}\n.custom-designer-title{font-size:17px;font-weight:900;margin-bottom:4px}.custom-designer-help{font-size:11px;line-height:1.55;color:rgba(255,255,255,.65);margin-bottom:12px}.custom-designer-section{font-size:12px;font-weight:900;color:#bae6fd;margin:13px 0 7px}.custom-tool-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:7px}.custom-tool-grid button,.custom-edit-grid button{border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.07);color:#fff;border-radius:9px;padding:9px 5px;font-size:11px;font-weight:700;cursor:pointer}.custom-tool-grid button.active{border-color:#38bdf8;background:rgba(14,165,233,.28)}.custom-edit-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:7px}.custom-danger{border-color:rgba(248,113,113,.45)!important;background:rgba(127,29,29,.30)!important}.custom-primary{border-color:rgba(56,189,248,.55)!important;background:rgba(3,105,161,.45)!important}.custom-object-list{display:grid;gap:6px;max-height:160px;overflow:auto}.custom-object-item{display:flex;align-items:center;justify-content:space-between;gap:8px;border:1px solid rgba(255,255,255,.12);border-radius:8px;padding:8px;background:rgba(255,255,255,.05);font-size:11px}.custom-object-item button{flex:1;text-align:left;border:0;background:transparent;color:#fff;cursor:pointer}.custom-object-item.selected{border-color:#38bdf8;background:rgba(56,189,248,.16)}#customDesignerStatus{margin-top:10px;min-height:32px;padding:8px;border-radius:8px;background:rgba(2,132,199,.12);font-size:11px;line-height:1.45;color:#bae6fd}.custom-pipe-row{display:grid;grid-template-columns:1fr 1fr 1fr;gap:7px}.custom-pipe-row button{border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.07);color:#fff;border-radius:9px;padding:9px 4px;font-size:11px;font-weight:700;cursor:pointer}.custom-pipe-row button.active{border-color:#38bdf8;background:rgba(14,165,233,.28)}\n@media(max-width:700px){#customDesignerPanel{top:158px;left:8px;width:calc(100vw - 16px);max-height:50vh}.custom-tool-grid{grid-template-columns:repeat(3,1fr)}}\n\n\n/* =========================================================\n   CUSTOM DESIGN -> DEVICE CONNECTION V4\n========================================================= */\n#customDesignActions{position:absolute;top:54px;left:422px;z-index:34;display:none;gap:8px;height:38px}\nbody.custom-design-mode.custom-design-completed #customDesignActions{display:flex}\n#customDesignActions button{width:118px;height:38px;display:flex;align-items:center;justify-content:center;border:1px solid rgba(255,255,255,.24);color:#fff;border-radius:10px;padding:0 8px;font-size:14px;font-weight:800;cursor:pointer;box-shadow:0 5px 16px rgba(0,0,0,.18);white-space:nowrap}\n#customDesignEditButton{background:rgba(30,41,59,.92)}\n#customDeviceConnectButton{background:rgba(3,105,161,.94)}\n#customDevicePanel{position:absolute;top:126px;left:16px;z-index:38;width:min(390px,calc(100vw - 32px));max-height:calc(100vh - 150px);overflow:auto;display:none;background:rgba(15,23,42,.96);color:#fff;border:1px solid rgba(255,255,255,.16);border-radius:15px;padding:14px;box-shadow:0 18px 50px rgba(0,0,0,.38)}\nbody.custom-device-connect-mode #customDevicePanel{display:block}\n.custom-device-title{font-size:17px;font-weight:900;margin-bottom:4px}.custom-device-help{font-size:11px;line-height:1.55;color:rgba(255,255,255,.68);margin-bottom:12px}.custom-device-section{font-size:12px;font-weight:900;color:#bae6fd;margin:13px 0 7px}.custom-device-card{border:1px solid rgba(255,255,255,.13);border-radius:9px;padding:9px;margin-bottom:6px;background:rgba(255,255,255,.05);cursor:pointer}.custom-device-card.selected{border-color:#38bdf8;background:rgba(56,189,248,.16)}.custom-device-card strong{display:block;font-size:12px}.custom-device-card span{display:block;margin-top:3px;font-size:10px;color:rgba(255,255,255,.62)}.custom-device-select{width:100%;border:1px solid rgba(255,255,255,.18);background:#1e293b;color:#fff;border-radius:8px;padding:9px;font-size:12px}.custom-device-actions{display:grid;grid-template-columns:1fr 1fr;gap:7px;margin-top:8px}.custom-device-actions button{border:1px solid rgba(255,255,255,.16);background:rgba(255,255,255,.07);color:#fff;border-radius:8px;padding:9px;font-size:11px;font-weight:800;cursor:pointer}.custom-device-actions .primary{background:rgba(3,105,161,.5);border-color:#38bdf8}.custom-device-actions .danger{background:rgba(127,29,29,.35);border-color:rgba(248,113,113,.45)}#customDeviceStatus{margin-top:10px;min-height:34px;padding:8px;border-radius:8px;background:rgba(2,132,199,.12);font-size:11px;line-height:1.5;color:#bae6fd}.custom-binding-badge{display:inline-block;margin-top:5px;padding:3px 6px;border-radius:5px;background:rgba(34,197,94,.18);color:#86efac;font-size:10px;font-weight:800}\n.custom-valve-operation-hint{position:fixed;left:50%;bottom:22px;transform:translateX(-50%);z-index:31;padding:8px 13px;border-radius:999px;background:rgba(15,23,42,.88);color:#fff;font-size:11px;font-weight:800;pointer-events:none;opacity:0;transition:opacity .2s}.custom-design-mode.custom-design-completed:not(.custom-device-connect-mode):not(.custom-design-editing) .custom-valve-operation-hint{opacity:1}\n@media(max-width:700px){#customDesignActions{top:118px;left:12px;right:12px;justify-content:flex-end}#customDesignActions button{padding:7px 10px;font-size:12px}#customDevicePanel{top:158px;left:8px;width:calc(100vw - 16px);max-height:55vh}}\n\n\n\n/* =========================================================\n   KONGGOMUL MOBILE UI V1\n   - Desktop layout unchanged\n   - Compact title/status\n   - Bottom action dock on phones\n   - Panels become mobile sheets\n========================================================= */\n@media (max-width: 700px) {\n  :root { --mobile-dock-h: 58px; }\n\n  #titleBox {\n    top: 10px !important;\n    left: 10px !important;\n    right: auto !important;\n    width: auto !important;\n    max-width: calc(100vw - 20px);\n    padding: 9px 12px !important;\n    border-radius: 14px !important;\n    background: rgba(255,255,255,.90) !important;\n    backdrop-filter: blur(10px);\n    -webkit-backdrop-filter: blur(10px);\n  }\n  #titleBox .title { font-size: 15px !important; line-height: 1.2; }\n  #systemStatus {\n    margin-top: 3px !important;\n    max-width: 70vw;\n    overflow: hidden;\n    text-overflow: ellipsis;\n    white-space: nowrap;\n    font-size: 10.5px !important;\n  }\n\n  #farmDesignBadge {\n    top: 76px !important;\n    left: 10px !important;\n    right: auto !important;\n    padding: 5px 8px !important;\n    border-radius: 8px !important;\n    font-size: 10px !important;\n    box-shadow: 0 3px 12px rgba(0,0,0,.18);\n  }\n\n  /* PC용 도움말은 모바일에서 3D를 가리지 않게 제거 */\n  #help { display: none !important; }\n\n  /* 하단 모바일 도크 */\n  #farmMapButton,\n  #farmDesignResetButton,\n  #customDesignActions {\n    position: fixed !important;\n    top: auto !important;\n    bottom: calc(8px + env(safe-area-inset-bottom)) !important;\n    z-index: 90 !important;\n  }\n\n  #farmMapButton,\n  #farmDesignResetButton,\n  #customDesignActions button {\n    height: 42px !important;\n    min-width: 0 !important;\n    margin: 0 !important;\n    padding: 0 9px !important;\n    border-radius: 11px !important;\n    border: 1px solid rgba(255,255,255,.22) !important;\n    box-shadow: 0 5px 18px rgba(0,0,0,.24) !important;\n    font-size: 11px !important;\n    font-weight: 800 !important;\n    white-space: nowrap;\n    backdrop-filter: blur(10px);\n    -webkit-backdrop-filter: blur(10px);\n  }\n\n  #farmMapButton {\n    left: 8px !important;\n    width: calc((100vw - 40px) / 4) !important;\n  }\n  #farmMapButton::after { content: \'\'; }\n\n  #farmDesignResetButton {\n    left: calc(16px + (100vw - 40px) / 4) !important;\n    right: auto !important;\n    width: calc((100vw - 40px) / 4) !important;\n    background: rgba(71,85,105,.94) !important;\n  }\n\n  #customDesignActions {\n    left: calc(24px + ((100vw - 40px) / 4) * 2) !important;\n    right: 8px !important;\n    width: auto !important;\n    height: 42px !important;\n    display: none !important;\n    gap: 8px !important;\n    justify-content: stretch !important;\n  }\n  body.custom-design-mode.custom-design-completed #customDesignActions {\n    display: flex !important;\n  }\n  #customDesignActions button {\n    flex: 1 1 0 !important;\n    width: auto !important;\n  }\n\n  /* 직접 설계 중에는 장치 연결 도크 대신 위치/설계변경만 유지 */\n  body.custom-design-mode.custom-design-editing #customDesignActions { display: none !important; }\n\n  .custom-valve-operation-hint {\n    left: 50% !important;\n    right: auto !important;\n    bottom: calc(60px + env(safe-area-inset-bottom)) !important;\n    width: auto !important;\n    max-width: calc(100vw - 32px) !important;\n    padding: 6px 10px !important;\n    font-size: 10px !important;\n    line-height: 1.3 !important;\n    white-space: nowrap;\n    text-align: center;\n    box-shadow: 0 4px 14px rgba(0,0,0,.18);\n  }\n\n  /* 편집/연결 패널은 화면을 덮는 하단 시트 형태 */\n  #customDesignerPanel,\n  #customDevicePanel,\n  #farmEditor {\n    position: fixed !important;\n    top: auto !important;\n    left: 8px !important;\n    right: 8px !important;\n    bottom: calc(58px + env(safe-area-inset-bottom)) !important;\n    width: auto !important;\n    max-height: min(64vh, 560px) !important;\n    border-radius: 16px !important;\n    padding: 12px !important;\n    box-shadow: 0 14px 42px rgba(0,0,0,.38) !important;\n    overscroll-behavior: contain;\n  }\n  .custom-tool-grid { grid-template-columns: repeat(3,1fr) !important; gap: 6px !important; }\n  .custom-tool-grid button,\n  .custom-pipe-row button,\n  .custom-edit-grid button { min-height: 38px; }\n\n  /* 모바일 3D 오브젝트 선택 공간 확보 */\n  #customDesignerPanel,\n  #customDevicePanel {\n    max-height: min(32vh, 280px) !important;\n    overflow-y: auto !important;\n  }\n  /* 농장 위치 설정 지도 */\n  #farmMapModal { padding: 0 !important; }\n  .farm-map-shell {\n    width: 100vw !important;\n    height: 100dvh !important;\n    max-height: none !important;\n    border: 0 !important;\n    border-radius: 0 !important;\n  }\n  .farm-map-toolbar { padding: 8px !important; gap: 6px !important; }\n  .farm-map-toolbar strong { width: 100%; font-size: 13px; margin: 0 0 2px !important; }\n  .farm-map-toolbar input { min-width: 100% !important; padding: 8px !important; }\n  .farm-map-toolbar button { flex: 1 1 auto; padding: 7px 6px !important; font-size: 11px; }\n  #farmKakaoMap { min-height: 0 !important; }\n  #farmMapInfo { font-size: 10px !important; padding: 7px 9px !important; }\n\n  /* 표준형 구역 패널도 모바일에서는 하단 도크 위로 */\n  #zonePanel {\n    left: 8px !important;\n    right: 8px !important;\n    bottom: calc(58px + env(safe-area-inset-bottom)) !important;\n    width: auto !important;\n    max-height: 46vh;\n    overflow: auto;\n    border-radius: 14px;\n  }\n\n  /* 터치 시 화면 가장자리 UI와 충돌 방지 */\n  #viewer > canvas { touch-action: none !important; }\n}\n\n@media (max-width: 390px) {\n  #farmMapButton,\n  #farmDesignResetButton,\n  #customDesignActions button { font-size: 10px !important; padding: 0 5px !important; }\n  #farmDesignBadge { font-size: 9px !important; }\n  .custom-valve-operation-hint { font-size: 9px !important; }\n}\n\n/* =========================================================\n   3D CCTV PREVIEW\n========================================================= */\n#cctvPreviewModal{\n  position:fixed;\n  left:0;right:0;top:0;\n  z-index:5000;\n  display:none;\n  align-items:flex-start;\n  justify-content:center;\n  padding:8px 12px 0;\n  background:transparent;\n  pointer-events:none;\n}\n#cctvPreviewModal.open{display:flex}\n.cctv-preview-shell{\n  width:min(760px,calc(100vw - 24px));\n  height:min(33.333dvh,360px);\n  min-height:190px;\n  background:#0f172a;\n  border:1px solid rgba(255,255,255,.18);\n  border-radius:0;\n  overflow:hidden;\n  box-shadow:0 14px 42px rgba(0,0,0,.42);\n  display:flex;\n  flex-direction:column;\n  pointer-events:auto;\n}\n.cctv-preview-head{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:8px 12px;color:#fff;background:#111827;flex:0 0 auto}\n.cctv-preview-title{font-weight:800;font-size:13px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}\n.cctv-preview-actions{display:flex;gap:7px}.cctv-preview-actions button{border:1px solid rgba(255,255,255,.2);border-radius:8px;background:#1f2937;color:#fff;padding:6px 9px;cursor:pointer;font-size:11px}\n.cctv-preview-body{position:relative;background:#000;display:flex;align-items:center;justify-content:center;flex:1;min-height:0;aspect-ratio:auto}\n#cctvPreviewImage{width:100%;height:100%;object-fit:contain;background:#000}\n#cctvPreviewMessage{position:absolute;left:50%;bottom:10px;transform:translateX(-50%);padding:5px 9px;border-radius:999px;background:rgba(15,23,42,.82);color:#fff;font-size:10px;pointer-events:none;white-space:nowrap}\n@media(max-width:700px){\n  #cctvPreviewModal{padding:0}\n  .cctv-preview-shell{width:100vw;height:33.333dvh;min-height:170px;max-height:300px;border:0;border-radius:0}\n  .cctv-preview-head{padding:calc(6px + env(safe-area-inset-top)) 9px 6px}\n}';

function loadKakaoMapsSdk() {
  if (window.kakao?.maps) {
    return new Promise((resolve) => {
      window.kakao.maps.load(resolve);
    });
  }

  return new Promise((resolve, reject) => {
    const existing = document.querySelector('script[data-konggomul-kakao-sdk="1"]');

    const finish = () => {
      if (!window.kakao?.maps) {
        reject(new Error('Kakao Maps SDK를 불러오지 못했습니다.'));
        return;
      }
      window.kakao.maps.load(resolve);
    };

    if (existing) {
      if (window.kakao?.maps) finish();
      else existing.addEventListener('load', finish, { once: true });
      return;
    }

    const script = document.createElement('script');
    script.src =
      'https://dapi.kakao.com/v2/maps/sdk.js?appkey=7485609cabd7a470cd92ef9a67ef38aa&libraries=drawing,services&autoload=false';
    script.async = true;
    script.dataset.konggomulKakaoSdk = '1';
    script.addEventListener('load', finish, { once: true });
    script.addEventListener('error', reject, { once: true });
    document.head.appendChild(script);
  });
}

export default function SellerSmartfarm3D() {
  useEffect(() => {
    const previousTitle = document.title;
    let mainSatelliteMapElement = null;
    const previousBodyClassName = document.body.className;
    document.title = '콩꼬물 3D 스마트팜';

    let disposed = false;
    let stopFarm3dLiveSockets = null;

    const start = async () => {
      await loadKakaoMapsSdk();
      if (disposed) return;

      if (disposed) return;

/* =========================================================
   CONFIG
========================================================= */

const ZONE_COUNT = 16;

// =========================================================
// REAL MQTT TEST BINDING
// Site 46 / Device 36 -> ZONE 01
// =========================================================
const API_BASE = '/api/seller/smartfarm/esp32';
const PREVIEW_API_BASE = '/api/seller/smartfarm/preview';
const AUTOMATION_API_BASE = '/api/seller/smartfarm/preview';
const SITE_PARAMS = new URLSearchParams(window.location.search);
const MQTT_SITE_ID = Number(SITE_PARAMS.get('site_id'));
const MQTT_DEVICE_ID = 36;
const MQTT_ZONE = 1;

let mqttRelayEntityId = null;


// =========================================================
// SERVER 3D DESIGN PERSISTENCE
// MariaDB = canonical shared state / localStorage = local cache
// =========================================================
const DESIGN_API_URL = `${API_BASE}/3d-design`;

// =========================================================
// SITE ACCESS GUARD
// URL의 site_id를 신뢰하지 않고 서버의 로그인 세션 + 소유권 검사로
// 이 사이트에 접근 가능한지 확인한 뒤에만 3D 화면을 계속 초기화한다.
// =========================================================
if (!Number.isInteger(MQTT_SITE_ID) || MQTT_SITE_ID <= 0) {
  window.location.replace('/seller/smartfarm');
  return;
}

try {
  const accessResponse = await fetch(
    `${DESIGN_API_URL}?site_id=${encodeURIComponent(MQTT_SITE_ID)}`,
    {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    }
  );

  if (!accessResponse.ok) {
    window.location.replace('/seller/smartfarm');
    return;
  }

  const accessResult = await accessResponse.json().catch(() => ({}));

  if (!accessResult.ok || Number(accessResult.site_id) !== MQTT_SITE_ID) {
    window.location.replace('/seller/smartfarm');
    return;
  }
const accessRoot = document.getElementById('smartfarm3dAccessRoot');
const accessLoading = document.getElementById('smartfarm3dAccessLoading');

// 접근권한 확인만 끝난 단계에서는 아직 3D 화면을 공개하지 않는다.
// 설계/객체/최초 실시간 상태 초기화가 완료된 뒤 start() 마지막에서 공개한다.
if (accessLoading) {
  accessLoading.textContent = '농장 불러오는 중...';
}

try {
  const titleResponse = await fetch(`${PREVIEW_API_BASE}/dashboard?site_id=${encodeURIComponent(MQTT_SITE_ID)}`, { credentials: 'include', cache: 'no-store' });
  const titleResult = await titleResponse.json().catch(() => ({}));
  const farmTitle = document.querySelector('#titleBox .title');
  if (farmTitle && titleResponse.ok && titleResult.ok && titleResult.site?.site_name) farmTitle.textContent = titleResult.site.site_name;
} catch (error) {
  console.warn('[3D TITLE] 농장 이름 조회 실패:', error);
}

} catch (error) {
  console.error('[3D ACCESS] 사이트 접근권한 확인 실패:', error);
  window.location.replace('/seller/smartfarm');
  return;
}

let serverDesignHydrated = false;
let serverDesignSaveTimer = null;
let serverDesignSaveBusy = false;
let serverDesignSavePending = false;

function parseStoredJson(key, fallback = null) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (error) {
    console.warn('[3D DB] localStorage JSON parse failed:', key, error);
    return fallback;
  }
}

function normalizeServerJson(value, fallback = null) {
  if (value === null || value === undefined || value === '') return fallback;
  if (typeof value === 'string') {
    try { return JSON.parse(value); } catch (error) { return fallback; }
  }
  return value;
}

function collectServerDesignPayload() {
  const designKey = `konggomul:farm-design:site:${MQTT_SITE_ID}`;
  const geoKey = `konggomul:farm-geo:site:${MQTT_SITE_ID}`;
  const customLayoutKey = `konggomul:custom-layout:site:${MQTT_SITE_ID}`;
  const customHardwareKey = `konggomul:custom-hardware:site:${MQTT_SITE_ID}`;
  const objectLayoutKey = `konggomul:3d-object-layout:site:${MQTT_SITE_ID}`;
  const rootTransformKey = `konggomul:farm-root-transform:site:${MQTT_SITE_ID}`;
  const rootRotationKey = `konggomul:farm-root-rotation:site:${MQTT_SITE_ID}`;

  const design = parseStoredJson(designKey, null);
  const rotationRaw = localStorage.getItem(rootRotationKey);
  const rotation = rotationRaw !== null && Number.isFinite(Number(rotationRaw))
    ? Number(rotationRaw)
    : null;

  return {
    site_id: MQTT_SITE_ID,
    design_mode: design?.design_mode || null,
    design_completed: design?.design_completed === true,
    farm_geo: parseStoredJson(geoKey, null),
    custom_layout: parseStoredJson(customLayoutKey, null),
    custom_hardware: parseStoredJson(customHardwareKey, {}),
    object_layout: parseStoredJson(objectLayoutKey, {}),
    farm_root_transform: parseStoredJson(rootTransformKey, null),
    farm_root_rotation: rotation
  };
}

async function persistFarmDesignToServer(options = {}) {
  if (serverDesignSaveBusy) {
    serverDesignSavePending = true;
    return false;
  }

  serverDesignSaveBusy = true;
  try {
    const response = await fetch(DESIGN_API_URL, {
      method: 'PUT',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(collectServerDesignPayload())
    });
    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result.ok) {
      throw new Error(result.error || `3D 설계 DB 저장 실패 (HTTP ${response.status})`);
    }
    if (!options.silent) console.log('[3D DB] 저장 완료', result);
    return true;
  } catch (error) {
    console.error('[3D DB] 저장 실패:', error);
    return false;
  } finally {
    serverDesignSaveBusy = false;
    if (serverDesignSavePending) {
      serverDesignSavePending = false;
      scheduleServerDesignSave(150);
    }
  }
}

function scheduleServerDesignSave(delay = 450) {
  if (!serverDesignHydrated) return;
  clearTimeout(serverDesignSaveTimer);
  serverDesignSaveTimer = setTimeout(() => {
    persistFarmDesignToServer({ silent: true });
  }, delay);
}

function buildFarmDesignCacheFromServer(design) {
  const customLayout = normalizeServerJson(design.custom_layout, null);
  const objects = Array.isArray(customLayout?.objects) ? customLayout.objects : [];
  const mode = design.design_mode || null;

  if (!mode) return null;

  return {
    site_id: MQTT_SITE_ID,
    design_mode: mode,
    design_completed: design.design_completed === true || Number(design.design_completed) === 1,
    template: mode === 'standard_400' ? 'standard_400_v1' : null,
    facilities: objects.filter(x => x?.kind === 'facility'),
    pipes: objects.filter(x => x?.kind === 'pipe'),
    zones: mode === 'standard_400' ? Array.from({length: ZONE_COUNT}, (_, i) => i + 1) : [],
    updated_at: design.updated_at || new Date().toISOString()
  };
}

function hydrateLocalCacheFromServer(design) {
  const setJson = (key, value) => {
    if (value === null || value === undefined) localStorage.removeItem(key);
    else localStorage.setItem(key, JSON.stringify(normalizeServerJson(value, value)));
  };

  const designKey = `konggomul:farm-design:site:${MQTT_SITE_ID}`;
  const farmDesign = buildFarmDesignCacheFromServer(design);
  if (farmDesign) localStorage.setItem(designKey, JSON.stringify(farmDesign));
  else localStorage.removeItem(designKey);

  setJson(`konggomul:farm-geo:site:${MQTT_SITE_ID}`, design.farm_geo);
  setJson(`konggomul:custom-layout:site:${MQTT_SITE_ID}`, design.custom_layout);
  setJson(`konggomul:custom-hardware:site:${MQTT_SITE_ID}`, design.custom_hardware ?? {});
  setJson(`konggomul:3d-object-layout:site:${MQTT_SITE_ID}`, design.object_layout ?? {});
  setJson(`konggomul:farm-root-transform:site:${MQTT_SITE_ID}`, design.farm_root_transform);

  const rotationKey = `konggomul:farm-root-rotation:site:${MQTT_SITE_ID}`;
  if (design.farm_root_rotation === null || design.farm_root_rotation === undefined) {
    localStorage.removeItem(rotationKey);
  } else {
    localStorage.setItem(rotationKey, String(design.farm_root_rotation));
  }
}

async function hydrateFarmDesignFromServer() {
  try {
    const response = await fetch(`${DESIGN_API_URL}?site_id=${encodeURIComponent(MQTT_SITE_ID)}`, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: { 'Accept': 'application/json' }
    });
    const result = await response.json().catch(() => ({}));

    if (!response.ok || !result.ok) {
      throw new Error(result.error || `3D 설계 DB 조회 실패 (HTTP ${response.status})`);
    }

    if (result.exists && result.design) {
      hydrateLocalCacheFromServer(result.design);
      serverDesignHydrated = true;
      console.log(`[3D DB] Site ${MQTT_SITE_ID} 설계를 DB에서 복원했습니다.`);
      return true;
    }

    // DB에 아직 설계가 없고 이 브라우저에 기존 설계가 있으면 최초 1회 업로드한다.
    serverDesignHydrated = true;
    const localDesign = parseStoredJson(`konggomul:farm-design:site:${MQTT_SITE_ID}`, null);
    if (localDesign) {
      await persistFarmDesignToServer({ silent: true });
      console.log(`[3D DB] Site ${MQTT_SITE_ID} 기존 localStorage 설계를 DB에 최초 저장했습니다.`);
    }
    return false;
  } catch (error) {
    // 서버 조회 장애 시 기존 localStorage로 동작하게 하여 현장 제어를 막지 않는다.
    serverDesignHydrated = true;
    console.error('[3D DB] 조회 실패 · localStorage fallback 사용:', error);
    return false;
  }
}

// =========================================================
// SMART FARM DESIGN STATE
// - custom: 빈 실제 농장에서 설계 시작
// - standard_400: 기존 400평 GLB/16구역 운영 구조 사용
// =========================================================
const FARM_DESIGN_STORAGE_KEY =
  `konggomul:farm-design:site:${MQTT_SITE_ID}`;

let farmDesignState = null;
let standardTemplateLoaded = false;

function readFarmDesignState() {
  try {
    const raw = localStorage.getItem(FARM_DESIGN_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    console.error('[FARM DESIGN] 저장값 읽기 실패', error);
    return null;
  }
}

function writeFarmDesignState(value) {
  farmDesignState = value;
  localStorage.setItem(FARM_DESIGN_STORAGE_KEY, JSON.stringify(value));
  updateFarmDesignBadge();
  scheduleServerDesignSave();
}

function updateFarmDesignBadge() {
  const badge = document.getElementById('farmDesignBadge');
  if (!badge || !farmDesignState) return;
  badge.style.display = 'block';
  badge.textContent = farmDesignState.design_mode === 'custom'
    ? '설계 모드 · 직접 설계'
    : '설계 · 400평 표준형';
}

function openFarmDesignStartModal() {
  document.getElementById('farmDesignStartModal')?.classList.add('open');
}

function closeFarmDesignStartModal() {
  document.getElementById('farmDesignStartModal')?.classList.remove('open');
}

function resetFarmDesignSelection() {
  const currentLabel = farmDesignState?.design_mode === 'custom'
    ? '직접 설계'
    : farmDesignState?.design_mode === 'standard_400'
      ? '400평 표준형'
      : '현재 설계';

  const ok = window.confirm(
    `${currentLabel} 선택을 취소하고 초기 설계 화면으로 돌아갈까요?\n\n` +
    '농장 위치/경계와 ESP32 배치·MQTT 연결 정보는 삭제하지 않습니다.'
  );

  if (!ok) return;

  // 설계 방식 선택값만 삭제한다.
  // FARM_GEO_STORAGE_KEY, 3D bindings, MQTT 데이터는 그대로 유지한다.
  localStorage.removeItem(FARM_DESIGN_STORAGE_KEY);
  persistFarmDesignToServer({ silent: true }).finally(() => window.location.reload());
}

document.getElementById('farmDesignResetButton')
  ?.addEventListener('click', resetFarmDesignSelection);




/* =========================================================
   SCENE
========================================================= */

const container =
  document.getElementById(
    'viewer'
  );


const scene =
  new THREE.Scene();


const DEFAULT_SCENE_BACKGROUND = new THREE.Color(0xe9eef2);
scene.background = DEFAULT_SCENE_BACKGROUND;



/* =========================================================
   CAMERA
========================================================= */

const camera =
  new THREE.PerspectiveCamera(
    45,
    Math.max(1, container.clientWidth) / Math.max(1, container.clientHeight),
    0.1,
    5000
  );


camera.position.set(
  48,
  -42,
  42
);



/* =========================================================
   RENDERER
========================================================= */

const renderer =
  new THREE.WebGLRenderer({

    antialias: true,
    alpha: true

  });

renderer.setClearColor(
  0x000000,
  0
);


renderer.setPixelRatio(

  Math.min(
    window.devicePixelRatio,
    2
  )

);


const initialViewerRect = container.getBoundingClientRect();

renderer.setSize(
  Math.max(1, Math.round(initialViewerRect.width)),
  Math.max(1, Math.round(initialViewerRect.height)),
  false
);


renderer.outputColorSpace =
  THREE.SRGBColorSpace;


container.appendChild(

  renderer.domElement

);

const cssRenderer = new CSS3DRenderer();
cssRenderer.setSize(
  Math.max(1, Math.round(initialViewerRect.width)),
  Math.max(1, Math.round(initialViewerRect.height))
);
cssRenderer.domElement.className = 'css3d-layer';
cssRenderer.domElement.style.pointerEvents = 'none';
container.insertBefore(cssRenderer.domElement, renderer.domElement);

renderer.domElement.style.pointerEvents = 'auto';
renderer.domElement.style.touchAction = 'none';




/* =========================================================
   CAMERA CONTROL
========================================================= */

const controls =
  new OrbitControls(

    camera,

    renderer.domElement

  );


controls.target.set(
  19,
  16,
  0
);


controls.enableDamping =
  true;


controls.dampingFactor =
  0.06;

// =========================================================
// MOBILE / REAL-FARM CAMERA SAFETY
// Keep OrbitControls above the farm ground.  This is especially important
// on touch devices where a large vertical swipe can otherwise flip the
// camera under/through the CSS satellite plane.
// =========================================================
controls.minPolarAngle = THREE.MathUtils.degToRad(12);
controls.maxPolarAngle = THREE.MathUtils.degToRad(78);
controls.minDistance = 4;
controls.maxDistance = 1200;
controls.screenSpacePanning = false;

function enforceFarmCameraSafety() {
  if (!document.body.classList.contains('real-farm-mode')) return;

  // Never allow the camera to cross below the farm ground.
  if (camera.position.y < 2.0) {
    camera.position.y = 2.0;
  }

  // Keep target close to the actual farm plane so orbiting cannot acquire
  // a large vertical component and make world objects appear detached.
  if (Math.abs(controls.target.y) > 0.001) {
    controls.target.y = 0;
  }
}


/* =========================================================
   OBJECT DRAG CONTROL
========================================================= */

const transformControls =
  new TransformControls(
    camera,
    renderer.domElement
  );

const transformHelper =
  transformControls.getHelper();

transformHelper.visible =
  false;

scene.add(
  transformHelper
);

let transformDragging =
  false;

transformControls.addEventListener(
  'dragging-changed',
  function(event) {

    transformDragging =
      !!event.value;

    controls.enabled =
      !event.value;

    if (!event.value) {

      if (
        farmRootPivot &&
        transformControls.object === farmRootPivot
      ) {
        saveFarmRootTransform();
        updateFarmRootTransformInfo();
        farmEditorStatus('관수시스템 전체 위치 저장 완료');
      }

      else if (selectedMovableObject) {
        saveObjectLayout();
        updateObjectMoveControls();

        farmEditorStatus(
          `${selectedMovableObject.name} 위치 저장 완료`
        );
      }

    }

  }
);

transformControls.addEventListener(
  'objectChange',
  function() {

    if (
      farmRootPivot &&
      transformControls.object === farmRootPivot
    ) {
      updateFarmRootTransformInfo();
    }

    else if (
      selectedMovableObject
    ) {
      updateObjectMoveControls();
    }

  }
);



/* =========================================================
   LIGHT
========================================================= */

const hemi =
  new THREE.HemisphereLight(

    0xffffff,

    0x666666,

    2.5

  );


scene.add(
  hemi
);



const sun =
  new THREE.DirectionalLight(

    0xffffff,

    3

  );


sun.position.set(
  20,
  -20,
  40
);


scene.add(
  sun
);



/* =========================================================
   RAYCASTER
========================================================= */

const raycaster =
  new THREE.Raycaster();


const pointer =
  new THREE.Vector2();


let pointerDownX = 0;
let pointerDownY = 0;



/* =========================================================
   DATA
========================================================= */

const zones = {};


let selectedZone =
  null;



/* =========================================================
   MATERIAL HELPER
========================================================= */

function prepareMaterial(
  obj
) {

  if (!obj) {

    return null;

  }


  let mesh =
    null;


  if (obj.isMesh) {

    mesh =
      obj;

  }

  else {

    obj.traverse(

      function(child) {

        if (
          !mesh &&
          child.isMesh
        ) {

          mesh =
            child;

        }

      }

    );

  }


  if (
    !mesh ||
    !mesh.material
  ) {

    return null;

  }


  mesh.material =
    mesh.material.clone();


  return mesh.material;

}



/* =========================================================
   NUMBER FORMAT
========================================================= */

function padZone(
  number
) {

  return String(
    number
  ).padStart(
    2,
    '0'
  );

}



/* =========================================================
   OBJECT TAG
========================================================= */

function tagZoneObject(
  obj,
  zoneNumber,
  zoneType
) {

  if (!obj) {

    return;

  }


  obj.userData.zoneNumber =
    zoneNumber;


  obj.userData.zoneType =
    zoneType;


  obj.traverse(

    function(child) {

      child.userData.zoneNumber =
        zoneNumber;


      child.userData.zoneType =
        zoneType;

    }

  );

}






/* =========================================================
   V8 MAIN SATELLITE BACKGROUND
========================================================= */

let mainSatelliteMap = null;
let satelliteCssObject = null; // legacy compatibility: no longer used for the live satellite ground
let satelliteGroundMesh = null;
let satelliteActualWidthM = 0;
let satelliteActualDepthM = 0;

function latLngDeltaMeters(latA, lngA, latB, lngB) {
  const midLat = THREE.MathUtils.degToRad((Number(latA) + Number(latB)) / 2);
  return {
    x: (Number(lngB) - Number(lngA)) * 111320 * Math.cos(midLat),
    z: -(Number(latB) - Number(latA)) * 111320
  };
}

function updateSatelliteCss3DFromVisibleBounds(saved) {
  if (!mainSatelliteMap) return;

  const b = mainSatelliteMap.getBounds();
  const sw = b.getSouthWest();
  const ne = b.getNorthEast();

  const midLat = (sw.getLat() + ne.getLat()) / 2;
  const midLng = (sw.getLng() + ne.getLng()) / 2;

  const widthM = Math.abs(
    (ne.getLng() - sw.getLng()) * 111320 * Math.cos(THREE.MathUtils.degToRad(midLat))
  );
  const depthM = Math.abs(
    (ne.getLat() - sw.getLat()) * 111320
  );

  satelliteActualWidthM = widthM;
  satelliteActualDepthM = depthM;

  const lat0 = saved.boundary.reduce((a,p)=>a+Number(p.lat),0) / saved.boundary.length;
  const lng0 = saved.boundary.reduce((a,p)=>a+Number(p.lng),0) / saved.boundary.length;
  const off = latLngDeltaMeters(lat0, lng0, midLat, midLng);

  // IMPORTANT:
  // The satellite ground is now a WebGL mesh in the SAME renderer/world as all farm objects.
  // This removes the mobile CSS3D/WebGL perspective divergence that made objects appear to fly.
  const mapCanvas = document.createElement('canvas');
  mapCanvas.width = 1024;
  mapCanvas.height = 1024;
  const ctx = mapCanvas.getContext('2d');

  try {
    // Kakao map is DOM/tile based and cannot be reliably drawn cross-origin to a canvas.
    // We therefore keep the Kakao CSS map as a visual source, but the world transform is owned
    // by this transparent WebGL ground plane. The CSS layer is locked to top-down only below.
    // The mesh also provides the common geometric ground used by raycasting and camera limits.
    ctx.clearRect(0, 0, 1024, 1024);
  } catch (e) {}

  if (satelliteGroundMesh) {
    scene.remove(satelliteGroundMesh);
    satelliteGroundMesh.geometry?.dispose?.();
    satelliteGroundMesh.material?.dispose?.();
    satelliteGroundMesh = null;
  }

  const groundGeometry = new THREE.PlaneGeometry(widthM, depthM);
  const groundMaterial = new THREE.MeshBasicMaterial({
    transparent: true,
    opacity: 0,
    depthWrite: false,
    side: THREE.DoubleSide
  });

  satelliteGroundMesh = new THREE.Mesh(groundGeometry, groundMaterial);
  satelliteGroundMesh.name = 'KONGGOMUL_SATELLITE_WORLD_GROUND';
  satelliteGroundMesh.rotation.x = -Math.PI / 2;
  satelliteGroundMesh.position.set(off.x, -0.035, off.z);
  satelliteGroundMesh.userData.isSatelliteGround = true;
  scene.add(satelliteGroundMesh);

  // Legacy CSS3D object, when present, is kept aligned to the exact same world transform.
  if (satelliteCssObject) {
    // Kakao DOM map must be viewed from its front face.
    // Flip only the CSS plane's local Y axis so north remains Three.js -Z.
    // This preserves the existing geographic X/Z convention:
    // east = +X, north = -Z.
    satelliteCssObject.scale.set(widthM / 1024, -depthM / 1024, 1);
    satelliteCssObject.position.set(off.x, -0.03, off.z);
    satelliteCssObject.rotation.set(Math.PI / 2, 0, 0);
  }
}

function initMainSatelliteMap() {
  const saved = readFarmGeo();
  if (!saved || !Array.isArray(saved.boundary) || saved.boundary.length < 3 || !window.kakao || !kakao.maps) return;

  kakao.maps.load(function() {
    const el = document.getElementById('mainSatelliteMap');
    if (!el) return;

    el.style.visibility = 'hidden';
    el.style.background = 'transparent';
    el.style.pointerEvents = 'none';

    document.body.classList.add('real-farm-mode');
    scene.background = null;
    renderer.setClearColor(0x000000, 0);
    renderer.setClearAlpha(0);

    const lats = saved.boundary.map(p=>Number(p.lat));
    const lngs = saved.boundary.map(p=>Number(p.lng));
    const centerLat = (Math.min(...lats)+Math.max(...lats))/2;
    const centerLng = (Math.min(...lngs)+Math.max(...lngs))/2;
    const center = new kakao.maps.LatLng(centerLat, centerLng);

    mainSatelliteMap = new kakao.maps.Map(el, { center, level: 3 });
    mainSatelliteMap.setMapTypeId(kakao.maps.MapTypeId.SKYVIEW);
    mainSatelliteMap.setDraggable(false);
    mainSatelliteMap.setZoomable(false);

    const path = saved.boundary.map(p=>new kakao.maps.LatLng(Number(p.lat),Number(p.lng)));
    const bounds = new kakao.maps.LatLngBounds();
    path.forEach(p=>bounds.extend(p));

    new kakao.maps.Polygon({
      map: mainSatelliteMap,
      path,
      strokeWeight: 4,
      strokeColor: '#00A0E9',
      strokeOpacity: 0.95,
      fillColor: '#00A0E9',
      fillOpacity: 0.03
    });

    if (satelliteCssObject) {
      scene.remove(satelliteCssObject);
      satelliteCssObject = null;
    }
    satelliteCssObject = new CSS3DObject(el);
    satelliteCssObject.name = 'KONGGOMUL_SATELLITE_GROUND';
    scene.add(satelliteCssObject);

    requestAnimationFrame(function(){
      mainSatelliteMap.relayout();

      let firstLoadReady = false;

      function finishFirstLoad() {
        if (firstLoadReady) return;
        firstLoadReady = true;

        updateSatelliteCss3DFromVisibleBounds(saved);
        alignIrrigationSystemToFarmBoundary(saved.boundary);
        fitThreeCameraToRealFarm(saved.boundary);

        controls.enabled = true;
        controls.enableRotate = true;
        controls.enablePan = true;
        controls.enableZoom = true;
        controls.minPolarAngle = THREE.MathUtils.degToRad(12);
        controls.maxPolarAngle = THREE.MathUtils.degToRad(78);
        enforceFarmCameraSafety();
        controls.update();

        requestAnimationFrame(function(){
          el.style.visibility = 'visible';
        });

        kakao.maps.event.removeListener(
          mainSatelliteMap,
          'idle',
          finishFirstLoad
        );
      }

      kakao.maps.event.addListener(
        mainSatelliteMap,
        'idle',
        finishFirstLoad
      );

      mainSatelliteMap.setBounds(bounds, 40, 40, 40, 40);
    });
  });
}

function removeMainSatelliteMap() {
  if (satelliteCssObject) {
    scene.remove(satelliteCssObject);
    satelliteCssObject = null;
  }
  if (satelliteGroundMesh) {
    scene.remove(satelliteGroundMesh);
    satelliteGroundMesh.geometry?.dispose?.();
    satelliteGroundMesh.material?.dispose?.();
    satelliteGroundMesh = null;
  }
  mainSatelliteMap = null;
  satelliteActualWidthM = 0;
  satelliteActualDepthM = 0;
  scene.background = DEFAULT_SCENE_BACKGROUND;
  document.body.classList.remove('real-farm-mode');
}

function refreshMainSatelliteMap() {
  removeMainSatelliteMap();
  initMainSatelliteMap();
}

/* =========================================================
   SITE FARM GEO / KAKAO SKYVIEW
========================================================= */

const FARM_GEO_STORAGE_KEY =
  `konggomul:farm-geo:site:${MQTT_SITE_ID}`;

let farmKakaoMap = null;
let farmDrawingManager = null;
let farmSavedPolygonOverlay = null;
let farmBoundaryPoints = [];
let generatedFarmBoundaryMesh = null;

function readFarmGeo() {
  try {
    const raw = localStorage.getItem(FARM_GEO_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (error) {
    return null;
  }
}

function writeFarmGeo(data) {
  localStorage.setItem(
    FARM_GEO_STORAGE_KEY,
    JSON.stringify(data)
  );
  scheduleServerDesignSave();
}

function setFarmMapInfo(text) {
  const el = document.getElementById('farmMapInfo');
  if (el) el.textContent = text || '';
}

function initKakaoFarmMap() {
  if (!window.kakao || !kakao.maps) {
    setFarmMapInfo('카카오 지도 SDK를 불러오지 못했습니다. JavaScript SDK 도메인 등록을 확인하세요.');
    return;
  }

  kakao.maps.load(function() {
    const saved = readFarmGeo();
    const center =
      saved && saved.center
        ? new kakao.maps.LatLng(saved.center.lat, saved.center.lng)
        : new kakao.maps.LatLng(35.8242, 127.1480);

    farmKakaoMap = new kakao.maps.Map(
      document.getElementById('farmKakaoMap'),
      { center, level: saved?.level || 3 }
    );

    farmKakaoMap.setMapTypeId(
      kakao.maps.MapTypeId.SKYVIEW
    );

    // 지도는 항상 마우스/터치 드래그 및 휠 확대축소 가능
    farmKakaoMap.setDraggable(true);
    farmKakaoMap.setZoomable(true);

    const options = {
      map: farmKakaoMap,
      drawingMode: [
        kakao.maps.drawing.OverlayType.POLYGON
      ],
      polygonOptions: {
        draggable: true,
        removable: true,
        editable: true,
        strokeColor: '#00A0E9',
        fillColor: '#00A0E9',
        fillOpacity: 0.22
      }
    };

    farmDrawingManager =
      new kakao.maps.drawing.DrawingManager(options);

    restoreFarmBoundaryOnMap();

    setTimeout(function() {
      farmKakaoMap.relayout();
    }, 80);
  });
}

function restoreFarmBoundaryOnMap() {
  const saved = readFarmGeo();
  if (!farmKakaoMap || !saved || !Array.isArray(saved.boundary) || saved.boundary.length < 3) return;

  farmBoundaryPoints = saved.boundary.slice();

  if (farmSavedPolygonOverlay) {
    farmSavedPolygonOverlay.setMap(null);
  }

  const path = saved.boundary.map(function(p) {
    return new kakao.maps.LatLng(p.lat, p.lng);
  });

  farmSavedPolygonOverlay = new kakao.maps.Polygon({
    map: farmKakaoMap,
    path,
    strokeWeight: 3,
    strokeColor: '#00A0E9',
    strokeOpacity: 1,
    fillColor: '#00A0E9',
    fillOpacity: 0.2
  });

  const bounds = new kakao.maps.LatLngBounds();
  path.forEach(function(p) { bounds.extend(p); });
  farmKakaoMap.setBounds(bounds);
}


function removeDrawingManagerPolygons() {
  if (!farmDrawingManager) return;

  farmDrawingManager.cancel();

  const overlays =
    farmDrawingManager.getOverlays([
      kakao.maps.drawing.OverlayType.POLYGON
    ]) || {};

  const polygons =
    overlays.polygon ||
    overlays[kakao.maps.drawing.OverlayType.POLYGON] ||
    [];

  polygons.slice().forEach(function(overlay) {
    farmDrawingManager.remove(overlay);
  });
}

function startFarmBoundaryDrawing() {
  if (!farmDrawingManager) return;

  removeDrawingManagerPolygons();

  if (farmKakaoMap) {
    farmKakaoMap.setDraggable(true);
    farmKakaoMap.setZoomable(true);
  }

  if (farmSavedPolygonOverlay) {
    farmSavedPolygonOverlay.setMap(null);
    farmSavedPolygonOverlay = null;
  }

  farmDrawingManager.select(
    kakao.maps.drawing.OverlayType.POLYGON
  );

  setFarmMapInfo('농지 외곽을 순서대로 클릭하고 마지막 점에서 더블클릭하여 경계를 완성하세요.');
}

function extractDrawingBoundary() {
  if (!farmDrawingManager) return [];

  const data = farmDrawingManager.getData();
  const polygons = data?.polygon || [];
  if (!polygons.length) return [];

  const points = polygons[polygons.length - 1].points || [];

  return points.map(function(point) {
    const y = typeof point.y === 'number' ? point.y : point.lat;
    const x = typeof point.x === 'number' ? point.x : point.lng;
    return { lat: Number(y), lng: Number(x) };
  }).filter(function(p) {
    return Number.isFinite(p.lat) && Number.isFinite(p.lng);
  });
}

function saveFarmBoundary() {
  let boundary = extractDrawingBoundary();

  if (boundary.length < 3) {
    boundary = farmBoundaryPoints.slice();
  }

  if (boundary.length < 3) {
    setFarmMapInfo('농장 경계를 먼저 그려주세요.');
    return;
  }

  const center = farmKakaoMap.getCenter();

  const payload = {
    site_id: MQTT_SITE_ID,
    center: {
      lat: center.getLat(),
      lng: center.getLng()
    },
    level: farmKakaoMap.getLevel(),
    boundary
  };

  writeFarmGeo(payload);
  farmBoundaryPoints = boundary.slice();

  // 저장 후 그리기 모드를 끝내야 지도 드래그가 정상 동작한다.
  if (farmDrawingManager) {
    farmDrawingManager.cancel();
  }

  if (farmKakaoMap) {
    farmKakaoMap.setDraggable(true);
    farmKakaoMap.setZoomable(true);
  }

  create3DFarmBoundary(boundary);
  alignIrrigationSystemToFarmBoundary(boundary);
  fitThreeCameraToRealFarm(boundary);
  refreshMainSatelliteMap();

  setFarmMapInfo(
    `Site ${MQTT_SITE_ID} 농장 경계 저장 완료 · 꼭짓점 ${boundary.length}개`
  );
}

function clearFarmBoundary() {
  localStorage.removeItem(FARM_GEO_STORAGE_KEY);
  scheduleServerDesignSave();
  farmBoundaryPoints = [];

  if (farmDrawingManager) {
    removeDrawingManagerPolygons();
  }

  if (farmSavedPolygonOverlay) {
    farmSavedPolygonOverlay.setMap(null);
    farmSavedPolygonOverlay = null;
  }

  if (generatedFarmBoundaryMesh) {
    scene.remove(generatedFarmBoundaryMesh);
    generatedFarmBoundaryMesh.geometry?.dispose();
    generatedFarmBoundaryMesh.material?.dispose();
    generatedFarmBoundaryMesh = null;
  }

  // No real boundary: show the original model again as a fallback.
  showLegacyFixedFarm();
  removeMainSatelliteMap();

  setFarmMapInfo('저장된 농장 경계를 삭제했습니다. 기존 농장 모델을 다시 표시합니다.');
}

function geoBoundaryToLocalXZ(boundary) {
  if (!Array.isArray(boundary) || boundary.length < 3) return [];

  const lat0 =
    boundary.reduce((sum, p) => sum + Number(p.lat), 0) / boundary.length;

  const lng0 =
    boundary.reduce((sum, p) => sum + Number(p.lng), 0) / boundary.length;

  const latRad = THREE.MathUtils.degToRad(lat0);
  const metersPerLat = 111320;
  const metersPerLng = 111320 * Math.cos(latRad);

  return boundary.map(function(p) {
    return {
      x: (Number(p.lng) - lng0) * metersPerLng,
      z: -(Number(p.lat) - lat0) * metersPerLat
    };
  });
}


function isLegacyFarmSurfaceObject(object) {
  if (!object || object === scene) return false;

  const name = String(object.name || '').toLowerCase();

  // Never hide control/facility objects.
  if (
    name.includes('zone_valve_') ||
    name.includes('drip_') ||
    name.includes('tank') ||
    name.includes('pump') ||
    name.includes('sensor') ||
    name.includes('esp32') ||
    name.includes('sprinkler') ||
    name.includes('filter') ||
    name.includes('fertilizer') ||
    name.includes('fertigation') ||
    name.includes('controller') ||
    name.includes('reservoir') ||
    name.includes('motor') ||
    name.includes('box') ||
    name.includes('물탱크') ||
    name.includes('탱크') ||
    name.includes('펌프') ||
    name.includes('센서') ||
    name.includes('스프링클러') ||
    name.includes('제어반')
  ) {
    return false;
  }

  // Legacy fixed 400-pyeong visual surface/rows.
  return (
    name.includes('ground') ||
    name.includes('field') ||
    name.includes('farm_ground') ||
    name.includes('soil') ||
    name.includes('row') ||
    name.includes('crop') ||
    name.includes('bed') ||
    name.includes('밭') ||
    name.includes('토양') ||
    name.includes('두둑') ||
    name.includes('작물')
  );
}

function hideLegacyFixedFarm() {
  if (!farmRoot) return;

  farmRoot.traverse(function(object) {
    if (isLegacyFarmSurfaceObject(object)) {
      object.visible = false;
      object.userData.konggomulLegacyHidden = true;
    }
  });
}

function showLegacyFixedFarm() {
  if (!farmRoot) return;

  farmRoot.traverse(function(object) {
    if (object.userData?.konggomulLegacyHidden) {
      object.visible = true;
      delete object.userData.konggomulLegacyHidden;
    }
  });
}

function create3DFarmBoundary(boundary) {
  const local = geoBoundaryToLocalXZ(boundary);
  if (local.length < 3) return;

  // A real farm boundary replaces the old fixed 40m x 33m farm surface.
  hideLegacyFixedFarm();

  if (generatedFarmBoundaryMesh) {
    scene.remove(generatedFarmBoundaryMesh);
    generatedFarmBoundaryMesh.geometry?.dispose();
    generatedFarmBoundaryMesh.material?.dispose();
  }

  const shape = new THREE.Shape();
  shape.moveTo(local[0].x, -local[0].z);

  for (let i = 1; i < local.length; i++) {
    shape.lineTo(local[i].x, -local[i].z);
  }

  shape.closePath();

  const geometry =
    new THREE.ShapeGeometry(shape);

  geometry.rotateX(-Math.PI / 2);

  const material =
    new THREE.MeshStandardMaterial({
      color: 0x00a0e9,
      roughness: 1,
      metalness: 0,
      side: THREE.DoubleSide,
      transparent: true,
      opacity: 0.035,
      depthWrite: false
    });

  generatedFarmBoundaryMesh =
    new THREE.Mesh(
      geometry,
      material
    );

  generatedFarmBoundaryMesh.name =
    'KONGGOMUL_REAL_FARM_BOUNDARY';

  generatedFarmBoundaryMesh.position.y =
    farmRoot ? farmRoot.position.y + 0.025 : 0.025;

  scene.add(
    generatedFarmBoundaryMesh
  );
}



function alignIrrigationSystemToFarmBoundary(boundary) {
  if (!farmRoot) return;

  // 사용자 전체 배치 Pivot이 만들어진 뒤에는 자동 정렬로 farmRoot를 다시 움직이지 않는다.
  // 새로고침 때 저장 위치가 달라지던 원인이 이 중복 정렬이었다.
  if (farmRootPivot) return;
  const local = geoBoundaryToLocalXZ(boundary);
  if (!Array.isArray(local) || local.length < 3) return;

  // Critical rule: 1 Three.js unit = 1 real meter.
  // Never auto-scale the irrigation GLB to "fit" the parcel.
  farmRoot.scale.set(1, 1, 1);
  farmRoot.updateMatrixWorld(true);

  const xs = local.map(p=>Number(p.x));
  const zs = local.map(p=>Number(p.z));
  const farmCenterX = (Math.min(...xs)+Math.max(...xs))/2;
  const farmCenterZ = (Math.min(...zs)+Math.max(...zs))/2;

  const box = new THREE.Box3().setFromObject(farmRoot);
  const modelCenter = new THREE.Vector3();
  box.getCenter(modelCenter);

  farmRoot.position.x += farmCenterX - modelCenter.x;
  farmRoot.position.z += farmCenterZ - modelCenter.z;
  farmRoot.position.y += -box.min.y + 0.08;
  farmRoot.updateMatrixWorld(true);
}

function fitThreeCameraToRealFarm(boundary) {
  const local = geoBoundaryToLocalXZ(boundary);
  if (!Array.isArray(local) || local.length < 3) return;

  const xs = local.map(p=>Number(p.x));
  const zs = local.map(p=>Number(p.z));
  const minX=Math.min(...xs), maxX=Math.max(...xs);
  const minZ=Math.min(...zs), maxZ=Math.max(...zs);
  const width=Math.max(maxX-minX,5), depth=Math.max(maxZ-minZ,5);
  const cx=(minX+maxX)/2, cz=(minZ+maxZ)/2;
  const span=Math.max(width,depth);

  camera.aspect = Math.max(container.clientWidth,1) / Math.max(container.clientHeight,1);
  camera.near = 0.1;
  camera.far = Math.max(5000, span*50);
  camera.position.set(cx + span*0.85, span*1.05, cz + span*0.95);
  camera.up.set(0,1,0);
  camera.updateProjectionMatrix();

  controls.target.set(cx,0,cz);
  controls.enableRotate = true;
  controls.enablePan = true;
  controls.enableZoom = true;
  controls.minDistance = Math.max(2,span*0.15);
  controls.maxDistance = Math.max(300,span*8);
  controls.update();
}

function restore3DFarmBoundary() {
  const saved = readFarmGeo();
  if (saved?.boundary?.length >= 3) {
    farmBoundaryPoints = saved.boundary.slice();
    create3DFarmBoundary(saved.boundary);

    // 실제 경계 내부에 관수 시스템 전체를 먼저 배치한 뒤
    // 위성사진과 같은 화면 범위로 카메라를 맞춘다.
    alignIrrigationSystemToFarmBoundary(saved.boundary);
    fitThreeCameraToRealFarm(saved.boundary);
  }
}

function openFarmMapEditor() {
  document.getElementById('farmMapModal').classList.add('open');

  if (!farmKakaoMap) {
    initKakaoFarmMap();
  } else {
    setTimeout(function() {
      farmKakaoMap.relayout();
      restoreFarmBoundaryOnMap();
    }, 50);
  }
}

function closeFarmMapEditor() {
  if (farmDrawingManager) {
    farmDrawingManager.cancel();
  }

  if (farmKakaoMap) {
    farmKakaoMap.setDraggable(true);
    farmKakaoMap.setZoomable(true);
  }

  document.getElementById('farmMapModal').classList.remove('open');
}

function searchFarmAddress() {
  const query =
    document.getElementById('farmAddressSearch').value.trim();

  if (!query || !farmKakaoMap || !kakao.maps.services) return;

  const places =
    new kakao.maps.services.Places();

  places.keywordSearch(
    query,
    function(result, status) {
      if (
        status === kakao.maps.services.Status.OK &&
        result.length
      ) {
        const p = result[0];
        farmKakaoMap.setCenter(
          new kakao.maps.LatLng(
            Number(p.y),
            Number(p.x)
          )
        );
        farmKakaoMap.setLevel(3);
        setFarmMapInfo(`${p.place_name || query} 위치로 이동했습니다.`);
      } else {
        setFarmMapInfo('검색 결과가 없습니다.');
      }
    }
  );
}

document.getElementById('farmMapButton')
  .addEventListener('click', openFarmMapEditor);

document.getElementById('farmMapCloseButton')
  .addEventListener('click', closeFarmMapEditor);

document.getElementById('farmBoundaryDrawButton')
  .addEventListener('click', startFarmBoundaryDrawing);

document.getElementById('farmBoundarySaveButton')
  .addEventListener('click', saveFarmBoundary);

document.getElementById('farmBoundaryClearButton')
  .addEventListener('click', clearFarmBoundary);

document.getElementById('farmAddressSearchButton')
  .addEventListener('click', searchFarmAddress);

document.getElementById('farmAddressSearch')
  .addEventListener('keydown', function(event) {
    if (event.key === 'Enter') searchFarmAddress();
  });

/* =========================================================
   MOVABLE 3D OBJECTS
   - GLB의 관수 구역(valve/drip)은 기존 제어용이므로 제외
   - 그 외 Mesh/Group 중 이름으로 시설물을 자동 탐색
   - 저장은 site_id별 localStorage 사용
========================================================= */

let farmRoot = null;
let farmRootPivot = null;
let farmRootBasePosition = null;

const FARM_ROOT_TRANSFORM_STORAGE_KEY =
  `konggomul:farm-root-transform:site:${MQTT_SITE_ID}`;

function readFarmRootTransform() {
  try {
    const raw = localStorage.getItem(FARM_ROOT_TRANSFORM_STORAGE_KEY);
    const value = raw ? JSON.parse(raw) : null;
    return value && typeof value === 'object' ? value : null;
  } catch (error) {
    return null;
  }
}

function saveFarmRootTransform() {
  if (!farmRootPivot) return;
  localStorage.setItem(
    FARM_ROOT_TRANSFORM_STORAGE_KEY,
    JSON.stringify({
      offset: {
        x: farmRootBasePosition ? farmRootPivot.position.x - farmRootBasePosition.x : 0,
        y: farmRootBasePosition ? farmRootPivot.position.y - farmRootBasePosition.y : 0,
        z: farmRootBasePosition ? farmRootPivot.position.z - farmRootBasePosition.z : 0
      },
      rotationY: farmRootPivot.rotation.y
    })
  );
  scheduleServerDesignSave();
}

function updateFarmRootTransformInfo() {
  const el = document.getElementById('farmRootTransformInfo');
  if (!el || !farmRootPivot) return;

  const deg = THREE.MathUtils.radToDeg(farmRootPivot.rotation.y);
  const normalized = ((deg % 360) + 360) % 360;
  const p = farmRootPivot.position;

  el.textContent =
    `전체 위치 X ${p.x.toFixed(1)}m / Z ${p.z.toFixed(1)}m · 방향 ${normalized.toFixed(0)}°`;
}

function createFarmRootCenterPivot() {
  if (!farmRoot || farmRootPivot) return;

  farmRoot.updateMatrixWorld(true);

  const box = new THREE.Box3().setFromObject(farmRoot);
  const centerWorld = new THREE.Vector3();
  box.getCenter(centerWorld);

  // 관수시스템의 실제 bounding-box 중심을 회전축으로 사용한다.
  farmRootPivot = new THREE.Group();
  farmRootPivot.name = 'KONGGOMUL_IRRIGATION_SYSTEM_PIVOT';
  farmRootPivot.position.copy(centerWorld);
  scene.add(farmRootPivot);

  // 현재 월드 변환을 보존한 채 farmRoot를 중심 Pivot의 자식으로 변경한다.
  farmRootPivot.attach(farmRoot);
  farmRootBasePosition = farmRootPivot.position.clone();

  const saved = readFarmRootTransform();
  if (saved) {
    if (saved.offset && farmRootBasePosition) {
      farmRootPivot.position.set(
        farmRootBasePosition.x + (Number(saved.offset.x) || 0),
        farmRootBasePosition.y + (Number(saved.offset.y) || 0),
        farmRootBasePosition.z + (Number(saved.offset.z) || 0)
      );
    }
    if (Number.isFinite(Number(saved.rotationY))) {
      farmRootPivot.rotation.y = Number(saved.rotationY);
    }
  }

  farmRootPivot.updateMatrixWorld(true);
  updateFarmRootTransformInfo();
}

function rotateFarmRootAroundCenter(degrees) {
  if (!farmRootPivot) return;
  farmRootPivot.rotation.y += THREE.MathUtils.degToRad(degrees);
  farmRootPivot.updateMatrixWorld(true);
  saveFarmRootTransform();
  updateFarmRootTransformInfo();
}

function moveFarmRoot(dx, dz) {
  if (!farmRootPivot) return;
  farmRootPivot.position.x += dx;
  farmRootPivot.position.z += dz;
  farmRootPivot.updateMatrixWorld(true);
  saveFarmRootTransform();
  updateFarmRootTransformInfo();
}

function resetFarmRootTransform() {
  if (!farmRootPivot || !farmRootBasePosition) return;
  farmRootPivot.position.copy(farmRootBasePosition);
  farmRootPivot.rotation.set(0, 0, 0);
  farmRootPivot.updateMatrixWorld(true);
  localStorage.removeItem(FARM_ROOT_TRANSFORM_STORAGE_KEY);
  scheduleServerDesignSave();
  updateFarmRootTransformInfo();
}

function showFarmRootMoveGizmo() {
  if (!farmRootPivot) return;
  selectedMovableObject = null;
  transformControls.attach(farmRootPivot);
  transformControls.setMode('translate');
  transformHelper.visible = true;
  farmEditorStatus('관수시스템 전체 이동축 표시 · 축을 드래그하여 이동하세요.');
}

function focusFarmRootCenter() {
  if (!farmRootPivot) return;
  controls.target.copy(farmRootPivot.position);
  controls.update();
}

document.getElementById('farmRootRotateLeft')
  .addEventListener('click', () => rotateFarmRootAroundCenter(-15));
document.getElementById('farmRootRotateRight')
  .addEventListener('click', () => rotateFarmRootAroundCenter(15));
document.getElementById('farmRootTransformReset')
  .addEventListener('click', resetFarmRootTransform);
document.getElementById('farmRootMoveLeft')
  .addEventListener('click', () => moveFarmRoot(-1, 0));
document.getElementById('farmRootMoveRight')
  .addEventListener('click', () => moveFarmRoot(1, 0));
document.getElementById('farmRootMoveForward')
  .addEventListener('click', () => moveFarmRoot(0, -1));
document.getElementById('farmRootMoveBack')
  .addEventListener('click', () => moveFarmRoot(0, 1));
document.getElementById('farmRootTransformMode')
  .addEventListener('click', showFarmRootMoveGizmo);
document.getElementById('farmRootCenterView')
  .addEventListener('click', focusFarmRootCenter);

let selectedMovableObject = null;
let movableObjects = [];
let objectMoveGround = null;

const OBJECT_LAYOUT_STORAGE_KEY =
  `konggomul:3d-object-layout:site:${MQTT_SITE_ID}`;

const MOVABLE_KEYWORDS = [
  'tank','water_tank','watertank','pump','sensor','esp32',
  'sprinkler','filter','fertilizer','fertigation','controller',
  'reservoir','motor','box',
  '물탱크','탱크','펌프','센서','스프링클러','제어반'
];

function isZoneControlObjectName(name) {
  const n = String(name || '').toUpperCase();
  return n.startsWith('ZONE_VALVE_') || n.startsWith('DRIP_');
}

function isMovableName(name) {
  const n = String(name || '').toLowerCase();
  if (!n || isZoneControlObjectName(n)) return false;
  return MOVABLE_KEYWORDS.some(function(keyword) {
    return n.includes(keyword.toLowerCase());
  });
}

function getMovableRoot(object) {
  let current = object;
  let candidate = object;

  while (current && current !== farmRoot) {
    if (isMovableName(current.name)) candidate = current;
    current = current.parent;
  }

  return candidate;
}

function discoverMovableObjects() {
  const map = new Map();

  if (!farmRoot) return;

  farmRoot.traverse(function(object) {
    if (!object.name || !isMovableName(object.name)) return;

    const root = getMovableRoot(object);
    if (!root || !root.uuid) return;

    map.set(root.uuid, root);
  });

  movableObjects = Array.from(map.values());

  movableObjects.forEach(function(object) {
    if (!object.userData.konggomulOriginalTransform) {
      object.userData.konggomulOriginalTransform = {
        position: object.position.toArray(),
        rotation: [object.rotation.x, object.rotation.y, object.rotation.z]
      };
    }
  });

  restoreObjectLayout();
  renderMovableObjectList();
}

function readObjectLayout() {
  try {
    const raw = localStorage.getItem(OBJECT_LAYOUT_STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : {};
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch (error) {
    console.error('[3D OBJECT] 저장 위치 읽기 실패:', error);
    return {};
  }
}

function saveObjectLayout() {
  const layout = {};

  movableObjects.forEach(function(object) {
    layout[object.name] = {
      position: {
        x: object.position.x,
        y: object.position.y,
        z: object.position.z
      },
      rotationY: object.rotation.y
    };
  });

  try {
    localStorage.setItem(
      OBJECT_LAYOUT_STORAGE_KEY,
      JSON.stringify(layout)
    );
    scheduleServerDesignSave();
  } catch (error) {
    console.error('[3D OBJECT] 위치 저장 실패:', error);
  }
}

function restoreObjectLayout() {
  const layout = readObjectLayout();

  movableObjects.forEach(function(object) {
    const saved = layout[object.name];
    if (!saved) return;

    if (saved.position) {
      object.position.set(
        Number(saved.position.x) || 0,
        Number(saved.position.y) || 0,
        Number(saved.position.z) || 0
      );
    }

    if (Number.isFinite(Number(saved.rotationY))) {
      object.rotation.y = Number(saved.rotationY);
    }
  });
}

function renderMovableObjectList() {
  const el = document.getElementById('movableObjectList');
  if (!el) return;

  if (!movableObjects.length) {
    el.innerHTML =
      '<div class="farm-empty">GLB에서 이동 가능한 시설물 이름을 찾지 못했습니다.<br>지원 이름: tank, pump, sensor, esp32, sprinkler 등</div>';
    return;
  }

  el.innerHTML = movableObjects.map(function(object) {
    const selected =
      selectedMovableObject &&
      selectedMovableObject.uuid === object.uuid;

    return `
      <button type="button"
        class="movable-object-card${selected ? ' selected' : ''}"
        data-movable-uuid="${object.uuid}">
        ${escapeFarmText(object.name)}
      </button>
    `;
  }).join('');

  el.querySelectorAll('[data-movable-uuid]').forEach(function(button) {
    button.addEventListener('click', function() {
      selectedMovableObject =
        movableObjects.find(function(object) {
          return object.uuid === button.dataset.movableUuid;
        }) || null;

      if (selectedMovableObject) {

        transformControls.attach(
          selectedMovableObject
        );

        transformControls.setMode(
          'translate'
        );

        transformHelper.visible =
          true;

      }

      else {

        transformControls.detach();
        transformHelper.visible =
          false;

      }

      renderMovableObjectList();
      updateObjectMoveControls();

      if (selectedMovableObject) {
        farmEditorStatus(
          `${selectedMovableObject.name} 선택됨 · 3D 화면의 원하는 땅을 클릭하세요.`
        );
      }
    });
  });
}

function updateObjectMoveControls() {
  const left = document.getElementById('objectRotateLeft');
  const right = document.getElementById('objectRotateRight');
  const reset = document.getElementById('objectReset');
  const info = document.getElementById('objectMoveInfo');
  const enabled = !!selectedMovableObject;

  if (left) left.disabled = !enabled;
  if (right) right.disabled = !enabled;
  if (reset) reset.disabled = !enabled;

  if (info) {
    if (!selectedMovableObject) {
      info.textContent = '이동할 시설물을 선택하세요.';
    } else {
      const p = selectedMovableObject.position;
      info.textContent =
        `${selectedMovableObject.name} · X ${p.x.toFixed(2)} / Z ${p.z.toFixed(2)} / 회전 ${(selectedMovableObject.rotation.y * 180 / Math.PI).toFixed(0)}° · 이동축을 직접 드래그 가능`;
    }
  }
}

function rotateSelectedObject(degrees) {
  if (!selectedMovableObject) return;
  selectedMovableObject.rotation.y +=
    THREE.MathUtils.degToRad(degrees);
  saveObjectLayout();
  updateObjectMoveControls();
}

function resetSelectedObject() {
  if (!selectedMovableObject) return;

  const original =
    selectedMovableObject.userData.konggomulOriginalTransform;

  if (!original) return;

  selectedMovableObject.position.fromArray(original.position);
  selectedMovableObject.rotation.set(
    original.rotation[0],
    original.rotation[1],
    original.rotation[2]
  );

  saveObjectLayout();
  updateObjectMoveControls();
  farmEditorStatus(`${selectedMovableObject.name} 위치를 초기화했습니다.`);
}

document.getElementById('objectRotateLeft')
  .addEventListener('click', function() {
    rotateSelectedObject(-15);
  });

document.getElementById('objectRotateRight')
  .addEventListener('click', function() {
    rotateSelectedObject(15);
  });

document.getElementById('objectReset')
  .addEventListener('click', resetSelectedObject);

/* =========================================================
   LOAD GLB
========================================================= */

function loadStandardFarmTemplate() {
  if (standardTemplateLoaded) return Promise.resolve(true);
  standardTemplateLoaded = true;

  return new Promise((resolve) => {
  document.body.classList.remove('custom-design-mode');
  document.getElementById('systemStatus').textContent = '로딩 중';

  const loader = new GLTFLoader();

  loader.load(

  '/farm3d-test/관수시스템.glb',


  /* =======================================================
     SUCCESS
  ======================================================= */

  async function(gltf) {


    const farm =
      gltf.scene;


    scene.add(
      farm
    );

    farmRoot = farm;

    discoverMovableObjects();

    // 먼저 실제 농장 경계 기준으로 GLB 기본 위치를 확정한다.
    restore3DFarmBoundary();

    // 기본 위치가 확정된 뒤 중심 Pivot을 만들고 사용자 이동/회전값을 복원한다.
    // 이 순서를 지켜야 새로고침해도 같은 위치가 유지된다.
    createFarmRootCenterPivot();

    initMainSatelliteMap();


    let completeZones =
      0;



    /* =====================================================
       SEARCH 16 ZONES
    ===================================================== */

    for (

      let zoneNumber = 1;

      zoneNumber <= ZONE_COUNT;

      zoneNumber++

    ) {


      const n =
        padZone(
          zoneNumber
        );



      /* ---------------------------------------------------
         OBJECT NAME
      --------------------------------------------------- */

      const valveName =
        `ZONE_VALVE_${n}`;


      const drip1Name =
        `DRIP_${n}_1`;


      const drip2Name =
        `DRIP_${n}_2`;



      /* ---------------------------------------------------
         SEARCH
      --------------------------------------------------- */

      const valve =
        farm.getObjectByName(
          valveName
        );


      const drip1 =
        farm.getObjectByName(
          drip1Name
        );


      const drip2 =
        farm.getObjectByName(
          drip2Name
        );



      /* ---------------------------------------------------
         MATERIAL
      --------------------------------------------------- */

      const valveMaterial =
        prepareMaterial(
          valve
        );


      const drip1Material =
        prepareMaterial(
          drip1
        );


      const drip2Material =
        prepareMaterial(
          drip2
        );



      /* ---------------------------------------------------
         ZONE DATA
      --------------------------------------------------- */

      zones[zoneNumber] = {

        zoneNumber,

        valve,

        drip1,

        drip2,

        valveMaterial,

        drip1Material,

        drip2Material,


        valveOriginalColor:

          valveMaterial

            ? valveMaterial.color.clone()

            : null,


        drip1OriginalColor:

          drip1Material

            ? drip1Material.color.clone()

            : null,


        drip2OriginalColor:

          drip2Material

            ? drip2Material.color.clone()

            : null,


        /*
         현재는 테스트 데이터.

         다음 단계에서 실제 MQTT 상태로 대체.
        */

        state:
          false,


        soilMoisture:
          null,


        lastWater:
          null

      };



      /* ---------------------------------------------------
         CLICK TAG
      --------------------------------------------------- */

      tagZoneObject(

        valve,

        zoneNumber,

        'valve'

      );


      tagZoneObject(

        drip1,

        zoneNumber,

        'drip1'

      );


      tagZoneObject(

        drip2,

        zoneNumber,

        'drip2'

      );



      /* ---------------------------------------------------
         CHECK
      --------------------------------------------------- */

      if (

        valveMaterial &&

        drip1Material &&

        drip2Material

      ) {

        completeZones++;

      }

      else {

        console.warn(

          `ZONE ${n} 객체 누락`,

          {
            valve,
            drip1,
            drip2
          }

        );

      }

    }



    /* =====================================================
       READY
    ===================================================== */

    document.getElementById(
      'systemStatus'
    ).textContent =

      '준비 완료';


    console.log(

      'KONGGOMUL ZONES',

      zones

    );

    /*
      GLB 구역 생성 완료 후
      실제 MQTT 상태 연결 시작
    */

    /*
      실제 장비 상태는 DB에 저장된 3D 배치 정보를 기준으로
      loadBoundZoneStates()가 갱신한다.
    */
    // 최초 MQTT/바인딩 상태까지 반영한 뒤에 초기 로딩 완료로 본다.
    try {
      await loadBoundZoneStates();
    } catch (error) {
      console.error('[3D BINDING] 최초 상태 조회 실패:', error);
    }
    resolve(true);

  },



  /* =======================================================
     PROGRESS
  ======================================================= */

  function(xhr) {

    if (!xhr.total) {

      return;

    }


    const percent =
      Math.round(

        xhr.loaded /

        xhr.total *

        100

      );


    document.getElementById(
      'systemStatus'
    ).textContent =

      '로딩 중';

  },



  /* =======================================================
     ERROR
  ======================================================= */

  function(error) {


    console.error(
      error
    );


    document.getElementById(
      'systemStatus'
    ).textContent =

      '로딩 실패';

    resolve(false);

  }

);
  });
}



/* =========================================================
   CUSTOM SMART FARM 3D DESIGNER V3
========================================================= */
const CUSTOM_LAYOUT_STORAGE_KEY = `konggomul:custom-layout:site:${MQTT_SITE_ID}`;
const customDesignRoot = new THREE.Group();
customDesignRoot.name = 'KONGGOMUL_CUSTOM_DESIGN_ROOT';
scene.add(customDesignRoot);

let customActiveFacilityType = null;
let customActivePipeType = null;
let customPipeStart = null;
let customSelectedObject = null;
let customObjectSerial = 1;

const CUSTOM_FACILITY_LABELS = {tank:'물탱크',pump:'펌프',filter:'필터',valve:'밸브',esp32:'ESP32',soil_sensor:'토양센서',climate_sensor:'온습도센서',power_switch:'전원버튼',nutrient_mixer:'양액기',cctv:'CCTV'};
const CUSTOM_PIPE_LABELS = {main:'메인배관',branch:'분기관',drip:'점적호스'};

function customStatus(text){const el=document.getElementById('customDesignerStatus');if(el)el.textContent=text||'';}
function customMaterial(color,metalness=0.05,roughness=0.65){return new THREE.MeshStandardMaterial({color,metalness,roughness});}
function markCustomObject(root,type,kind){root.userData.customDesignObject=true;root.userData.customType=type;root.userData.customKind=kind;root.traverse(c=>{c.userData.customDesignOwner=root.uuid;});return root;}

function makeFacility(type,id){
  const g=new THREE.Group(); g.name=`CUSTOM_${type.toUpperCase()}_${id}`; g.userData.customId=id;
  const add=(geo,mat,x=0,y=0,z=0)=>{const m=new THREE.Mesh(geo,mat);m.position.set(x,y,z);m.castShadow=true;m.receiveShadow=true;g.add(m);return m;};
  if(type==='tank'){
    add(new THREE.CylinderGeometry(1.45,1.45,2.8,32),customMaterial(0x3b82f6,0.15,0.38),0,1.4,0);
    add(new THREE.CylinderGeometry(1.52,1.52,.12,32),customMaterial(0xdbeafe,0.15,.35),0,2.82,0);
  }else if(type==='pump'){
    add(new THREE.BoxGeometry(1.45,.75,.9),customMaterial(0x2563eb,.35,.32),0,.5,0);
    add(new THREE.CylinderGeometry(.34,.34,.9,24),customMaterial(0x64748b,.55,.25),.92,.5,0).rotation.z=Math.PI/2;
  }else if(type==='filter'){
    add(new THREE.CylinderGeometry(.38,.38,1.45,24),customMaterial(0x475569,.45,.3),0,.78,0);
    add(new THREE.BoxGeometry(.9,.12,.45),customMaterial(0x94a3b8,.3,.35),0,.08,0);
  }else if(type==='valve'){
    add(new THREE.CylinderGeometry(.18,.18,.75,18),customMaterial(0x64748b,.5,.25),0,.28,0).rotation.z=Math.PI/2;
    add(new THREE.BoxGeometry(.5,.16,.12),customMaterial(0xef4444,.15,.45),0,.65,0);
    add(new THREE.BoxGeometry(.12,.16,.5),customMaterial(0xef4444,.15,.45),0,.65,0);
  }else if(type==='esp32'){
    add(new THREE.BoxGeometry(.72,.16,1.05),customMaterial(0x0f766e,.15,.45),0,.22,0);
    add(new THREE.BoxGeometry(.5,.08,.22),customMaterial(0x94a3b8,.55,.25),0,.34,-.34);
  }else if(type==='soil_sensor'){
    add(new THREE.BoxGeometry(.38,.45,.24),customMaterial(0x16a34a,.1,.55),0,.48,0);
    add(new THREE.BoxGeometry(.08,.72,.08),customMaterial(0x94a3b8,.55,.25),-.11,.02,0);
    add(new THREE.BoxGeometry(.08,.72,.08),customMaterial(0x94a3b8,.55,.25),.11,.02,0);
  }else if(type==='power_switch'){
    add(new THREE.BoxGeometry(.75,.85,.38),customMaterial(0x1e293b,.25,.38),0,.48,0);
    add(new THREE.CylinderGeometry(.24,.24,.09,28),customMaterial(0x0ea5e9,.2,.3),0,.53,-.24).rotation.x=Math.PI/2;
    add(new THREE.BoxGeometry(.54,.1,.08),customMaterial(0xe2e8f0,.15,.4),0,.97,0);
  }else if(type==='climate_sensor'){
    add(new THREE.BoxGeometry(.5,.72,.26),customMaterial(0xf8fafc,.2,.4),0,.53,0);
    add(new THREE.BoxGeometry(.32,.18,.035),customMaterial(0x38bdf8,.1,.4),0,.62,-.15);
    add(new THREE.CylinderGeometry(.06,.06,.2,12),customMaterial(0x64748b,.4,.2),0,.1,0);
  }else if(type==='nutrient_mixer'){
    // 양액기 3D 외형만 추가한다. 기존 자동화 및 제어 로직은 변경하지 않는다.
    const steel=customMaterial(0x94a3b8,.5,.28);
    const body=customMaterial(0xe2e8f0,.25,.35);
    const accent=customMaterial(0x0ea5e9,.2,.35);
    const liquid=customMaterial(0x22c55e,.12,.3);
    // 혼합 탱크와 상단 덮개
    add(new THREE.CylinderGeometry(.88,.88,1.45,28),body,0,1.0,0);
    add(new THREE.CylinderGeometry(.92,.92,.12,28),steel,0,1.78,0);
    add(new THREE.CylinderGeometry(.79,.79,.045,28),liquid,0,1.86,0);
    // A/B 원액통과 주입관
    for(const x of [-1.24,1.24]){
      add(new THREE.CylinderGeometry(.31,.31,.9,20),accent,x,.48,.12);
      add(new THREE.CylinderGeometry(.33,.33,.07,20),steel,x,.96,.12);
      add(new THREE.BoxGeometry(.1,.1,.95),steel,x,1.05,-.39);
    }
    // 제어함, 상태창, 배출 배관
    add(new THREE.BoxGeometry(.8,.83,.35),customMaterial(0x334155,.25,.4),0,.97,-.97);
    add(new THREE.BoxGeometry(.56,.29,.04),accent,0,1.11,-1.17);
    add(new THREE.CylinderGeometry(.11,.11,.9,14),steel,0,.32,1.03).rotation.x=Math.PI/2;
    // 지지대
    for(const x of [-.6,.6])for(const z of [-.5,.5])add(new THREE.BoxGeometry(.11,.32,.11),steel,x,.16,z);
  }else if(type==='cctv'){
    add(new THREE.CylinderGeometry(.055,.055,1.8,12),customMaterial(0x475569,.45,.32),0,.9,0);
    add(new THREE.BoxGeometry(.82,.48,.55),customMaterial(0xf8fafc,.12,.38),0,1.88,0);
    add(new THREE.CylinderGeometry(.19,.19,.12,24),customMaterial(0x111827,.25,.28),0,1.88,-.33).rotation.x=Math.PI/2;
    add(new THREE.CylinderGeometry(.09,.09,.13,24),customMaterial(0x38bdf8,.1,.25),0,1.88,-.40).rotation.x=Math.PI/2;
    add(new THREE.BoxGeometry(.38,.10,.30),customMaterial(0x64748b,.35,.3),0,1.58,0);
  }
  return markCustomObject(g,type,'facility');
}

function makePipe(type,id,start,end){
  const a=new THREE.Vector3(start.x,.16,start.z), b=new THREE.Vector3(end.x,.16,end.z);
  const dir=new THREE.Vector3().subVectors(b,a); const len=dir.length(); if(len<.2)return null;
  const radius=type==='main'?.13:type==='branch'?.09:.045;
  const color=type==='main'?0x2563eb:type==='branch'?0x0ea5e9:0x111827;
  const mesh=new THREE.Mesh(new THREE.CylinderGeometry(radius,radius,len,14),customMaterial(color,.15,.45));
  mesh.position.copy(a).add(b).multiplyScalar(.5); mesh.quaternion.setFromUnitVectors(new THREE.Vector3(0,1,0),dir.clone().normalize());
  mesh.name=`CUSTOM_PIPE_${type.toUpperCase()}_${id}`; mesh.userData.customId=id; mesh.userData.pipeStart={x:start.x,z:start.z}; mesh.userData.pipeEnd={x:end.x,z:end.z};
  return markCustomObject(mesh,type,'pipe');
}

function getCustomGroundPoint(event){
  const rect=renderer.domElement.getBoundingClientRect();
  pointer.x=((event.clientX-rect.left)/rect.width)*2-1; pointer.y=-((event.clientY-rect.top)/rect.height)*2+1; raycaster.setFromCamera(pointer,camera);
  if(generatedFarmBoundaryMesh){const hit=raycaster.intersectObject(generatedFarmBoundaryMesh,false)[0];if(hit)return hit.point.clone();}
  const plane=new THREE.Plane(new THREE.Vector3(0,1,0),0); const p=new THREE.Vector3(); return raycaster.ray.intersectPlane(plane,p)?p:null;
}

function selectCustomObject(obj){
  customSelectedObject=obj||null; transformControls.detach(); transformHelper.visible=false;
  renderCustomObjectList();
  if(obj)customStatus(`${obj.userData.customKind==='pipe'?(CUSTOM_PIPE_LABELS[obj.userData.customType]||'배관'):(CUSTOM_FACILITY_LABELS[obj.userData.customType]||'시설물')} 선택됨`);
}
function findCustomOwner(object){let o=object;while(o&&o!==scene){if(o.userData?.customDesignObject)return o;o=o.parent;}return null;}
function selectCustomObjectAt(event){
  const rect=renderer.domElement.getBoundingClientRect();pointer.x=((event.clientX-rect.left)/rect.width)*2-1;pointer.y=-((event.clientY-rect.top)/rect.height)*2+1;raycaster.setFromCamera(pointer,camera);
  const hits=raycaster.intersectObjects(customDesignRoot.children,true); if(hits.length){selectCustomObject(findCustomOwner(hits[0].object));return true;} return false;
}
function activateCustomFacility(type){customActiveFacilityType=type;customActivePipeType=null;customPipeStart=null;document.querySelectorAll('[data-facility],[data-pipe]').forEach(b=>b.classList.remove('active'));document.querySelector(`[data-facility="${type}"]`)?.classList.add('active');customStatus(`${CUSTOM_FACILITY_LABELS[type]} 배치: 농장 위 원하는 위치를 클릭하세요.`);}
function activateCustomPipe(type){customActivePipeType=type;customActiveFacilityType=null;customPipeStart=null;document.querySelectorAll('[data-facility],[data-pipe]').forEach(b=>b.classList.remove('active'));document.querySelector(`[data-pipe="${type}"]`)?.classList.add('active');customStatus(`${CUSTOM_PIPE_LABELS[type]}: 시작점을 클릭하세요.`);}
function cancelCustomTool(){customActiveFacilityType=null;customActivePipeType=null;customPipeStart=null;document.querySelectorAll('[data-facility],[data-pipe]').forEach(b=>b.classList.remove('active'));customStatus('도구를 취소했습니다. 시설물 또는 배관을 선택하세요.');}

function addCustomFacilityAt(type,p){const id=customObjectSerial++;const o=makeFacility(type,id);o.position.set(p.x,0,p.z);customDesignRoot.add(o);selectCustomObject(o);saveCustomLayout();customStatus(`${CUSTOM_FACILITY_LABELS[type]} 추가 완료 · 이동축으로 미세 조정할 수 있습니다.`);}
function addCustomPipeBetween(type,a,b){const id=customObjectSerial++;const o=makePipe(type,id,a,b);if(!o){customStatus('배관 길이가 너무 짧습니다.');return;}customDesignRoot.add(o);selectCustomObject(o);saveCustomLayout();customStatus(`${CUSTOM_PIPE_LABELS[type]} 생성 완료`);}

function serializeCustomObject(o){
  if(o.userData.customKind==='pipe')return {id:o.userData.customId,kind:'pipe',type:o.userData.customType,start:o.userData.pipeStart,end:o.userData.pipeEnd};
  return {id:o.userData.customId,kind:'facility',type:o.userData.customType,position:o.position.toArray(),rotation:o.rotation.toArray().slice(0,3)};
}
function saveCustomLayout(){
  const data={site_id:MQTT_SITE_ID,version:1,objects:customDesignRoot.children.filter(o=>o.userData.customDesignObject).map(serializeCustomObject),updated_at:new Date().toISOString()};
  localStorage.setItem(CUSTOM_LAYOUT_STORAGE_KEY,JSON.stringify(data));
  if(farmDesignState?.design_mode==='custom'){farmDesignState.facilities=data.objects.filter(x=>x.kind==='facility');farmDesignState.pipes=data.objects.filter(x=>x.kind==='pipe');farmDesignState.updated_at=data.updated_at;writeFarmDesignState(farmDesignState);}
  renderCustomObjectList(); return data;
}
function restoreCustomLayout(){
  customDesignRoot.clear(); let data=null;try{data=JSON.parse(localStorage.getItem(CUSTOM_LAYOUT_STORAGE_KEY)||'null');}catch(e){}
  const items=Array.isArray(data?.objects)?data.objects:[];let maxId=0;
  for(const x of items){let o=null;if(x.kind==='pipe'&&x.start&&x.end)o=makePipe(x.type,x.id,x.start,x.end);else if(x.kind==='facility'){o=makeFacility(x.type,x.id);if(Array.isArray(x.position))o.position.fromArray(x.position);if(Array.isArray(x.rotation))o.rotation.set(x.rotation[0]||0,x.rotation[1]||0,x.rotation[2]||0);}if(o){customDesignRoot.add(o);maxId=Math.max(maxId,Number(x.id)||0);}}
  customObjectSerial=maxId+1;renderCustomObjectList();customStatus(items.length?`저장된 직접 설계 ${items.length}개 객체를 복원했습니다.`:'빈 농장입니다. 시설물을 추가하세요.');
}
function renderCustomObjectList(){const el=document.getElementById('customObjectList');if(!el)return;const arr=customDesignRoot.children.filter(o=>o.userData.customDesignObject);el.innerHTML=arr.length?arr.map(o=>{const label=o.userData.customKind==='pipe'?CUSTOM_PIPE_LABELS[o.userData.customType]:CUSTOM_FACILITY_LABELS[o.userData.customType];return `<div class="custom-object-item ${customSelectedObject===o?'selected':''}"><button type="button" data-custom-id="${o.userData.customId}">${label} #${o.userData.customId}</button></div>`}).join(''):'<div class="farm-empty">아직 설계 객체가 없습니다.</div>';el.querySelectorAll('[data-custom-id]').forEach(b=>b.addEventListener('click',()=>{const id=Number(b.dataset.customId);selectCustomObject(arr.find(o=>Number(o.userData.customId)===id)||null);}));}
function deleteCustomSelected(){if(!customSelectedObject)return;transformControls.detach();transformHelper.visible=false;customDesignRoot.remove(customSelectedObject);customSelectedObject=null;saveCustomLayout();customStatus('선택 객체를 삭제했습니다.');}
function rotateCustomSelected(deg){if(!customSelectedObject||customSelectedObject.userData.customKind==='pipe')return;customSelectedObject.rotation.y+=THREE.MathUtils.degToRad(deg);saveCustomLayout();}
function showCustomMove(){if(!customSelectedObject){customStatus('먼저 시설물을 선택하세요.');return;}if(customSelectedObject.userData.customKind==='pipe'){customStatus('배관은 현재 이동축 편집 대상이 아닙니다. 삭제 후 다시 그려주세요.');return;}selectedMovableObject=null;transformControls.attach(customSelectedObject);transformControls.setMode('translate');transformHelper.visible=true;customStatus('이동축을 드래그하세요. 놓으면 자동 저장됩니다.');}


/* =========================================================
   CUSTOM DESIGN COMPLETION + REAL HARDWARE BINDING V4
========================================================= */
const CUSTOM_HARDWARE_STORAGE_KEY = `konggomul:custom-hardware:site:${MQTT_SITE_ID}`;
let customHardwareBindings = {};
let customHardwareSelectedObject = null;
let customHardwareDevices = [];
let customHardwareEntities = [];
let customPreviewCameras = [];

// V5 운영 상태: 직접 설계 밸브의 실제 릴레이 상태를 3D에 표시한다.
const customValveRuntime = new Map();
let customValveSyncBusy = false;
let customValvePollTimer = null;
let customValveCommandBusy = false;

function parseRelayOn(value){
  if(typeof value==='boolean') return value;
  return ['on','true','1'].includes(String(value??'').trim().toLowerCase());
}
function makeValveStatusSprite(){
  const canvas=document.createElement('canvas'); canvas.width=512; canvas.height=160;
  const texture=new THREE.CanvasTexture(canvas); texture.colorSpace=THREE.SRGBColorSpace; texture.minFilter=THREE.LinearFilter;
  const material=new THREE.SpriteMaterial({map:texture,transparent:true,depthTest:false,depthWrite:false});
  const sprite=new THREE.Sprite(material); sprite.name='CUSTOM_VALVE_STATUS_LABEL'; sprite.scale.set(2.65,.83,1); sprite.position.set(0,1.55,0); sprite.renderOrder=999;
  sprite.userData.statusCanvas=canvas; sprite.userData.statusTexture=texture; return sprite;
}
function ensureValveStatusSprite(o){
  if(!o||o.userData?.customType!=='valve')return null;
  let sp=o.getObjectByName('CUSTOM_VALVE_STATUS_LABEL'); if(!sp){sp=makeValveStatusSprite();o.add(sp);}
  return sp;
}
function drawValveStatus(o,state){
  if(!o||o.userData?.customType!=='valve')return;
  const sp=ensureValveStatusSprite(o),c=sp.userData.statusCanvas,ctx=c.getContext('2d');
  const mode=state==='on'?'on':state==='off'?'off':state==='offline'?'offline':'unlinked';
  const bg=mode==='on'?'#16a34a':mode==='off'?'#334155':mode==='offline'?'#991b1b':'#b45309';
  const text=mode==='on'?'ON · 온라인':mode==='off'?'OFF · 온라인':mode==='offline'?'오프라인':'미연결';
  ctx.clearRect(0,0,c.width,c.height); ctx.fillStyle='rgba(2,6,23,.86)'; roundRect(ctx,8,8,496,144,36); ctx.fill();
  ctx.fillStyle=bg; roundRect(ctx,20,20,472,120,30); ctx.fill();
  ctx.fillStyle='#fff'; ctx.font=mode==='on'||mode==='off'?'900 48px sans-serif':'900 58px sans-serif'; ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillText(text,256,82);
  sp.userData.statusTexture.needsUpdate=true;
  // 멀리서도 상태를 알아볼 수 있도록 밸브 손잡이 자체도 상태색으로 바꾼다.
  o.traverse(ch=>{if(!ch.isMesh)return;if(ch.userData.baseValveColor===undefined&&ch.material?.color)ch.userData.baseValveColor=ch.material.color.getHex();if(ch.position.y>.5&&ch.material?.color){ch.material.color.setHex(mode==='on'?0x22c55e:mode==='off'?0xef4444:mode==='offline'?0x7f1d1d:0xf59e0b);}});
}
function roundRect(ctx,x,y,w,h,r){ctx.beginPath();ctx.roundRect?ctx.roundRect(x,y,w,h,r):(ctx.rect(x,y,w,h));}
function getCustomValveObjects(){return customDesignRoot.children.filter(o=>o.userData?.customDesignObject&&o.userData?.customType==='valve');}

// V6 운영 상태: 토양수분센서의 실제 연결/측정값을 3D 위에 표시한다.
const customSoilRuntime = new Map();
let customSoilSyncBusy = false;
function getCustomSoilObjects(){return customDesignRoot.children.filter(o=>o.userData?.customDesignObject&&o.userData?.customType==='soil_sensor');}
function makeSoilStatusSprite(){
  const canvas=document.createElement('canvas');canvas.width=640;canvas.height=176;
  const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;texture.minFilter=THREE.LinearFilter;
  const material=new THREE.SpriteMaterial({map:texture,transparent:true,depthTest:false,depthWrite:false});
  const sprite=new THREE.Sprite(material);sprite.name='CUSTOM_SOIL_STATUS_LABEL';sprite.scale.set(3.35,.92,1);sprite.position.set(0,1.45,0);sprite.renderOrder=999;
  sprite.userData.statusCanvas=canvas;sprite.userData.statusTexture=texture;return sprite;
}
function ensureSoilStatusSprite(o){
  if(!o||o.userData?.customType!=='soil_sensor')return null;
  let sp=o.getObjectByName('CUSTOM_SOIL_STATUS_LABEL');if(!sp){sp=makeSoilStatusSprite();o.add(sp);}return sp;
}
function normalizeSoilValue(entity){
  const raw=entity?.value ?? entity?.state ?? entity?.sensor_value ?? null;
  const n=Number(raw);return Number.isFinite(n)?n:null;
}
function drawSoilStatus(o,mode,value=null){
  if(!o||o.userData?.customType!=='soil_sensor')return;
  const sp=ensureSoilStatusSprite(o),c=sp.userData.statusCanvas,ctx=c.getContext('2d');
  let bg='#b45309',text='미연결';
  if(mode==='value'){bg='#0284c7';text=`수분 ${Number(value).toFixed(1)}% · 온라인`;}
  else if(mode==='nodata'){bg='#475569';text='데이터 없음 · 온라인';}
  else if(mode==='offline'){bg='#991b1b';text='오프라인';}
  ctx.clearRect(0,0,c.width,c.height);ctx.fillStyle='rgba(2,6,23,.88)';roundRect(ctx,8,8,624,160,38);ctx.fill();
  ctx.fillStyle=bg;roundRect(ctx,20,20,600,136,32);ctx.fill();
  ctx.fillStyle='#fff';ctx.font=mode==='value'?'900 44px sans-serif':'900 54px sans-serif';ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillText(text,320,88);
  sp.userData.statusTexture.needsUpdate=true;
}
function refreshSoilUnlinkedLabels(){
  customHardwareBindings=readCustomHardwareBindings();
  for(const o of getCustomSoilObjects()){
    const b=customHardwareBindings[o.userData.customId];
    if(!b?.device_id||!b?.entity_id){customSoilRuntime.delete(Number(o.userData.customId));drawSoilStatus(o,'unlinked');}
  }
}
async function syncCustomSoilStates(){
  if(customSoilSyncBusy||farmDesignState?.design_mode!=='custom'||!farmDesignState?.design_completed)return;
  customSoilSyncBusy=true;
  try{
    customHardwareBindings=readCustomHardwareBindings();
    const sensors=getCustomSoilObjects(),byDevice=new Map();
    for(const o of sensors){
      const b=customHardwareBindings[o.userData.customId];
      if(!b?.device_id||!b?.entity_id){drawSoilStatus(o,'unlinked');continue;}
      const id=Number(b.device_id);if(!byDevice.has(id))byDevice.set(id,[]);byDevice.get(id).push({o,b});
    }
    await Promise.all([...byDevice.entries()].map(async([deviceId,items])=>{
      try{
        const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${deviceId}`,{credentials:'include',cache:'no-store'});
        const d=await r.json();if(!r.ok||!d.ok)throw new Error(d.error||'센서 상태 조회 실패');
        if(d.online===false){
          for(const {o} of items){
            customSoilRuntime.delete(Number(o.userData.customId));
            drawSoilStatus(o,'offline');
          }
          return;
        }
        const ents=Array.isArray(d.entities)?d.entities:[];
        for(const {o,b} of items){
          const e=ents.find(x=>x.entity_id===b.entity_id);
          if(!e){customSoilRuntime.delete(Number(o.userData.customId));drawSoilStatus(o,'nodata');continue;}
          const value=normalizeSoilValue(e);
          if(value===null){customSoilRuntime.delete(Number(o.userData.customId));drawSoilStatus(o,'nodata');continue;}
          customSoilRuntime.set(Number(o.userData.customId),{value,updatedAt:Date.now(),entity:e});drawSoilStatus(o,'value',value);
        }
      }catch(err){console.warn('custom soil state sync failed',deviceId,err);for(const {o} of items)drawSoilStatus(o,'nodata');}
    }));
  }finally{customSoilSyncBusy=false;}
}
// V7: 3D 온습도센서/전원버튼 실측 상태 라벨 (기존 제어 로직은 변경하지 않음).
let customClimatePowerSyncBusy=false;
function customLiveObjects(type){return customDesignRoot.children.filter(o=>o.userData?.customDesignObject&&o.userData?.customType===type);}
function customLiveLabel(o){
  const type=o.userData.customType, name='CUSTOM_'+(type==='climate_sensor'?'CLIMATE':'POWER')+'_LIVE_LABEL';
  let sp=o.getObjectByName(name);
  if(!sp){
    const canvas=document.createElement('canvas');canvas.width=720;canvas.height=184;
    const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;texture.minFilter=THREE.LinearFilter;
    sp=new THREE.Sprite(new THREE.SpriteMaterial({map:texture,transparent:true,depthTest:false,depthWrite:false}));
    sp.name=name;sp.scale.set(type==='climate_sensor'?3.85:3.1,.99,1);sp.position.set(0,type==='climate_sensor'?1.75:1.65,0);sp.renderOrder=999;
    sp.userData.liveCanvas=canvas;sp.userData.liveTexture=texture;o.add(sp);
  }
  return sp;
}
function drawCustomLive(o,mode,value=null){
  const sp=customLiveLabel(o),canvas=sp.userData.liveCanvas,ctx=canvas.getContext('2d');
  const isClimate=o.userData.customType==='climate_sensor';
  let label=isClimate?'온습도 센서':'전원 버튼',detail='미연결',color='#b45309';
  if(mode==='offline'){detail='오프라인';color='#991b1b';}
  if(mode==='nodata'){detail='데이터 없음';color='#475569';}
  if(mode==='value'){
    if(isClimate){
      const t=value?.temperature,h=value?.humidity;
      detail=(t===null?'온도 --':`온도 ${t.toFixed(1)}°C`)+'  ·  '+(h===null?'습도 --':`습도 ${h.toFixed(1)}%`);
      color='#0369a1';
    }else{detail=value?'ON · 온라인':'OFF · 온라인';color=value?'#15803d':'#334155';}
  }
  ctx.clearRect(0,0,720,184);ctx.fillStyle='rgba(2,6,23,.9)';roundRect(ctx,6,6,708,172,32);ctx.fill();
  ctx.fillStyle=color;roundRect(ctx,16,16,688,152,26);ctx.fill();
  ctx.fillStyle='#fff';ctx.textAlign='center';ctx.textBaseline='middle';ctx.font='bold 33px sans-serif';ctx.fillText(label,360,61);
  ctx.font='bold 46px sans-serif';ctx.fillText(detail,360,122,675);sp.userData.liveTexture.needsUpdate=true;
}
function refreshCustomLiveLabels(){
  const bindings=readCustomHardwareBindings();
  for(const type of ['climate_sensor','power_switch'])for(const o of customLiveObjects(type)){
    const b=bindings[o.userData.customId];if(!b?.device_id||!b?.entity_id)drawCustomLive(o,'unlinked');
  }
}
async function syncCustomClimatePowerStates(){
  if(customClimatePowerSyncBusy||farmDesignState?.design_mode!=='custom'||!farmDesignState?.design_completed)return;
  customClimatePowerSyncBusy=true;
  try{
    const bindings=readCustomHardwareBindings(),byDevice=new Map();
    for(const type of ['climate_sensor','power_switch'])for(const o of customLiveObjects(type)){
      const b=bindings[o.userData.customId];
      if(!b?.device_id||!b?.entity_id){drawCustomLive(o,'unlinked');continue;}
      const id=Number(b.device_id);if(!byDevice.has(id))byDevice.set(id,[]);byDevice.get(id).push({o,b});
    }
    await Promise.all([...byDevice.entries()].map(async([deviceId,items])=>{
      try{
        const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${deviceId}`,{credentials:'include',cache:'no-store'});
        const d=await r.json();if(!r.ok||!d.ok)throw new Error(d.error||'상태 조회 실패');
        if(d.online===false){items.forEach(({o})=>drawCustomLive(o,'offline'));return;}
        const entities=Array.isArray(d.entities)?d.entities:[];
        for(const {o,b} of items){
          if(o.userData.customType==='power_switch'){
            const e=entities.find(e=>e.entity_id===b.entity_id&&e.domain==='switch');
            if(e)customPowerRuntime.set(Number(o.userData.customId),{on:parseRelayOn(e.value),updatedAt:Date.now()});
            drawCustomLive(o,e?'value':'nodata',e?parseRelayOn(e.value):null);
          }else{
            const selected=entities.find(e=>e.entity_id===b.entity_id);
            // 같은 ESP32에 연결된 온도/습도 채널을 한 쌍으로 표시한다.
            const kind=e=>String(e.device_class||'').toLowerCase()+' '+String(e.name||'').toLowerCase()+' '+String(e.entity_id||'').toLowerCase();
            const temperature=entities.find(e=>e.domain==='sensor'&&/temperature|온도/.test(kind(e)));
            const humidity=entities.find(e=>e.domain==='sensor'&&/humidity|습도/.test(kind(e)));
            const numeric=e=>{if(!e)return null;const v=e.value;if(v===null||v===undefined||v==='')return null;const n=Number(v);return Number.isFinite(n)?n:null;};
            const t=numeric(temperature),h=numeric(humidity);
            // 지정한 엔티티가 센서가 아니면 무관한 장치 값을 표시하지 않는다.
            if(!selected||selected.domain!=='sensor'){drawCustomLive(o,'nodata');continue;}
            drawCustomLive(o,t!==null||h!==null?'value':'nodata',{temperature:t,humidity:h});
          }
        }
      }catch(err){console.warn('3D climate/power state sync failed',deviceId,err);items.forEach(({o})=>drawCustomLive(o,'nodata'));}
    }));
  }finally{customClimatePowerSyncBusy=false;paintExtra3dRelayButtons();}
}
// Existing farm3d MQTT WebSocket updates these labels immediately.
function applyCustomClimatePowerLiveState(deviceId,entities,online){
  if(farmDesignState?.design_mode!=='custom'||!farmDesignState?.design_completed)return;
  const bindings=readCustomHardwareBindings();
  const kind=e=>`${e.device_class||''} ${e.name||''} ${e.entity_id||''}`.toLowerCase();
  const numeric=e=>{if(!e||e.value===null||e.value===undefined||e.value==='')return null;const n=Number(e.value);return Number.isFinite(n)?n:null;};
  const temperature=entities.find(e=>e.domain==='sensor'&&/temperature|온도/.test(kind(e)));
  const humidity=entities.find(e=>e.domain==='sensor'&&/humidity|습도/.test(kind(e)));
  for(const type of ['climate_sensor','power_switch'])for(const o of customLiveObjects(type)){
    const b=bindings[o.userData.customId];
    if(Number(b?.device_id)!==deviceId||!b?.entity_id)continue;
    if(!online){drawCustomLive(o,'offline');continue;}
    const e=entities.find(x=>x.entity_id===b.entity_id);
    if(!e){drawCustomLive(o,'nodata');continue;}
    if(type==='power_switch'){
      if(e.domain!=='switch'){drawCustomLive(o,'nodata');continue;}
      const on=parseRelayOn(e.value);
      customPowerRuntime.set(Number(o.userData.customId),{on,updatedAt:Date.now()});
      drawCustomLive(o,'value',on);
    }else{
      if(e.domain!=='sensor'){drawCustomLive(o,'nodata');continue;}
      const t=numeric(temperature),h=numeric(humidity);
      drawCustomLive(o,t!==null||h!==null?'value':'nodata',{temperature:t,humidity:h});
    }
  }
}
const customPowerRuntime=new Map();
let customPowerCommandBusy=false;
function findOperationalPowerAt(event){
  const rect=renderer.domElement.getBoundingClientRect();
  pointer.x=((event.clientX-rect.left)/rect.width)*2-1;
  pointer.y=-((event.clientY-rect.top)/rect.height)*2+1;
  raycaster.setFromCamera(pointer,camera);
  const hits=raycaster.intersectObjects(customLiveObjects('power_switch'),true);
  for(const hit of hits){const owner=findCustomOwner(hit.object);if(owner?.userData?.customType==='power_switch')return owner;}
  return null;
}
async function toggleOperationalPower(o){
  if(customPowerCommandBusy)return;
  const b=readCustomHardwareBindings()[o.userData.customId];
  if(!b?.device_id||!b?.entity_id){drawCustomLive(o,'unlinked');alert('전원버튼에 실제 릴레이를 먼저 연결하세요.');return;}
  customPowerCommandBusy=true;
  try{
    let rt=customPowerRuntime.get(Number(o.userData.customId));
    if(!rt){
      const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${Number(b.device_id)}`,{credentials:'include',cache:'no-store'});
      const d=await r.json();if(!r.ok||!d.ok)throw new Error(d.error||'전원 상태 조회 실패');
      const e=(d.entities||[]).find(x=>x.entity_id===b.entity_id&&x.domain==='switch');
      if(!e)throw new Error('연결된 릴레이의 상태를 찾지 못했습니다.');
      rt={on:parseRelayOn(e.value)};
    }
    const desired=!rt.on;
    const r=await fetch(`${API_BASE}/entity-command`,{method:'POST',headers:{'Content-Type':'application/json'},credentials:'include',cache:'no-store',body:JSON.stringify({site_id:MQTT_SITE_ID,device_id:Number(b.device_id),entity_id:b.entity_id,state:desired?'on':'off'})});
    const d=await r.json().catch(()=>({}));if(!r.ok||d.ok===false)throw new Error(d.error||'전원 제어 실패');
    // The authoritative display is the MQTT state event; do not fake an ON/OFF confirmation.
    customDeviceStatus(`전원 ${desired?'ON':'OFF'} 명령 전송 · 실제 상태 수신 대기`);
    setTimeout(()=>syncCustomClimatePowerStates(),1800);
  }catch(e){alert(e.message||'전원 제어 실패');syncCustomClimatePowerStates();}
  finally{customPowerCommandBusy=false;}
}
function refreshValveUnlinkedLabels(){
  customHardwareBindings=readCustomHardwareBindings();
  for(const o of getCustomValveObjects()){const b=customHardwareBindings[o.userData.customId];if(!b?.device_id||!b?.entity_id){customValveRuntime.delete(Number(o.userData.customId));drawValveStatus(o,'unlinked');}}
}
async function syncCustomValveStates(){
  if(customValveSyncBusy||farmDesignState?.design_mode!=='custom'||!farmDesignState?.design_completed)return;
  customValveSyncBusy=true;
  try{
    customHardwareBindings=readCustomHardwareBindings();
    const valves=getCustomValveObjects();
    const byDevice=new Map();
    for(const o of valves){const b=customHardwareBindings[o.userData.customId];if(!b?.device_id||!b?.entity_id){drawValveStatus(o,'unlinked');continue;}const id=Number(b.device_id);if(!byDevice.has(id))byDevice.set(id,[]);byDevice.get(id).push({o,b});}
    await Promise.all([...byDevice.entries()].map(async([deviceId,items])=>{
      try{
        const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${deviceId}`,{credentials:'include',cache:'no-store'});const d=await r.json();if(!r.ok||!d.ok)throw new Error(d.error||'상태 조회 실패');
        if(d.online===false){
          for(const {o} of items){
            customValveRuntime.delete(Number(o.userData.customId));
            drawValveStatus(o,'offline');
          }
          return;
        }
        const ents=Array.isArray(d.entities)?d.entities:[];
        for(const {o,b} of items){const e=ents.find(x=>x.entity_id===b.entity_id);if(!e){drawValveStatus(o,'off');continue;}const on=parseRelayOn(e.value);customValveRuntime.set(Number(o.userData.customId),{on,value:e.value,updatedAt:Date.now()});drawValveStatus(o,on?'on':'off');}
      }catch(err){console.warn('custom valve state sync failed',deviceId,err);}
    }));
  }finally{customValveSyncBusy=false;}
}
function startCustomValvePolling(){
  if(customValvePollTimer)clearInterval(customValvePollTimer); refreshValveUnlinkedLabels(); refreshSoilUnlinkedLabels(); refreshCustomLiveLabels(); syncCustomValveStates(); syncCustomSoilStates(); syncCustomClimatePowerStates(); customValvePollTimer=setInterval(()=>{syncCustomValveStates();syncCustomSoilStates();syncCustomClimatePowerStates();},30000);
}
function stopCustomValvePolling(){if(customValvePollTimer){clearInterval(customValvePollTimer);customValvePollTimer=null;}}
function findOperationalValveAt(event){
  const rect=renderer.domElement.getBoundingClientRect();pointer.x=((event.clientX-rect.left)/rect.width)*2-1;pointer.y=-((event.clientY-rect.top)/rect.height)*2+1;raycaster.setFromCamera(pointer,camera);
  const valves=getCustomValveObjects();const hits=raycaster.intersectObjects(valves,true);if(!hits.length)return null;let p=hits[0].object;while(p&&p.parent&&p.parent!==customDesignRoot)p=p.parent;return p?.userData?.customType==='valve'?p:null;
}
async function commandOperationalValve(o,state){
  if(customValveCommandBusy)return;
  const b=readCustomHardwareBindings()[o.userData.customId];if(!b?.device_id||!b?.entity_id){drawValveStatus(o,'unlinked');alert('이 밸브는 실제 릴레이에 연결되지 않았습니다. 장치 연결에서 먼저 연결하세요.');return;}
  customValveCommandBusy=true;
  try{
    const commandState=state?'on':'off'; drawValveStatus(o,state?'on':'off');
    const r=await fetch(`${API_BASE}/entity-command`,{method:'POST',headers:{'Content-Type':'application/json'},credentials:'include',cache:'no-store',body:JSON.stringify({site_id:MQTT_SITE_ID,device_id:Number(b.device_id),entity_id:b.entity_id,state:commandState})});
    const d=await r.json();if(!r.ok||!d.ok)throw new Error(d.error||'릴레이 제어 실패');
    await new Promise(resolve=>setTimeout(resolve,450)); await syncCustomValveStates();
  }catch(e){console.error(e);alert(e.message||'릴레이 제어에 실패했습니다.');await syncCustomValveStates();}
  finally{customValveCommandBusy=false;}
}
async function toggleOperationalValve(o){
  let rt=customValveRuntime.get(Number(o.userData.customId));if(!rt){await syncCustomValveStates();rt=customValveRuntime.get(Number(o.userData.customId));}
  if(!rt){const b=readCustomHardwareBindings()[o.userData.customId];if(!b?.entity_id){drawValveStatus(o,'unlinked');alert('이 밸브는 실제 릴레이에 연결되지 않았습니다.');return;}}
  await commandOperationalValve(o,!(rt?.on===true));
}


function readCustomHardwareBindings(){
  try{return JSON.parse(localStorage.getItem(CUSTOM_HARDWARE_STORAGE_KEY)||'{}')||{};}catch(e){return {};}
}
function writeCustomHardwareBindings(){localStorage.setItem(CUSTOM_HARDWARE_STORAGE_KEY,JSON.stringify(customHardwareBindings));scheduleServerDesignSave();}
function customDeviceStatus(text){const el=document.getElementById('customDeviceStatus');if(el)el.textContent=text||'';}
function getCustomLabel(o){if(!o)return '선택 없음';const map=o.userData.customKind==='pipe'?CUSTOM_PIPE_LABELS:CUSTOM_FACILITY_LABELS;return `${map[o.userData.customType]||o.userData.customType} #${o.userData.customId}`;}
function setCustomDesignUiState(editing){
  document.body.classList.toggle('custom-design-editing',!!editing);
  document.body.classList.toggle('custom-design-completed',!editing);
  if(editing) document.body.classList.remove('custom-device-connect-mode');
}
function completeCustomDesign(){
  const d=saveCustomLayout();
  cancelCustomTool();
  transformControls.detach(); transformHelper.visible=false; customSelectedObject=null;
  farmDesignState.design_completed=true;
  farmDesignState.completed_at=new Date().toISOString();
  farmDesignState.updated_at=farmDesignState.completed_at;
  writeFarmDesignState(farmDesignState);
  setCustomDesignUiState(false);
  startCustomValvePolling();
  document.getElementById('systemStatus').textContent='설계 완료';
}
function reopenCustomDesign(){
  stopCustomValvePolling();
  setCustomDesignUiState(true);
  document.getElementById('systemStatus').textContent='설계 편집 중';
  customStatus('설계를 수정한 뒤 “설계 저장 · 완료”를 누르세요.');
}
async function loadCustomHardwareDevices(){
  customDeviceStatus('등록된 ESP32 정보를 불러오는 중...');
  const r=await fetch(`${API_BASE}/3d-bindings?site_id=${MQTT_SITE_ID}`,{credentials:'include',cache:'no-store'});
  const d=await r.json(); if(!r.ok||!d.ok)throw new Error(d.error||'ESP32 목록 조회 실패');
  const byId=new Map();
  [...(d.unassigned_devices||[]),...(d.bindings||[])].forEach(x=>{const id=Number(x.device_id);if(id)byId.set(id,{...x,device_id:id});});
  customHardwareDevices=[...byId.values()].sort((a,b)=>a.device_id-b.device_id);
  renderCustomHardwareDeviceSelect();
  customDeviceStatus(customHardwareDevices.length?`${customHardwareDevices.length}개 ESP32를 불러왔습니다.`:'등록된 ESP32가 없습니다.');
}
async function loadCustomPreviewCameras(){
  // CCTV만: 판매자가 이미 등록한 ONVIF/go2rtc 카메라 DB 목록을 사용한다.
  const r=await fetch(`/api/seller/smartfarm/camera-configs?site_id=${encodeURIComponent(MQTT_SITE_ID)}`,{credentials:'include',cache:'no-store'});
  const d=await r.json();
  if(!r.ok||!d.ok)throw new Error(d.error||'등록된 CCTV 목록 조회 실패');
  customPreviewCameras=(Array.isArray(d.cameras)?d.cameras:[])
    .filter(c=>String(c.is_active||'').toUpperCase()==='Y' && c.entity_id)
    .map(c=>({...c,domain:'camera',name:c.camera_name||c.entity_id,entity_name:c.camera_name||c.entity_id}));
  customHardwareEntities=customPreviewCameras;
  renderCustomHardwareEntitySelect();
  customDeviceStatus(customPreviewCameras.length?`${customPreviewCameras.length}개 등록된 CCTV를 불러왔습니다.`:'사용 가능한 등록 CCTV가 없습니다.');
}

function renderCustomHardwareDeviceSelect(){
  const el=document.getElementById('customHardwareDeviceSelect'); if(!el)return;
  const binding=customHardwareSelectedObject?customHardwareBindings[customHardwareSelectedObject.userData.customId]:null;
  el.innerHTML='<option value="">ESP32를 선택하세요</option>'+customHardwareDevices.map(d=>`<option value="${d.device_id}" ${Number(binding?.device_id)===Number(d.device_id)?'selected':''}>${escapeFarmText(d.device_name||`ESP32 #${d.device_id}`)} · Device ${d.device_id}${d.online?' · 온라인':' · 오프라인'}</option>`).join('');
}
async function loadCustomHardwareEntities(deviceId){
  customHardwareEntities=[]; const entitySection=document.getElementById('customEntitySection');
  if(!deviceId){if(entitySection)entitySection.style.display='none';renderCustomHardwareEntitySelect();return;}
  const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${Number(deviceId)}`,{credentials:'include',cache:'no-store'});
  const d=await r.json();if(!r.ok||!d.ok)throw new Error(d.error||'장치 Entity 조회 실패');
  customHardwareEntities=Array.isArray(d.entities)?d.entities:[];
  if(entitySection)entitySection.style.display=customHardwareSelectedObject?.userData.customType==='esp32'?'none':'block';
  renderCustomHardwareEntitySelect();
  customDeviceStatus(d.online?'ESP32 온라인 · Entity를 선택하세요.':'ESP32 오프라인 · 마지막 Entity 정보가 표시될 수 있습니다.');
}
function entityAllowedForSelected(e){
  const t=customHardwareSelectedObject?.userData.customType;
  if(t==='climate_sensor')return e.domain==='sensor';
  if(t==='power_switch')return e.domain==='switch';
  if(t==='soil_sensor')return e.domain==='sensor'&&(String(e.name||'').includes('토양')||String(e.name||'').toLowerCase().includes('soil')||String(e.device_class||'').toLowerCase().includes('moisture'));
  if(t==='valve'||t==='pump')return e.domain==='switch'||e.domain==='button';
  if(t==='cctv')return e.domain==='camera';
  return true;
}
function renderCustomHardwareEntitySelect(){
  const el=document.getElementById('customHardwareEntitySelect');if(!el)return;
  const binding=customHardwareSelectedObject?customHardwareBindings[customHardwareSelectedObject.userData.customId]:null;
  let arr=customHardwareEntities.filter(entityAllowedForSelected); if(!arr.length)arr=customHardwareEntities;
  el.innerHTML='<option value="">Entity를 선택하세요</option>'+arr.map(e=>`<option value="${escapeFarmText(e.entity_id||'')}" ${binding?.entity_id===e.entity_id?'selected':''}>${escapeFarmText(e.name||e.entity_id)} · ${escapeFarmText(e.domain||'')}</option>`).join('');
}
function renderCustomHardwareSelection(){
  const box=document.getElementById('customDeviceSelected');if(!box)return;
  const o=customHardwareSelectedObject; const b=o?customHardwareBindings[o.userData.customId]:null;
  if(!o){box.innerHTML='<strong>선택 없음</strong><span>ESP32·토양센서·밸브·펌프·CCTV 객체를 클릭하세요.</span>';return;}
  box.innerHTML=`<strong>${escapeFarmText(getCustomLabel(o))}</strong><span>${b?(o.userData.customType==='cctv'?`${escapeFarmText(b.entity_name||b.entity_id||'카메라')}`:`Device ${b.device_id}${b.entity_id?` · ${escapeFarmText(b.entity_name||b.entity_id)}`:''}`):'아직 실제 장치에 연결되지 않았습니다.'}</span>${b?'<span class="custom-binding-badge">연결됨</span>':''}`;
  renderCustomHardwareDeviceSelect();
  if(o.userData.customType==='nutrient_mixer'&&b){
    box.querySelector('span').textContent=String(b.entity_name||`양액 자동화 #${b.nutrient_id}`);
  }
  const relayVisible=['valve','pump','power_switch'].includes(o.userData.customType)&&!!b?.entity_id;
  document.getElementById('customRelayOn').style.display=relayVisible?'block':'none';
  document.getElementById('customRelayOff').style.display=relayVisible?'block':'none';
}

// 양액기 3D 객체 ↔ 기존 양액 자동화 연결 및 읽기 전용 상태 표시.
let nutrient3dRows=[];
let nutrient3dPollBusy=false;
function nutrient3dObjects(){return customDesignRoot.children.filter(o=>o.userData?.customType==='nutrient_mixer');}
function nutrient3dDraw(o,state){
  let sp=o.userData.nutrientStatusSprite;
  if(!sp){
    const canvas=document.createElement('canvas');canvas.width=640;canvas.height=150;
    const texture=new THREE.CanvasTexture(canvas);texture.colorSpace=THREE.SRGBColorSpace;
    sp=new THREE.Sprite(new THREE.SpriteMaterial({map:texture,depthTest:false,transparent:true}));
    sp.position.set(0,2.85,0);sp.scale.set(2.8,.66,1);sp.userData.nutrientCanvas=canvas;
    o.add(sp);o.userData.nutrientStatusSprite=sp;
  }
  const ctx=sp.userData.nutrientCanvas.getContext('2d');
  const colors={running:'#15803d',stopped:'#475569',unknown:'#b45309',unlinked:'#64748b'};
  const labels={running:'양액 자동화 실행 중',stopped:'양액 자동화 중지',unknown:'상태 확인 불가',unlinked:'양액 자동화 미연결'};
  ctx.clearRect(0,0,640,150);ctx.fillStyle=colors[state]||colors.unknown;
  ctx.beginPath();ctx.roundRect(8,8,624,134,20);ctx.fill();
  ctx.lineWidth=12;ctx.strokeStyle='#111827';ctx.stroke();
  ctx.font='bold 41px sans-serif';ctx.textAlign='center';ctx.textBaseline='middle';ctx.fillStyle='#fff';
  ctx.fillText(labels[state]||labels.unknown,320,75,608);
  sp.material.map.needsUpdate=true;
}
function nutrient3dPaint(){
  const bindings=readCustomHardwareBindings();
  for(const o of nutrient3dObjects()){
    const b=bindings[o.userData.customId];
    if(b?.object_type!=='nutrient_mixer'||!b.nutrient_id){nutrient3dDraw(o,'unlinked');continue;}
    const row=nutrient3dRows.find(x=>Number(x.id)===Number(b.nutrient_id));
    nutrient3dDraw(o,!row||row.status_verified!==true||typeof row.active!=='boolean'?'unknown':row.active?'running':'stopped');
  }
}
async function nutrient3dFetch(){
  if(nutrient3dPollBusy||!nutrient3dObjects().some(o=>readCustomHardwareBindings()[o.userData.customId]?.nutrient_id))return;
  nutrient3dPollBusy=true;
  try{
    const r=await fetch(`${AUTOMATION_API_BASE}/nutrient?site_id=${encodeURIComponent(MQTT_SITE_ID)}`,{credentials:'include',cache:'no-store'});
    const data=await r.json().catch(()=>({}));
    if(!r.ok||data.ok===false)throw new Error(data.error||'양액 자동화 조회 실패');
    nutrient3dRows=Array.isArray(data.nutrients)?data.nutrients:[];
  }catch(e){nutrient3dRows=[];console.warn('[3D nutrient status]',e);}
  finally{nutrient3dPollBusy=false;nutrient3dPaint();}
}
async function nutrient3dSelect(){
  const select=document.getElementById('customHardwareEntitySelect');
  if(!select)return;
  const b=customHardwareSelectedObject?readCustomHardwareBindings()[customHardwareSelectedObject.userData.customId]:null;
  const section=document.getElementById('customEntitySection');if(section)section.style.display='block';
  select.innerHTML='<option value="">양액 자동화를 선택하세요</option>';
  try{
    const r=await fetch(`${AUTOMATION_API_BASE}/nutrient?site_id=${encodeURIComponent(MQTT_SITE_ID)}`,{credentials:'include',cache:'no-store'});
    const data=await r.json().catch(()=>({}));
    if(!r.ok||data.ok===false)throw new Error(data.error||'목록 조회 실패');
    nutrient3dRows=Array.isArray(data.nutrients)?data.nutrients:[];
    select.innerHTML='<option value="">양액 자동화를 선택하세요</option>'+nutrient3dRows.map(a=>`<option value="${Number(a.id)}" ${Number(b?.nutrient_id)===Number(a.id)?'selected':''}>${escapeFarmText(a.name||`양액 자동화 #${a.id}`)}</option>`).join('');
    customDeviceStatus(nutrient3dRows.length?'양액 자동화를 선택한 후 연결 저장을 누르세요.':'등록된 양액 자동화가 없습니다.');
    nutrient3dPaint();
  }catch(e){customDeviceStatus(e.message);}
}
setInterval(()=>{nutrient3dPaint();nutrient3dFetch();},5000);

function selectCustomHardwareObject(o){
  if(!o||o.userData.customKind!=='facility'||!['esp32','soil_sensor','climate_sensor','valve','pump','power_switch','cctv','nutrient_mixer'].includes(o.userData.customType)){
    customHardwareSelectedObject=null;renderCustomHardwareSelection();customDeviceStatus('ESP32·토양센서·밸브·펌프·CCTV 중 하나를 선택하세요.');return;
  }
  customHardwareSelectedObject=o;renderCustomHardwareSelection();
  const b=customHardwareBindings[o.userData.customId];
  const deviceSelect=document.getElementById('customHardwareDeviceSelect');
  const entitySection=document.getElementById('customEntitySection');
  if(o.userData.customType==='nutrient_mixer'){
    if(deviceSelect)deviceSelect.style.display='none';
    nutrient3dSelect();
  }else if(o.userData.customType==='cctv'){
    // customHardwareDeviceSelect is a direct child of #customDevicePanel.
    // Hiding parentElement would therefore hide the whole connection panel.
    // CCTV does not need an ESP32 selector, so hide only the select itself.
    if(deviceSelect)deviceSelect.style.display='none';
    if(entitySection)entitySection.style.display='block';
    loadCustomPreviewCameras().catch(e=>customDeviceStatus(e.message));
  }else{
    if(deviceSelect)deviceSelect.style.display='';
    if(b?.device_id)loadCustomHardwareEntities(b.device_id).catch(e=>customDeviceStatus(e.message));
    else {if(entitySection)entitySection.style.display=o.userData.customType==='esp32'?'none':'block';renderCustomHardwareEntitySelect();customDeviceStatus(`${getCustomLabel(o)} 선택됨 · 실제 ESP32를 선택하세요.`);}
  }
}
function selectCustomHardwareAt(event){
  const rect=renderer.domElement.getBoundingClientRect();
  pointer.x=((event.clientX-rect.left)/rect.width)*2-1;
  pointer.y=-((event.clientY-rect.top)/rect.height)*2+1;
  raycaster.setFromCamera(pointer,camera);

  // 직접 설계 시설물의 모든 하위 Mesh를 대상으로 실제 클릭 객체를 찾는다.
  const hits=raycaster.intersectObjects(customDesignRoot.children,true);
  let owner=null;
  for(const h of hits){
    let p=h.object;
    while(p&&p.parent&&p.parent!==customDesignRoot)p=p.parent;
    if(p?.userData?.customDesignObject){owner=p;break;}
  }
  selectCustomHardwareObject(owner);
}
async function saveCustomHardwareBinding(){
  const o=customHardwareSelectedObject;if(!o){customDeviceStatus('먼저 3D 객체를 선택하세요.');return;}
  if(o.userData.customType==='nutrient_mixer'){
    const nutrientId=Number(document.getElementById('customHardwareEntitySelect').value);
    const nutrient=nutrient3dRows.find(a=>Number(a.id)===nutrientId);
    if(!nutrient){customDeviceStatus('양액 자동화를 선택하세요.');return;}
    customHardwareBindings[o.userData.customId]={custom_id:Number(o.userData.customId),object_type:'nutrient_mixer',device_id:null,entity_id:null,nutrient_id:nutrientId,entity_name:nutrient.name||`양액 자동화 #${nutrientId}`,updated_at:new Date().toISOString()};
    writeCustomHardwareBindings();renderCustomHardwareSelection();nutrient3dPaint();await nutrient3dFetch();
    customDeviceStatus(`${getCustomLabel(o)} · 양액 자동화 연결 저장`);return;
  }
  if(o.userData.customType==='cctv'){
    const entityId=document.getElementById('customHardwareEntitySelect').value;
    if(!entityId){customDeviceStatus('등록된 CCTV를 선택하세요.');return;}
    const ent=customPreviewCameras.find(e=>e.entity_id===entityId);
    if(!ent){customDeviceStatus('등록된 CCTV 목록에서 카메라를 다시 선택하세요.');return;}
    customHardwareBindings[o.userData.customId]={custom_id:Number(o.userData.customId),object_type:'cctv',device_id:null,entity_id:entityId,entity_name:ent?.entity_name||ent?.name||entityId,updated_at:new Date().toISOString()};
    writeCustomHardwareBindings();renderCustomHardwareSelection();customDeviceStatus(`${getCustomLabel(o)} 카메라 연결 저장 · ${entityId}`);return;
  }
  const deviceId=Number(document.getElementById('customHardwareDeviceSelect').value);if(!deviceId){customDeviceStatus('실제 ESP32를 선택하세요.');return;}
  const needsEntity=o.userData.customType!=='esp32'; const entityId=document.getElementById('customHardwareEntitySelect').value;
  if(needsEntity&&!entityId){customDeviceStatus('연결할 센서/릴레이 Entity를 선택하세요.');return;}
  const ent=customHardwareEntities.find(e=>e.entity_id===entityId);
  const deviceInfo=customHardwareDevices.find(d=>Number(d.device_id)===deviceId);
  // 기존 400평 바인딩에서 이미 검증된 실제 Entity가 있으면 그것을 우선 보존한다.
  // 신규/미배치 ESP32는 사용자가 선택한 Entity를 사용한다.
  const authoritativeEntityId=(o.userData.customType==='valve'||o.userData.customType==='pump')
    ? (deviceInfo?.relay_entity_id||entityId||null)
    : (o.userData.customType==='soil_sensor' ? (deviceInfo?.soil_entity_id||entityId||null) : (entityId||null));
  const authoritativeEntity=customHardwareEntities.find(e=>e.entity_id===authoritativeEntityId)||ent;
  customHardwareBindings[o.userData.customId]={
    custom_id:Number(o.userData.customId),
    object_type:o.userData.customType,
    device_id:deviceId,
    entity_id:authoritativeEntityId,
    entity_name:authoritativeEntity?.name||authoritativeEntityId||null,
    selected_entity_id:entityId||null,
    updated_at:new Date().toISOString()
  };
  writeCustomHardwareBindings();renderCustomHardwareSelection();refreshValveUnlinkedLabels();refreshSoilUnlinkedLabels();refreshCustomLiveLabels();syncCustomValveStates();syncCustomSoilStates();syncCustomClimatePowerStates();
  customDeviceStatus(`${getCustomLabel(o)} 실제 장치 연결 저장 · ${authoritativeEntityId||'ESP32'}`);
}
function unbindCustomHardware(){const o=customHardwareSelectedObject;if(!o)return;delete customHardwareBindings[o.userData.customId];if(o.userData.customType==='nutrient_mixer')nutrient3dDraw(o,'unlinked');writeCustomHardwareBindings();renderCustomHardwareSelection();refreshValveUnlinkedLabels();refreshSoilUnlinkedLabels();refreshCustomLiveLabels();syncCustomSoilStates();syncCustomClimatePowerStates();customDeviceStatus(`${getCustomLabel(o)} 연결을 해제했습니다.`);}

function getAuthoritativeRelayEntityId(deviceId,fallbackEntityId){
  const deviceInfo=customHardwareDevices.find(d=>Number(d.device_id)===Number(deviceId));
  return deviceInfo?.relay_entity_id||fallbackEntityId||null;
}

async function verifyCustomRelayState(deviceId,entityId,expectedState){
  const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${Number(deviceId)}`,{credentials:'include',cache:'no-store'});
  const d=await r.json();
  if(!r.ok||!d.ok)return {verified:false,reason:d.error||'상태 조회 실패'};
  const entities=Array.isArray(d.entities)?d.entities:[];
  const relay=entities.find(e=>e.entity_id===entityId);
  if(!relay)return {verified:false,reason:'명령한 릴레이 Entity를 상태 목록에서 찾지 못했습니다.'};
  const v=relay.value;
  const actual=typeof v==='boolean'?v:['on','true','1'].includes(String(v??'').trim().toLowerCase());
  return {verified:actual===expectedState,actual,value:v};
}

async function testCustomRelay(state){
  const o=customHardwareSelectedObject,b=o?customHardwareBindings[o.userData.customId]:null;
  if(!b?.device_id){customDeviceStatus('먼저 밸브/펌프의 실제 ESP32를 연결하세요.');return;}

  // 백엔드 /entity-command는 state에 boolean이 아니라 정확히 "on" / "off" 문자열을 요구한다.
  // 기존 400평 제어 함수와 동일한 형식으로 정규화한다.
  const commandState=String(state).trim().toLowerCase();
  if(commandState!=='on'&&commandState!=='off'){
    throw new Error('릴레이 state는 on 또는 off여야 합니다.');
  }
  const expectedOn=commandState==='on';

  // 기존 400평에서 사용 중인 /3d-bindings의 relay_entity_id를 최우선 사용한다.
  // 이것이 없을 때만 직접설계에 저장된 Entity를 fallback으로 사용한다.
  const relayEntityId=getAuthoritativeRelayEntityId(b.device_id,b.entity_id);
  if(!relayEntityId){customDeviceStatus('제어할 실제 릴레이 Entity를 찾을 수 없습니다.');return;}

  customDeviceStatus(`Device ${b.device_id} · ${relayEntityId} → ${expectedOn?'ON':'OFF'} 명령 전송 중...`);
  const r=await fetch(`${API_BASE}/entity-command`,{
    method:'POST',
    headers:{'Content-Type':'application/json'},
    credentials:'include',
    cache:'no-store',
    body:JSON.stringify({
      site_id:MQTT_SITE_ID,
      device_id:Number(b.device_id),
      entity_id:relayEntityId,
      state:commandState
    })
  });
  const d=await r.json();
  if(!r.ok||!d.ok)throw new Error(d.error||'릴레이 제어 실패');

  // MQTT 반영 시간을 조금 준 뒤 실제 센서 API에서 상태를 다시 확인한다.
  await new Promise(resolve=>setTimeout(resolve,450));
  const verified=await verifyCustomRelayState(b.device_id,relayEntityId,expectedOn);
  if(verified.verified){
    customDeviceStatus(`${getCustomLabel(o)} 실제 릴레이 ${expectedOn?'ON':'OFF'} 확인 완료`);
  }else if('actual' in verified){
    customDeviceStatus(`명령은 전송됐지만 실제 상태는 ${verified.actual?'ON':'OFF'}입니다. (${relayEntityId})`);
  }else{
    customDeviceStatus(`명령 전송 완료 · 상태 확인 실패: ${verified.reason}`);
  }
}
function findOperationalCctvAt(event){
  const rect=renderer.domElement.getBoundingClientRect();
  pointer.x=((event.clientX-rect.left)/rect.width)*2-1; pointer.y=-((event.clientY-rect.top)/rect.height)*2+1; raycaster.setFromCamera(pointer,camera);
  const hits=raycaster.intersectObjects(customDesignRoot.children,true);
  for(const h of hits){const owner=findCustomOwner(h.object);if(owner?.userData?.customType==='cctv')return owner;}
  return null;
}
let activeCctvBinding=null;
let sellerCctvRequestSerial=0;
async function refreshCctvPreview(){
  if(!activeCctvBinding?.entity_id)return;
  const entityId=String(activeCctvBinding.entity_id);
  const serial=++sellerCctvRequestSerial;
  const img=document.getElementById('cctvPreviewImage');
  const frame=document.getElementById('cctvPreviewFrame');
  const msg=document.getElementById('cctvPreviewMessage');
  if(!img||!frame)return;
  if(msg)msg.textContent='카메라 연결 중...';
  img.onload=null;
  img.onerror=null;
  img.removeAttribute('src');
  img.style.display='none';
  frame.onload=null;
  frame.removeAttribute('src');
  frame.style.display='none';
  try{
    const r=await fetch(
      `${PREVIEW_API_BASE}/camera-url?site_id=${encodeURIComponent(MQTT_SITE_ID)}&entity_id=${encodeURIComponent(entityId)}&v=${Date.now()}`,
      {credentials:'include',cache:'no-store'}
    );
    const d=await r.json().catch(()=>({}));
    if(!r.ok||!d.ok||!d.url)throw new Error(d.error||`카메라 URL 생성 실패 (HTTP ${r.status})`);
    if(serial!==sellerCctvRequestSerial||String(activeCctvBinding?.entity_id||'')!==entityId)return;
    if(d.provider==='go2rtc'){
      frame.style.display='block';
      frame.onload=()=>{if(serial===sellerCctvRequestSerial&&msg)msg.textContent='';};
      frame.src=d.url;
    }else{
      img.style.display='block';
      img.onload=()=>{if(serial===sellerCctvRequestSerial&&msg)msg.textContent='';};
      img.onerror=()=>{if(serial===sellerCctvRequestSerial&&msg)msg.textContent='카메라 영상을 불러오지 못했습니다.';};
      img.src=d.url;
    }
  }catch(error){
    console.error('[3D CCTV] 스트림 연결 실패:',error);
    if(serial===sellerCctvRequestSerial&&msg)msg.textContent=error?.message||'카메라 영상을 불러오지 못했습니다.';
  }
}
function openOperationalCctv(o){
  const b=readCustomHardwareBindings()[o.userData.customId];
  if(!b?.entity_id){alert('이 CCTV는 실제 카메라에 연결되지 않았습니다. 장치 연결에서 먼저 연결하세요.');return;}
  activeCctvBinding=b;
  document.getElementById('cctvPreviewTitle').textContent=b.entity_name||b.entity_id||'CCTV 실시간보기';
  document.getElementById('cctvPreviewModal').classList.add('open');
  document.body.style.overflow='hidden';
  refreshCctvPreview();
}
function closeCctvPreview(){
  ++sellerCctvRequestSerial;
  document.getElementById('cctvPreviewModal').classList.remove('open');
  const img=document.getElementById('cctvPreviewImage');
  const frame=document.getElementById('cctvPreviewFrame');
  if(img){img.onload=null;img.onerror=null;img.removeAttribute('src');img.style.display='none';}
  if(frame){frame.onload=null;frame.removeAttribute('src');frame.style.display='none';}
  activeCctvBinding=null;
  document.body.style.overflow='';
}
document.getElementById('cctvPreviewRefresh')?.addEventListener('click',refreshCctvPreview);
document.getElementById('cctvPreviewClose')?.addEventListener('click',closeCctvPreview);
document.getElementById('cctvPreviewModal')?.addEventListener('click',e=>{if(e.target.id==='cctvPreviewModal')closeCctvPreview();});
window.addEventListener('keydown',e=>{if(e.key==='Escape'&&document.getElementById('cctvPreviewModal')?.classList.contains('open'))closeCctvPreview();});

async function openCustomDevicePanel(){
  stopCustomValvePolling();
  if(!farmDesignState?.design_completed){customDeviceStatus('먼저 설계를 저장·완료하세요.');return;}
  document.body.classList.remove('custom-design-editing');document.body.classList.add('custom-design-completed','custom-device-connect-mode');
  customHardwareBindings=readCustomHardwareBindings();customHardwareSelectedObject=null;renderCustomHardwareSelection();
  try{await loadCustomHardwareDevices();}catch(e){console.error(e);customDeviceStatus(e.message||'ESP32 목록 조회 실패');}
}
function closeCustomDevicePanel(){document.body.classList.remove('custom-device-connect-mode');customHardwareSelectedObject=null;renderCustomHardwareSelection();startCustomValvePolling();}

document.getElementById('customDesignEditButton')?.addEventListener('click',reopenCustomDesign);
let userOperationMode=null;
function userOpEsc(v){return String(v??'').replace(/[&<>"']/g,m=>({'&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;'}[m]));}
function userOpPanel(){return document.getElementById('userOperationPanel');}
function closeUserOperationPanel(){userOpPanel()?.classList.remove('open');document.querySelectorAll('#customAutomationButton').forEach(b=>b.classList.remove('active'));userOperationMode=null;}
function focusUserOperationalObject(o){if(!o)return;const box=new THREE.Box3().setFromObject(o),center=box.getCenter(new THREE.Vector3()),size=box.getSize(new THREE.Vector3());const span=Math.max(size.x,size.y,size.z,2);controls.target.copy(center);camera.position.set(center.x+span*3,center.y+span*2.3+2,center.z+span*3);camera.lookAt(center);controls.update();}
function openUserOperationPanel(mode,title,buttonId){userOperationMode=mode;document.getElementById('userOperationTitle').textContent=title;userOpPanel()?.classList.add('open');document.querySelectorAll('#customAutomationButton').forEach(b=>b.classList.toggle('active',b.id===buttonId));}
function userOpObjectById(id,type){return customDesignRoot.children.find(o=>o.userData?.customDesignObject&&o.userData?.customType===type&&Number(o.userData?.customId)===Number(id));}


// MULTI AUTOMATION - ADDITIVE ONLY
// 기존 3D 관수/센서/CCTV/MQTT 로직은 그대로 두고,
// "관수 목록"이 열린 뒤 Mycodo 다중 자동관수 목록만 위에 추가한다.
// =========================================================
let user3dMultiAutomations = [];
let user3dMultiAutomationBusyId = null;
let user3dMultiPumpBusyId = null;

async function loadUser3dMultiAutomations() {
  const response = await fetch(
    `${AUTOMATION_API_BASE}/automations?site_id=${encodeURIComponent(MQTT_SITE_ID)}`,
    { credentials: 'include', cache: 'no-store' }
  );
  const data = await response.json();
  if (!response.ok || !data.ok) {
    throw new Error(data.error || '자동관수 목록을 불러오지 못했습니다.');
  }
  user3dMultiAutomations = Array.isArray(data.automations) ? data.automations : [];
  return user3dMultiAutomations;
}

function user3dAutomationStateText(value) {
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(1)} %` : '-';
}

function findUser3dAutomationObject(deviceId, entityId, preferredTypes = []) {
  const bindings = readCustomHardwareBindings();
  const wantedDeviceId = Number(deviceId);
  const wantedEntityId = String(entityId || '').trim();
  const types = Array.isArray(preferredTypes) ? preferredTypes : [preferredTypes];

  // 3D 바인딩은 실제 운용 entity_id와 사용자가 처음 선택한
  // selected_entity_id를 함께 보존한다. 자동관수 DB에는 둘 중 하나가
  // 들어갈 수 있으므로 두 값을 모두 비교해야 같은 3D 객체를 찾을 수 있다.
  let match = Object.entries(bindings).find(([, binding]) => {
    if (Number(binding?.device_id) !== wantedDeviceId) return false;
    const boundEntityId = String(binding?.entity_id || '').trim();
    const selectedEntityId = String(binding?.selected_entity_id || '').trim();
    return !wantedEntityId || boundEntityId === wantedEntityId || selectedEntityId === wantedEntityId;
  });

  // Entity 표기가 달라도 같은 ESP32에 해당 종류 객체가 하나뿐이면
  // device_id + 객체 종류로 안전하게 위치를 찾는다.
  if (!match) {
    const candidates = Object.entries(bindings).filter(([, binding]) =>
      Number(binding?.device_id) === wantedDeviceId
    );
    const typed = candidates.filter(([customId]) => {
      const o = customDesignRoot.children.find(item =>
        item.userData?.customDesignObject && Number(item.userData?.customId) === Number(customId)
      );
      return o && types.includes(o.userData?.customType);
    });
    if (typed.length === 1) match = typed[0];
  }

  if (!match) return null;
  const customId = Number(match[0]);
  const object = customDesignRoot.children.find(o =>
    o.userData?.customDesignObject && Number(o.userData?.customId) === customId
  ) || null;
  if (!object) return null;
  if (types.length && !types.includes(object.userData?.customType)) return null;
  return object;
}

// =========================================================
// USER OPERATION BUTTON STATE SYNC ONLY
// 3D 지도에서 밸브를 직접 조작했을 때 customValveRuntime의 실제 상태를
// 자동관수/수동관수 버튼 모양에 반영한다. 제어 경로는 건드리지 않는다.
// =========================================================
function getUser3dAutomationPumpRuntime(automation) {
  if (!automation) return null;
  const pumpObject = findUser3dAutomationObject(
    automation.pump_device_id,
    automation.pump_entity_id,
    ['valve', 'pump']
  );
  if (!pumpObject) return null;
  return customValveRuntime.get(Number(pumpObject.userData?.customId)) || null;
}

function syncUserOperationPumpButtons() {
  const list = document.getElementById('userOperationList');
  if (!list) return;

  // 기존 수동 관수 버튼: customValveRuntime의 실제 MQTT 상태만 표시한다.
  list.querySelectorAll('button[data-user-toggle-valve]').forEach(button => {
    const customId = Number(button.dataset.userToggleValve);
    const runtime = customValveRuntime.get(customId);
    if (!runtime || typeof runtime.on !== 'boolean') return;
    const on = runtime.on === true;
    button.classList.toggle('danger', on);
    button.classList.toggle('primary', !on);
    button.textContent = on ? '관수 OFF' : '관수 ON';
  });

  // 자동관수 펌프 버튼: 같은 3D 밸브에 연결된 실제 MQTT 상태를 표시한다.
  list.querySelectorAll('button[data-user-auto-pump]').forEach(button => {
    const automationId = Number(button.dataset.userAutoPump);
    const automation = user3dMultiAutomations.find(a => Number(a.id) === automationId);
    const runtime = getUser3dAutomationPumpRuntime(automation);
    if (!runtime || typeof runtime.on !== 'boolean') return;
    const on = runtime.on === true;
    button.classList.toggle('danger', on);
    button.classList.toggle('primary', !on);
    button.dataset.pumpState = on ? 'off' : 'on';
    if (Number(user3dMultiPumpBusyId) !== automationId) {
      button.textContent = on ? '펌프 OFF' : '펌프 ON';
    }
  });
}

function renderUser3dMultiAutomations() {
  const list = document.getElementById('userOperationList');
  if (!list || !['automation', 'valve'].includes(userOperationMode)) return;

  const old = document.getElementById('user3dMultiAutomationBlock');
  if (old) old.remove();

  const block = document.createElement('div');
  block.id = 'user3dMultiAutomationBlock';
  block.innerHTML = `
    <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 12px 6px;">
      <div style="font-weight:800;font-size:13px;opacity:.82;">자동관수</div>
      ${userOperationMode === 'automation' ? '<button type="button" data-user-auto-create style="border:1px solid rgba(56,189,248,.45);background:rgba(3,105,161,.28);color:#e0f2fe;border-radius:8px;padding:6px 9px;font-size:11px;font-weight:800;cursor:pointer;">+ 자동관수 추가</button>' : ''}
    </div>
    ${user3dMultiAutomations.length ? user3dMultiAutomations.map(a => {
      const id = Number(a.id);
      const active = !!a.active;
      const pumpRuntime = getUser3dAutomationPumpRuntime(a);
      const pumpOn = typeof pumpRuntime?.on === 'boolean'
        ? pumpRuntime.on
        : String(a.pump_state || '').toLowerCase() === 'on';
      const autoBusy = Number(user3dMultiAutomationBusyId) === id;
      const pumpBusy = Number(user3dMultiPumpBusyId) === id;
      const soilDeviceId = Number(a.soil_device_id || 0);
      const pumpDeviceId = Number(a.pump_device_id || 0);
      const soilEntityId = String(a.soil_entity_id || '');
      const pumpEntityId = String(a.pump_entity_id || '');
      return `<div class="user-op-item" data-user-automation-row="${id}">
        <div class="user-op-main">
          <div class="user-op-name">${userOpEsc(a.name || '자동관수')}</div>
          <div class="user-op-meta">토양수분 ${userOpEsc(user3dAutomationStateText(a.soil_value))} · 기준 ${userOpEsc(a.soil_threshold ?? '-')}% · 관수 ${userOpEsc(a.watering_seconds ?? '-')}초 · 대기 ${userOpEsc(a.watering_interval ?? '-')}초</div>
        </div>
        <div style="display:flex;align-items:center;gap:6px;justify-self:end;">
          <button type="button" data-user-auto-settings="${id}" aria-label="자동관수 설정" title="자동관수 설정" style="border:0;background:transparent;color:rgba(255,255,255,.72);padding:4px;line-height:1;cursor:pointer;font-size:17px;">⚙</button>
          <div class="user-op-state ${active ? 'on' : 'off'}">${active ? '실행 중' : '중지'}</div>
        </div>
        <div class="user-op-actions">
          <button data-user-auto-focus-soil="${id}" data-device-id="${soilDeviceId}" data-entity-id="${userOpEsc(soilEntityId)}" ${soilDeviceId && soilEntityId ? '' : 'disabled'}>센서 위치</button>
          <button data-user-auto-focus-pump="${id}" data-device-id="${pumpDeviceId}" data-entity-id="${userOpEsc(pumpEntityId)}" ${pumpDeviceId && pumpEntityId ? '' : 'disabled'}>밸브 위치</button>
          <button class="${active ? 'danger' : 'primary'}" data-user-auto-control="${id}" ${autoBusy ? 'disabled' : ''}>${autoBusy ? '처리 중...' : active ? '자동 중지' : '자동 실행'}</button>
          <button class="${pumpOn ? 'danger' : 'primary'}" data-user-auto-pump="${id}" data-pump-state="${pumpOn ? 'off' : 'on'}" ${pumpBusy ? 'disabled' : ''}>${pumpBusy ? '처리 중...' : pumpOn ? '펌프 OFF' : '펌프 ON'}</button>
        </div>
      </div>`;
    }).join('') : '<div class="user-op-empty">등록된 자동관수가 없습니다.</div>'}
    ${userOperationMode === 'valve' ? `
      <div style="height:1px;background:rgba(127,127,127,.22);margin:8px 12px;"></div>
      <div style="padding:2px 12px 6px;font-weight:800;font-size:13px;opacity:.82;">기존 3D 수동 관수</div>
    ` : ''}
  `;
  if (userOperationMode === 'automation') {
    // 자동관수 전용 화면에서는 로딩 문구를 실제 목록으로 완전히 교체한다.
    list.replaceChildren(block);
  } else {
    // 기존 관수 목록에서는 기존 MQTT 수동관수 위에 자동관수만 추가한다.
    list.prepend(block);
  }
  syncUserOperationPumpButtons();
}

async function refreshUser3dMultiAutomationBlock() {
  try {
    await loadUser3dMultiAutomations();
    renderUser3dMultiAutomations();
  } catch (error) {
    console.error('[3D MULTI AUTOMATION] 목록 조회 실패:', error);
    if (['automation', 'valve'].includes(userOperationMode)) {
      const list = document.getElementById('userOperationList');
      if (list) {
        const old = document.getElementById('user3dMultiAutomationBlock');
        if (old) old.remove();
        const block = document.createElement('div');
        block.id = 'user3dMultiAutomationBlock';
        block.innerHTML = `<div class="user-op-empty">${userOpEsc(error.message || '자동관수 목록 조회 실패')}</div>`;
        if (userOperationMode === 'automation') list.replaceChildren(block);
        else list.prepend(block);
      }
    }
  }
}

// 전체보기 버튼을 자동관수 전용 메뉴로 사용한다.
// 기존 관수 목록(showUserValveList)과 MQTT 수동 관수 로직은 그대로 둔다.
async function showUserAutomationList() {
  openUserOperationPanel('automation', '자동관수', 'customAutomationButton');
  const list = document.getElementById('userOperationList');
  if (list) list.innerHTML = '<div class="user-op-empty">자동관수 상태 확인 중...</div>';
  await refreshUser3dMultiAutomationBlock();
}

// 기존 전체보기의 카메라 refit 동작 대신 자동관수 목록을 연다.
// 기존 관수/센서/CCTV 버튼과 각 기능의 이벤트는 변경하지 않는다.
document.getElementById('customAutomationButton')?.addEventListener('click', () => showUserAutomationList().catch(console.error));
document.getElementById('userOperationClose')?.addEventListener('click', closeUserOperationPanel);

// =========================================================
// MULTI AUTOMATION SETTINGS
// 기존 자동관수 생성/제어 로직은 유지하고, 현재 자동관수의 조건만 PUT으로 수정한다.
// =========================================================
function closeUser3dAutomationSettings() {
  document.getElementById('user3dAutomationSettingsModal')?.remove();
}

async function openUser3dAutomationCreate() {
  closeUser3dAutomationSettings();

  let entities = { soils: [], pumps: [] };
  try {
    const response = await fetch(`${AUTOMATION_API_BASE}/automation-entities?site_id=${encodeURIComponent(MQTT_SITE_ID)}`, {
      method: 'GET', credentials: 'include', cache: 'no-store', headers: { 'Accept': 'application/json' }
    });
    const data = await response.json().catch(() => ({}));
    if (!response.ok || !data.ok) throw new Error(data.error || '자동관수 기기 목록을 불러오지 못했습니다.');
    entities = data.entities || data;
  } catch (error) {
    alert(error.message || '자동관수 기기 목록을 불러오지 못했습니다.');
    return;
  }

  const soils = Array.isArray(entities.soils) ? entities.soils : [];
  const pumps = Array.isArray(entities.pumps) ? entities.pumps : [];
  const usedSoils = new Set(user3dMultiAutomations.map(a => `${Number(a.soil_device_id)}::${String(a.soil_entity_id || '')}`));
  const usedPumps = new Set(user3dMultiAutomations.map(a => `${Number(a.pump_device_id)}::${String(a.pump_entity_id || '')}`));
  const availableSoils = soils.filter(x => !usedSoils.has(`${Number(x.device_id)}::${String(x.entity_id || '')}`));
  const availablePumps = pumps.filter(x => !usedPumps.has(`${Number(x.device_id)}::${String(x.entity_id || '')}`));

  const optionHtml = (items, emptyText) => items.length
    ? `<option value="">선택하세요</option>${items.map(x => `<option value="${Number(x.device_id)}::${userOpEsc(String(x.entity_id || ''))}">${userOpEsc(x.name || x.entity_name || x.friendly_name || x.entity_id || '기기')} · Device ${Number(x.device_id)}</option>`).join('')}`
    : `<option value="">${userOpEsc(emptyText)}</option>`;

  const modal = document.createElement('div');
  modal.id = 'user3dAutomationSettingsModal';
  modal.style.cssText = 'position:fixed;inset:0;z-index:6500;background:rgba(2,6,23,.62);display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML = `
    <div style="width:min(460px,100%);max-height:90vh;overflow:auto;background:#fff;color:#111827;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.35);padding:20px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;">
        <div><div style="font-size:18px;font-weight:900;">자동관수 추가</div><div style="font-size:12px;color:#64748b;margin-top:3px;">토양수분 센서와 관수 릴레이를 연결합니다.</div></div>
        <button type="button" data-auto-settings-close style="border:0;background:#f1f5f9;border-radius:50%;width:32px;height:32px;font-size:18px;cursor:pointer;">×</button>
      </div>
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">이름</label>
      <input data-auto-setting="name" maxlength="120" value="자동관수 ${user3dMultiAutomations.length + 1}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;">
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">토양수분 센서</label>
      <select data-auto-setting="soil" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;background:#fff;">${optionHtml(availableSoils, '사용 가능한 토양수분 센서가 없습니다.')}</select>
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">관수 릴레이</label>
      <select data-auto-setting="pump" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;background:#fff;">${optionHtml(availablePumps, '사용 가능한 관수 릴레이가 없습니다.')}</select>
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">토양수분 기준 (%)</label>
      <input data-auto-setting="soil_threshold" type="number" min="1" max="100" step="0.1" value="60" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;">
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">1회 관수시간 (초)</label>
      <input data-auto-setting="watering_seconds" type="number" min="1" max="3600" step="1" value="5" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;">
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">재관수 대기시간 (초)</label>
      <input data-auto-setting="watering_interval" type="number" min="10" max="86400" step="1" value="180" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:8px;">
      <div data-auto-settings-error style="min-height:18px;color:#dc2626;font-size:12px;margin:4px 0 10px;"></div>
      <div style="display:flex;gap:8px;justify-content:flex-end;">
        <button type="button" data-auto-settings-close style="border:1px solid #cbd5e1;background:#fff;border-radius:9px;padding:9px 14px;cursor:pointer;">취소</button>
        <button type="button" data-auto-create-save ${availableSoils.length && availablePumps.length ? '' : 'disabled'} style="border:0;background:#2563eb;color:#fff;border-radius:9px;padding:9px 14px;font-weight:800;cursor:pointer;">자동관수 생성</button>
      </div>
    </div>`;
  document.body.appendChild(modal);

  modal.addEventListener('click', async event => {
    if (event.target === modal || event.target.closest('[data-auto-settings-close]')) { closeUser3dAutomationSettings(); return; }
    const saveButton = event.target.closest('[data-auto-create-save]');
    if (!saveButton) return;
    const errorEl = modal.querySelector('[data-auto-settings-error]');
    const name = String(modal.querySelector('[data-auto-setting="name"]')?.value || '자동관수').trim() || '자동관수';
    const soilValue = String(modal.querySelector('[data-auto-setting="soil"]')?.value || '');
    const pumpValue = String(modal.querySelector('[data-auto-setting="pump"]')?.value || '');
    const [soilDeviceId, ...soilEntityParts] = soilValue.split('::');
    const [pumpDeviceId, ...pumpEntityParts] = pumpValue.split('::');
    const soilEntityId = soilEntityParts.join('::');
    const pumpEntityId = pumpEntityParts.join('::');
    const soilThreshold = Number(modal.querySelector('[data-auto-setting="soil_threshold"]')?.value);
    const wateringSeconds = Number(modal.querySelector('[data-auto-setting="watering_seconds"]')?.value);
    const wateringInterval = Number(modal.querySelector('[data-auto-setting="watering_interval"]')?.value);
    if (!soilDeviceId || !soilEntityId || !pumpDeviceId || !pumpEntityId) { errorEl.textContent='토양수분 센서와 관수 릴레이를 모두 선택하세요.'; return; }
    if (!Number.isFinite(soilThreshold) || soilThreshold < 1 || soilThreshold > 100) { errorEl.textContent='토양수분 기준은 1~100% 사이로 입력하세요.'; return; }
    if (!Number.isInteger(wateringSeconds) || wateringSeconds < 1 || wateringSeconds > 3600) { errorEl.textContent='1회 관수시간은 1~3600초 사이의 정수로 입력하세요.'; return; }
    if (!Number.isInteger(wateringInterval) || wateringInterval < 10 || wateringInterval > 86400) { errorEl.textContent='재관수 대기시간은 10~86400초 사이의 정수로 입력하세요.'; return; }
    saveButton.disabled=true; saveButton.textContent='생성 중...'; errorEl.textContent='';
    try {
      const response = await fetch(`${AUTOMATION_API_BASE}/automations`, {
        method:'POST', credentials:'include', cache:'no-store', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({site_id:MQTT_SITE_ID,name,soil_device_id:Number(soilDeviceId),soil_entity_id:soilEntityId,pump_device_id:Number(pumpDeviceId),pump_entity_id:pumpEntityId,soil_threshold:soilThreshold,watering_seconds:wateringSeconds,watering_interval:wateringInterval})
      });
      const data = await response.json().catch(()=>({}));
      if (!response.ok || !data.ok) throw new Error(data.error || '자동관수 생성에 실패했습니다.');
      closeUser3dAutomationSettings();
      await loadUser3dMultiAutomations();
      renderUser3dMultiAutomations();
    } catch(error) {
      errorEl.textContent=error.message || '자동관수 생성에 실패했습니다.';
      saveButton.disabled=false; saveButton.textContent='자동관수 생성';
    }
  });
}


// 3D 자체 등록창: 기존 자동관수 모달과 동일한 생성/저장 패턴.
// 2D 페이지 이동/iframe 없이 셀러 Preview API에 직접 등록한다.
async function openUser3dExtraAutomationCreate(kind) {
  closeUser3dAutomationSettings();
  const climate = kind === 'temperature' || kind === 'humidity';
  const title = kind === 'temperature' ? '자동온도' : kind === 'humidity' ? '자동습도' : '양액 자동화';
  const fetchJson = async (url, options = {}) => {
    const response = await fetch(url, {credentials:'include',cache:'no-store',...options});
    const data = await response.json().catch(()=>({}));
    if(!response.ok || data.ok === false) throw new Error(data.error || data.message || `${title} 조회/저장 실패`);
    return data;
  };
  let sensors=[], outputs=[];
  try {
    if(climate) {
      const data=await fetchJson(`${AUTOMATION_API_BASE}/climate-entities?site_id=${encodeURIComponent(MQTT_SITE_ID)}`);
      sensors=(data.sensors||[]).filter(x=>x.kind===kind);
      outputs=data.relays||[];
    } else {
      const data=await fetchJson(`${AUTOMATION_API_BASE}/nutrient/devices?site_id=${encodeURIComponent(MQTT_SITE_ID)}`);
      sensors=data.sensors||[];
      outputs=data.outputs||[];
    }
  }catch(e){alert(e.message);return;}
  const option=(items,placeholder)=>`<option value="">${placeholder}</option>`+items.map(x=>`<option value="${Number(x.device_id)}::${userOpEsc(x.entity_id)}">${userOpEsc(x.device_name||'장치')} / ${userOpEsc(x.entity_name||x.name||x.entity_id)}</option>`).join('');
  const field=(label,key,value,type='text')=>`<label style="display:block;font-size:12px;font-weight:800;margin:9px 0 5px;">${label}</label><input data-extra-field="${key}" type="${type}" value="${userOpEsc(value)}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;">`;
  const select=(label,key,items)=>`<label style="display:block;font-size:12px;font-weight:800;margin:9px 0 5px;">${label}</label><select data-extra-field="${key}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;background:white;">${option(items,'장치 선택')}</select>`;
  const numeric=(label,key,val)=>field(label,key,val,'number');
  const roles={a:'A액',b:'B액',c:'pH 조정',mix:'혼합',water:'원수',supply:'공급',aux:'예비'};
  const contents=climate
    ? field('이름','name',title)+select('센서','sensor',sensors)+select('릴레이','relay',outputs)
      +numeric(kind==='temperature'?'목표 온도 (°C)':'목표 습도 (%)','setpoint',kind==='temperature'?25:60)
      +numeric('히스테리시스','hysteresis',1)
      +`<label style="display:block;font-size:12px;font-weight:800;margin:9px 0 5px;">제어 방향</label><select data-extra-field="direction" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;"><option value="raise">${kind==='temperature'?'난방':'가습'}</option><option value="lower">${kind==='temperature'?'냉방':'제습'}</option></select>`
      +numeric('검사주기 (초)','update_period',5)
    : field('양액기 이름','name',title)
      +select('EC 센서','ec',sensors.filter(x=>x.kind==='ec'))
      +select('pH 센서','ph',sensors.filter(x=>x.kind==='ph'))
      +Object.entries(roles).map(([key,label])=>select(`${label} 릴레이`,key,outputs)).join('')
      +numeric('목표 EC','target_ec',1.5)+numeric('목표 pH','target_ph',6)
      +numeric('1회 투입 시간 (초)','dose_seconds',1)
      +numeric('혼합 시간 (초)','mix_seconds',60)
      +numeric('안정화 대기 (초)','settle_seconds',60)
      +numeric('제어 주기 (초)','cycle_interval',300);
  const modal=document.createElement('div');
  modal.id='user3dAutomationSettingsModal';
  modal.style.cssText='position:fixed;inset:0;z-index:6500;background:rgba(2,6,23,.65);display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML=`<div style="width:min(480px,100%);max-height:90dvh;overflow:auto;background:#fff;color:#111827;border-radius:16px;padding:20px;box-shadow:0 24px 70px rgba(0,0,0,.35);">
    <div style="display:flex;align-items:center;justify-content:space-between;"><strong style="font-size:18px;">${title} 추가</strong><button type="button" data-extra-close style="border:0;background:#f1f5f9;border-radius:8px;padding:7px 11px;cursor:pointer;">×</button></div>
    ${contents}<div data-extra-error style="color:#dc2626;font-size:12px;min-height:18px;margin:10px 0;"></div>
    <div style="display:flex;justify-content:flex-end;gap:8px;"><button type="button" data-extra-close style="padding:10px;border:1px solid #cbd5e1;border-radius:9px;background:#fff;">취소</button><button type="button" data-extra-save style="padding:10px;border:0;border-radius:9px;background:#2563eb;color:white;font-weight:800;">${title} 생성</button></div>
  </div>`;
  document.body.appendChild(modal);
  modal.addEventListener('click',async event=>{
    if(event.target===modal||event.target.closest('[data-extra-close]')){closeUser3dAutomationSettings();return;}
    const save=event.target.closest('[data-extra-save]');if(!save)return;
    const get=k=>String(modal.querySelector(`[data-extra-field="${k}"]`)?.value||'').trim();
    const parse=k=>{const [device_id,...rest]=get(k).split('::');return device_id&&rest.length?{device_id:Number(device_id),entity_id:rest.join('::')}:null;};
    const error=modal.querySelector('[data-extra-error]');
    let payload,url;
    if(climate){
      const sensor=parse('sensor'),relay=parse('relay');
      if(!sensor||!relay){error.textContent='센서와 릴레이를 모두 선택하세요.';return;}
      payload={site_id:MQTT_SITE_ID,kind,name:get('name')||title,
        sensor_device_id:sensor.device_id,sensor_entity_id:sensor.entity_id,
        relay_device_id:relay.device_id,relay_entity_id:relay.entity_id,
        setpoint:Number(get('setpoint')),hysteresis:Number(get('hysteresis')),
        direction:get('direction'),update_period:Number(get('update_period'))};
      url=`${AUTOMATION_API_BASE}/climate-automations`;
    }else{
      const ec=parse('ec'),ph=parse('ph'),out={};
      for(const role of Object.keys(roles)){const val=parse(role);if(val)out[role]=val;}
      if(!ec||!ph||['a','b','c','mix','water','supply'].some(role=>!out[role])){
        error.textContent='EC·pH 센서와 필요한 양액 릴레이를 선택하세요.';return;
      }
      payload={site_id:MQTT_SITE_ID,name:get('name')||title,
        auto_devices:{sensors:{ec,ph},outputs:out},
        target_ec:Number(get('target_ec')),target_ph:Number(get('target_ph')),
        dose_seconds:Number(get('dose_seconds')),mix_seconds:Number(get('mix_seconds')),
        settle_seconds:Number(get('settle_seconds')),cycle_interval:Number(get('cycle_interval'))};
      url=`${AUTOMATION_API_BASE}/nutrient`;
    }
    save.disabled=true;save.textContent='생성 중...';error.textContent='';
    try{
      await fetchJson(url,{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify(payload)});
      closeUser3dAutomationSettings();
      await showExtra3dAutomationList(kind);
    }catch(e){error.textContent=e.message;save.disabled=false;save.textContent=`${title} 생성`;}
  });
}

// 자동관수와 동일하게 상단 버튼은 목록을 열고, 톱니바퀴에서만 설정창을 연다.
// 2D ClimateAutomationPanel과 동일한 현장 MQTT WebSocket 상태 수신.
// API 목록은 릴레이의 실시간 ON/OFF를 보장하지 않으므로 이 값을 별도로 갱신한다.
// 자동온도/습도 전원 버튼: 실제 MQTT 상태만 표시하는 캐시.
const extra3dRelayLive = new Map();
// 멤버 3D와 동일하게 실제 3D 전원 객체의 런타임 상태만 버튼에 반영한다.
function extra3dPowerObject(row){
  return findUser3dAutomationObject(row.relay_device_id,row.relay_entity_id,['power_switch']);
}
function paintExtra3dRelayButtons(){
  if(userOperationMode!=='extra-automation'||extra3dKind==='nutrient')return;
  const list=document.getElementById('userOperationList');
  if(!list)return;
  for(const row of extra3dRows){
    const button=list.querySelector(`[data-extra-row="${Number(row.id)}"] [data-extra-relay]`);
    if(!button)continue;
    const object=extra3dPowerObject(row);
    const runtime=object?customPowerRuntime.get(Number(object.userData.customId)):null;
    const known=typeof runtime?.on==='boolean';
    const on=known&&runtime.on;
    button.textContent=known?(on?'전원 OFF':'전원 ON'):'상태 확인 중';
    button.classList.toggle('danger',on);
    button.classList.toggle('primary',!on);
    button.disabled=Boolean(row.active)||!object||!known||extra3dRelayBusy===Number(row.id);
  }
}
function acceptExtra3dRelayLive(deviceId,entities,online){
  // MQTT 이벤트는 applyCustomClimatePowerLiveState에서 customPowerRuntime에 먼저 반영된다.
  paintExtra3dRelayButtons();
}
let extra3dRelayBusy=null;
let extra3dClimateSockets=[];
let extra3dClimateRetry=[];
let extra3dClimateGeneration=0;
function stopExtra3dClimateSockets(){
  extra3dClimateGeneration++;
  extra3dClimateRetry.forEach(clearTimeout);
  extra3dClimateRetry=[];
  extra3dClimateSockets.forEach(ws=>{try{ws.close();}catch(_){}});
  extra3dClimateSockets=[];
}
function watchExtra3dClimateState(rows,kind){
  stopExtra3dClimateSockets();
  if(kind==='nutrient')return;
  const generation=extra3dClimateGeneration;
  const ids=[...new Set(rows.flatMap(r=>[Number(r.sensor_device_id),Number(r.relay_device_id)]).filter(x=>Number.isInteger(x)&&x>0))];
  const norm=v=>String(v??'').trim().toLowerCase();
  const match=(entities,id,name,domain)=>(entities||[]).find(e=>{
    if(norm(e.domain)!==domain&&!norm(e.entity_id).startsWith(`${domain}.`))return false;
    if(id&&[e.entity_id,e.object_id,e.unique_id].some(v=>norm(v)===norm(id)))return true;
    return Boolean(name&&norm(e.name||e.friendly_name)===norm(name));
  });
  ids.forEach(deviceId=>{
    const connect=()=>{
      if(generation!==extra3dClimateGeneration)return;
      const scheme=window.location.protocol==='https:'?'wss':'ws';
      const ws=new WebSocket(`${scheme}://${window.location.host}${AUTOMATION_API_BASE}/ws?site_id=${encodeURIComponent(MQTT_SITE_ID)}&device_id=${deviceId}`);
      extra3dClimateSockets.push(ws);
      ws.onmessage=event=>{
        if(generation!==extra3dClimateGeneration)return;
        let data;try{data=JSON.parse(event.data||'{}');}catch(_){return;}
        if(data?.type!=='state'||Number(data.site_id)!==Number(MQTT_SITE_ID)||Number(data.device_id)!==deviceId)return;
        const entities=Array.isArray(data.entities)?data.entities:[];
        acceptExtra3dRelayLive(deviceId,entities,data.online!==false);

      };
      ws.onclose=()=>{if(generation===extra3dClimateGeneration)extra3dClimateRetry.push(setTimeout(connect,2000));};
    };
    connect();
  });
}
let extra3dRows=[];
let extra3dKind='temperature';
async function showExtra3dAutomationList(kind){
  extra3dKind=kind;
  const label=kind==='temperature'?'자동온도':kind==='humidity'?'자동습도':'양액 자동화';
  openUserOperationPanel('extra-automation',label,kind==='temperature'?'customTemperatureButton':kind==='humidity'?'customHumidityButton':'customNutrientButton');
  const list=document.getElementById('userOperationList');
  list.innerHTML='<div class="user-op-empty">목록 불러오는 중...</div>';
  try{
    const url=kind==='nutrient'?`${AUTOMATION_API_BASE}/nutrient?site_id=${encodeURIComponent(MQTT_SITE_ID)}`:`${AUTOMATION_API_BASE}/climate-automations?site_id=${encodeURIComponent(MQTT_SITE_ID)}`;
    const response=await fetch(url,{credentials:'include',cache:'no-store'});
    const data=await response.json().catch(()=>({}));
    if(!response.ok||data.ok===false)throw new Error(data.error||'자동화 목록 조회 실패');
    extra3dRows=(kind==='nutrient'?data.nutrients:data.automations||[]);
    if(!Array.isArray(extra3dRows))extra3dRows=[];
    if(kind!=='nutrient')extra3dRows=extra3dRows.filter(row=>row.kind===kind);

    // 기존 3D MQTT 웹소켓 재사용 (중복 웹소켓 생성 없음).
    list.innerHTML=`<div style="display:flex;align-items:center;justify-content:space-between;gap:10px;padding:10px 12px;"><strong>${label}</strong><button type="button" data-extra-new style="border:1px solid #38bdf8;border-radius:8px;background:#075985;color:white;padding:7px 10px;cursor:pointer;">+ ${label} 추가</button></div>`+
      (extra3dRows.length?extra3dRows.map(row=>`<div class="user-op-item" data-extra-row="${Number(row.id)}"><div class="user-op-main"><div class="user-op-name">${userOpEsc(row.name||label)}</div><div class="user-op-meta">${kind==='nutrient'?`목표 EC ${userOpEsc(row.target_ec)} · pH ${userOpEsc(row.target_ph)}`:`목표 ${userOpEsc(row.setpoint)} ${kind==='temperature'?'°C':'%'} · 히스테리시스 ${userOpEsc(row.hysteresis)}`}</div></div><div style="display:flex;gap:6px;align-items:center;justify-self:end;"><button type="button" data-extra-settings="${Number(row.id)}" title="설정" style="background:transparent;border:0;color:rgba(255,255,255,.72);font-size:17px;cursor:pointer;">⚙</button><span class="user-op-state ${row.active?'on':'off'}">${row.active?'실행 중':'중지'}</span></div>${kind==='nutrient'?'':`<div class="user-op-actions"><button type="button" data-extra-focus-sensor="${Number(row.id)}" ${row.sensor_device_id&&row.sensor_entity_id?'':'disabled'}>센서 위치</button><button type="button" data-extra-focus-relay="${Number(row.id)}" ${row.relay_device_id&&row.relay_entity_id?'':'disabled'}>전원 위치</button><button type="button" class="${row.active?'danger':'primary'}" data-extra-control="${Number(row.id)}">${row.active?'자동 중지':'자동 실행'}</button><button type="button" class="primary" data-extra-relay="${Number(row.id)}" ${row.active?'disabled':''}>전원 ON</button></div>`}</div>`).join(''):'<div class="user-op-empty">등록된 자동화가 없습니다.</div>');
    paintExtra3dRelayButtons();
  }catch(e){list.innerHTML=`<div class="user-op-empty">${userOpEsc(e.message)}</div>`;}
}
async function openUser3dExtraAutomationSettings(kind,row){
  closeUser3dAutomationSettings();
  const nutrient=kind==='nutrient';
  const title=nutrient?'양액 자동화':kind==='temperature'?'자동온도':'자동습도';
  const fields=nutrient?[
    ['target_ec','목표 EC'],['target_ph','목표 pH'],['dose_seconds','투입 시간 (초)'],['mix_seconds','혼합 시간 (초)'],['settle_seconds','안정화 시간 (초)'],['cycle_interval','제어 주기 (초)']
  ]:[['name','이름'],['setpoint',kind==='temperature'?'목표 온도':'목표 습도'],['hysteresis','히스테리시스'],['update_period','검사 주기 (초)']];
  const modal=document.createElement('div');modal.id='user3dAutomationSettingsModal';
  modal.style.cssText='position:fixed;inset:0;z-index:6500;background:rgba(2,6,23,.65);display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML=`<div style="width:min(460px,100%);max-height:90dvh;overflow:auto;background:white;color:#111827;border-radius:16px;padding:20px;"><div style="display:flex;justify-content:space-between;align-items:center;"><strong>${title} 설정</strong><button data-extra-close type="button" aria-label="설정 닫기" style="width:32px;height:32px;display:inline-flex;align-items:center;justify-content:center;flex:0 0 32px;border:0;border-radius:9px;background:#f1f5f9;color:#475569;font-size:20px;font-weight:600;line-height:1;cursor:pointer;">×</button></div><div style="font-size:12px;color:#64748b;margin:5px 0 15px;">${userOpEsc(row.name)}</div>`+
    fields.map(([key,label])=>`<label style="display:block;margin:10px 0 4px;font-size:12px;font-weight:700;">${label}</label><input data-extra-edit="${key}" type="${key==='name'?'text':'number'}" step="any" value="${userOpEsc(row[key]??'')}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:8px;">`).join('')+
    `<p data-extra-error style="color:#dc2626;font-size:12px;"></p><div style="display:flex;gap:8px;justify-content:flex-end;margin-top:15px;"><button data-extra-delete type="button" style="margin-right:auto;border:1px solid #fecaca;background:#fff1f2;color:#dc2626;border-radius:8px;padding:9px 14px;font-size:13px;font-weight:700;cursor:pointer;">삭제</button><button data-extra-close type="button" style="border:1px solid #cbd5e1;background:#f8fafc;color:#334155;border-radius:8px;padding:9px 14px;font-size:13px;font-weight:700;cursor:pointer;">취소</button><button data-extra-save type="button" style="background:#2563eb;color:white;padding:9px;border-radius:8px;border:0;">설정 저장</button></div></div>`;
  document.body.appendChild(modal);
  modal.addEventListener('click',async e=>{
    if(e.target===modal||e.target.closest('[data-extra-close]')){closeUser3dAutomationSettings();return;}
    const save=e.target.closest('[data-extra-save]'),del=e.target.closest('[data-extra-delete]');if(!save&&!del)return;
    if(del&&!window.confirm(`${row.name} 자동화를 삭제할까요?`))return;
    const error=modal.querySelector('[data-extra-error]');const button=save||del;button.disabled=true;
    try{
      const payload={};
      if(save){fields.forEach(([key])=>{const val=modal.querySelector(`[data-extra-edit="${key}"]`).value;payload[key]=key==='name'?val:Number(val);});if(!nutrient){payload.site_id=Number(MQTT_SITE_ID);payload.kind=kind;}}
      const url=nutrient?`${AUTOMATION_API_BASE}/nutrient/${row.id}`:`${AUTOMATION_API_BASE}/climate-automations/${row.id}`;
      const response=await fetch(url,{method:del?'DELETE':'PUT',credentials:'include',headers:{'Content-Type':'application/json'},body:del?undefined:JSON.stringify(payload)});
      const data=await response.json().catch(()=>({}));if(!response.ok||data.ok===false)throw new Error(data.error||data.message||'저장 실패');
      closeUser3dAutomationSettings();await showExtra3dAutomationList(kind);
    }catch(err){error.textContent=err.message;button.disabled=false;}
  });
}
document.getElementById('customTemperatureButton')?.addEventListener('click',()=>showExtra3dAutomationList('temperature'));
document.getElementById('customHumidityButton')?.addEventListener('click',()=>showExtra3dAutomationList('humidity'));
document.getElementById('customNutrientButton')?.addEventListener('click',()=>showExtra3dAutomationList('nutrient'));

function openUser3dAutomationSettings(automation) {
  if (!automation?.id) return;
  closeUser3dAutomationSettings();

  const modal = document.createElement('div');
  modal.id = 'user3dAutomationSettingsModal';
  modal.style.cssText = 'position:fixed;inset:0;z-index:6500;background:rgba(2,6,23,.62);display:flex;align-items:center;justify-content:center;padding:16px;';
  modal.innerHTML = `
    <div style="width:min(460px,100%);max-height:90vh;overflow:auto;background:#fff;color:#111827;border-radius:16px;box-shadow:0 24px 70px rgba(0,0,0,.35);padding:20px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:16px;">
        <div><div style="font-size:18px;font-weight:900;">자동관수 설정</div><div style="font-size:12px;color:#64748b;margin-top:3px;">${userOpEsc(automation.name || '자동관수')}</div></div>
        <button type="button" data-auto-settings-close style="border:0;background:#f1f5f9;border-radius:50%;width:32px;height:32px;font-size:18px;cursor:pointer;">×</button>
      </div>
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">이름</label>
      <input data-auto-setting="name" maxlength="120" value="${userOpEsc(automation.name || '자동관수')}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;">
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">토양수분 기준 (%)</label>
      <input data-auto-setting="soil_threshold" type="number" min="1" max="100" step="0.1" value="${userOpEsc(automation.soil_threshold ?? 60)}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;">
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">1회 관수시간 (초)</label>
      <input data-auto-setting="watering_seconds" type="number" min="1" max="3600" step="1" value="${userOpEsc(automation.watering_seconds ?? 5)}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:13px;">
      <label style="display:block;font-size:12px;font-weight:800;margin-bottom:5px;">재관수 대기시간 (초)</label>
      <input data-auto-setting="watering_interval" type="number" min="10" max="86400" step="1" value="${userOpEsc(automation.watering_interval ?? 180)}" style="width:100%;padding:10px;border:1px solid #cbd5e1;border-radius:9px;margin-bottom:8px;">
      <div data-auto-settings-error style="min-height:18px;color:#dc2626;font-size:12px;margin:4px 0 10px;"></div>
      <div style="display:flex;gap:8px;align-items:center;justify-content:flex-end;">
        <button type="button" data-auto-settings-delete="${Number(automation.id)}" style="margin-right:auto;border:1px solid #dc2626;background:#fff;color:#dc2626;border-radius:9px;padding:9px 14px;font-weight:800;cursor:pointer;">삭제</button>
        <button type="button" data-auto-settings-close style="border:1px solid #cbd5e1;background:#fff;border-radius:9px;padding:9px 14px;cursor:pointer;">취소</button>
        <button type="button" data-auto-settings-save="${Number(automation.id)}" style="border:0;background:#2563eb;color:#fff;border-radius:9px;padding:9px 14px;font-weight:800;cursor:pointer;">설정 저장</button>
      </div>
    </div>`;
  document.body.appendChild(modal);
  modal.addEventListener('click', async event => {
    if (event.target === modal || event.target.closest('[data-auto-settings-close]')) {
      closeUser3dAutomationSettings();
      return;
    }
    const deleteButton = event.target.closest('[data-auto-settings-delete]');
    if (deleteButton) {
      const automationName = String(automation.name || '자동관수');
      if (!window.confirm(`“${automationName}”를 삭제하시겠습니까?\n실행 중이면 먼저 중지됩니다.`)) return;
      const errorEl = modal.querySelector('[data-auto-settings-error]');
      deleteButton.disabled = true;
      deleteButton.textContent = '삭제 중...';
      errorEl.textContent = '';
      try {
        const response = await fetch(`${AUTOMATION_API_BASE}/automations/${Number(automation.id)}`, {
          method:'DELETE', credentials:'include', cache:'no-store', headers:{'Accept':'application/json'}
        });
        const data = await response.json().catch(()=>({}));
        if (!response.ok || !data.ok) throw new Error(data.error || '자동관수 삭제에 실패했습니다.');
        closeUser3dAutomationSettings();
        await loadUser3dMultiAutomations();
        renderUser3dMultiAutomations();
      } catch(error) {
        errorEl.textContent = error.message || '자동관수 삭제에 실패했습니다.';
        deleteButton.disabled = false;
        deleteButton.textContent = '삭제';
      }
      return;
    }
    const saveButton = event.target.closest('[data-auto-settings-save]');
    if (!saveButton) return;
    const errorEl = modal.querySelector('[data-auto-settings-error]');
    const name = String(modal.querySelector('[data-auto-setting="name"]')?.value || '자동관수').trim() || '자동관수';
    const soilThreshold = Number(modal.querySelector('[data-auto-setting="soil_threshold"]')?.value);
    const wateringSeconds = Number(modal.querySelector('[data-auto-setting="watering_seconds"]')?.value);
    const wateringInterval = Number(modal.querySelector('[data-auto-setting="watering_interval"]')?.value);
    if (!Number.isFinite(soilThreshold) || soilThreshold < 1 || soilThreshold > 100) { errorEl.textContent='토양수분 기준은 1~100% 사이로 입력하세요.'; return; }
    if (!Number.isInteger(wateringSeconds) || wateringSeconds < 1 || wateringSeconds > 3600) { errorEl.textContent='1회 관수시간은 1~3600초 사이의 정수로 입력하세요.'; return; }
    if (!Number.isInteger(wateringInterval) || wateringInterval < 10 || wateringInterval > 86400) { errorEl.textContent='재관수 대기시간은 10~86400초 사이의 정수로 입력하세요.'; return; }
    saveButton.disabled = true; saveButton.textContent = '저장 중...'; errorEl.textContent='';
    try {
      const response = await fetch(`${AUTOMATION_API_BASE}/automations/${Number(automation.id)}`, {
        method:'PUT', credentials:'include', cache:'no-store', headers:{'Content-Type':'application/json'},
        body:JSON.stringify({name,soil_threshold:soilThreshold,watering_seconds:wateringSeconds,watering_interval:wateringInterval})
      });
      const data = await response.json().catch(()=>({}));
      if (!response.ok || !data.ok) throw new Error(data.error || '자동관수 설정 저장에 실패했습니다.');
      closeUser3dAutomationSettings();
      await loadUser3dMultiAutomations();
      renderUser3dMultiAutomations();
    } catch(error) {
      errorEl.textContent = error.message || '자동관수 설정 저장에 실패했습니다.';
      saveButton.disabled=false; saveButton.textContent='설정 저장';
    }
  });
}

document.getElementById('userOperationList')?.addEventListener('click', async event => {
  const btn = event.target.closest('button');
  if (!btn) return;

  // 자동온도/자동습도: 자동관수와 동일한 목록 내 4개 버튼.
  // 실제 제어는 2D ClimateAutomationPanel과 같은 API로만 처리한다.
  if (['extra-automation'].includes(userOperationMode) && extra3dKind !== 'nutrient') {
    const action=['extraFocusSensor','extraFocusRelay','extraControl','extraRelay'].find(key=>btn.dataset[key]!==undefined);
    if(action){
      const row=extra3dRows.find(x=>Number(x.id)===Number(btn.dataset[action]));
      if(!row)return;
      if(action==='extraFocusSensor'||action==='extraFocusRelay'){
        const sensor=action==='extraFocusSensor';
        const obj=findUser3dAutomationObject(sensor?row.sensor_device_id:row.relay_device_id,sensor?row.sensor_entity_id:row.relay_entity_id,sensor?['climate_sensor','soil_sensor']:['power_switch','valve','pump']);
        if(obj){closeUserOperationPanel();focusUserOperationalObject(obj);}
        else alert(`${sensor?'센서':'전원'}에 연결된 3D 장치 위치를 찾을 수 없습니다.`);
        return;
      }
      if(btn.disabled)return;
      const isRelay=action==='extraRelay';
      if(isRelay && row.active){alert('자동화를 중지한 후 전원을 수동 제어하세요.');return;}
      if(isRelay){
        const powerObject=extra3dPowerObject(row);
        if(!powerObject){alert('연결된 3D 전원 객체를 찾을 수 없습니다.');return;}
        if(extra3dRelayBusy!==null)return;
        extra3dRelayBusy=Number(row.id);
        paintExtra3dRelayButtons();
        try{
          await toggleOperationalPower(powerObject);
          // 실제 릴레이 상태는 MQTT/기존 보조 동기화가 반영한다.
          await syncCustomClimatePowerStates();
        }catch(error){alert(error.message||'전원 제어 실패');}
        finally{extra3dRelayBusy=null;paintExtra3dRelayButtons();}
      }else{
        btn.disabled=true;
        const oldText=btn.textContent;
        btn.textContent='처리 중...';
        try{
          const response=await fetch(`${AUTOMATION_API_BASE}/climate-automations/${Number(row.id)}/control`,{
            method:'POST',credentials:'include',cache:'no-store',
            headers:{'Content-Type':'application/json'},
            body:JSON.stringify({activate:!row.active})
          });
          const data=await response.json().catch(()=>({}));
          if(!response.ok||data.ok===false)throw new Error(data.error||data.message||'제어에 실패했습니다.');
          await showExtra3dAutomationList(extra3dKind);
        }catch(error){alert(error.message||'제어에 실패했습니다.');btn.disabled=false;btn.textContent=oldText;}
      }
      return;
    }
  }

  if(btn.hasAttribute('data-extra-new')){
    await openUser3dExtraAutomationCreate(extra3dKind);
    return;
  }
  if(btn.hasAttribute('data-extra-settings')){
    const row=extra3dRows.find(x=>Number(x.id)===Number(btn.dataset.extraSettings));
    if(!row)return;
    // 목록을 선택한 다음 설정 팝업. 실제 수정 API는 기존 2D와 동일.
    await openUser3dExtraAutomationSettings(extra3dKind,row);
    return;
  }

  if (btn.hasAttribute('data-user-auto-create')) {
    await openUser3dAutomationCreate();
    return;
  }

  if (btn.dataset.userAutoSettings) {
    const id = Number(btn.dataset.userAutoSettings);
    const automation = user3dMultiAutomations.find(a => Number(a.id) === id);
    if (automation) openUser3dAutomationSettings(automation);
    return;
  }

  if (btn.dataset.userAutoFocusSoil) {
    const o = findUser3dAutomationObject(btn.dataset.deviceId, btn.dataset.entityId, ['soil_sensor']);
    if (o) { closeUserOperationPanel(); focusUserOperationalObject(o); }
    else alert('자동관수 센서와 연결된 3D 센서 위치를 찾을 수 없습니다.');
    return;
  }

  if (btn.dataset.userAutoFocusPump) {
    const o = findUser3dAutomationObject(btn.dataset.deviceId, btn.dataset.entityId, ['valve', 'pump']);
    if (o) { closeUserOperationPanel(); focusUserOperationalObject(o); }
    else alert('자동관수 밸브와 연결된 3D 위치를 찾을 수 없습니다.');
    return;
  }

  if (btn.dataset.userAutoControl) {
    const id = Number(btn.dataset.userAutoControl);
    const automation = user3dMultiAutomations.find(a => Number(a.id) === id);
    if (!automation || user3dMultiAutomationBusyId) return;
    user3dMultiAutomationBusyId = id;
    renderUser3dMultiAutomations();
    try {
      const response = await fetch(`${AUTOMATION_API_BASE}/automations/${id}/control`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ activate: !automation.active })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || '자동관수 제어에 실패했습니다.');
      await loadUser3dMultiAutomations();
    } catch (error) {
      console.error('[3D MULTI AUTOMATION] 실행/중지 실패:', error);
      alert(error.message || '자동관수 제어에 실패했습니다.');
    } finally {
      user3dMultiAutomationBusyId = null;
      renderUser3dMultiAutomations();
    }
    return;
  }

  if (btn.dataset.userAutoPump) {
    const id = Number(btn.dataset.userAutoPump);
    const state = String(btn.dataset.pumpState || '').toLowerCase();
    if (!id || !['on', 'off'].includes(state) || user3dMultiPumpBusyId) return;
    user3dMultiPumpBusyId = id;
    renderUser3dMultiAutomations();
    try {
      const response = await fetch(`${AUTOMATION_API_BASE}/automations/${id}/pump`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({ state })
      });
      const data = await response.json();
      if (!response.ok || !data.ok) throw new Error(data.error || '물펌프 제어에 실패했습니다.');
      const target = user3dMultiAutomations.find(a => Number(a.id) === id);
      if (target) target.pump_state = state;
      await new Promise(resolve => setTimeout(resolve, 450));
      await syncCustomValveStates();
    } catch (error) {
      console.error('[3D MULTI AUTOMATION] 펌프 제어 실패:', error);
      alert(error.message || '물펌프 제어에 실패했습니다.');
    } finally {
      user3dMultiPumpBusyId = null;
      renderUser3dMultiAutomations();
    }
  }
});


document.getElementById('customDeviceConnectButton')?.addEventListener('click',openCustomDevicePanel);
document.getElementById('customDevicePanelClose')?.addEventListener('click',closeCustomDevicePanel);
document.getElementById('customHardwareDeviceSelect')?.addEventListener('change',e=>loadCustomHardwareEntities(Number(e.target.value)).catch(err=>customDeviceStatus(err.message)));
document.getElementById('customHardwareSave')?.addEventListener('click',()=>saveCustomHardwareBinding().catch(e=>customDeviceStatus(e.message)));
document.getElementById('customHardwareUnbind')?.addEventListener('click',unbindCustomHardware);
document.getElementById('customRelayOn')?.addEventListener('click',()=>testCustomRelay('on').catch(e=>customDeviceStatus(e.message)));
document.getElementById('customRelayOff')?.addEventListener('click',()=>testCustomRelay('off').catch(e=>customDeviceStatus(e.message)));
document.getElementById('customHardwareRefresh')?.addEventListener('click',async()=>{try{if(customHardwareSelectedObject?.userData?.customType==='nutrient_mixer'){await nutrient3dSelect();await nutrient3dFetch();}else if(customHardwareSelectedObject?.userData?.customType==='cctv'){await loadCustomPreviewCameras();}else{await loadCustomHardwareDevices();const d=Number(document.getElementById('customHardwareDeviceSelect').value);if(d)await loadCustomHardwareEntities(d);}}catch(e){customDeviceStatus(e.message)}});

function initCustomDesignerUI(){
  document.querySelectorAll('[data-facility]').forEach(b=>b.addEventListener('click',()=>activateCustomFacility(b.dataset.facility)));
  document.querySelectorAll('[data-pipe]').forEach(b=>b.addEventListener('click',()=>activateCustomPipe(b.dataset.pipe)));
  document.getElementById('customMoveButton')?.addEventListener('click',showCustomMove);
  document.getElementById('customRotateLeft')?.addEventListener('click',()=>rotateCustomSelected(-15));
  document.getElementById('customRotateRight')?.addEventListener('click',()=>rotateCustomSelected(15));
  document.getElementById('customDeleteButton')?.addEventListener('click',deleteCustomSelected);
  document.getElementById('customCancelTool')?.addEventListener('click',cancelCustomTool);
  document.getElementById('customSaveButton')?.addEventListener('click',completeCustomDesign);
}
initCustomDesignerUI();

renderer.domElement.addEventListener('pointerup',function(event){
  if(farmDesignState?.design_mode!=='custom'||transformDragging)return;
  const dx=Math.abs(event.clientX-pointerDownX),dy=Math.abs(event.clientY-pointerDownY);if(dx>5||dy>5)return;
  if(document.body.classList.contains('custom-device-connect-mode')){selectCustomHardwareAt(event);return;}
  if(farmDesignState?.design_completed&&!document.body.classList.contains('custom-design-editing')){const cctv=findOperationalCctvAt(event);if(cctv){openOperationalCctv(cctv);return;}const power=findOperationalPowerAt(event);if(power){toggleOperationalPower(power);return;}const valve=findOperationalValveAt(event);if(valve){toggleOperationalValve(valve);return;}return;}
  if(!document.body.classList.contains('custom-design-editing'))return;
  if(customActiveFacilityType){const p=getCustomGroundPoint(event);if(p)addCustomFacilityAt(customActiveFacilityType,p);return;}
  if(customActivePipeType){const p=getCustomGroundPoint(event);if(!p)return;if(!customPipeStart){customPipeStart={x:p.x,z:p.z};customStatus(`${CUSTOM_PIPE_LABELS[customActivePipeType]} 시작점 저장 · 끝점을 클릭하세요.`);}else{addCustomPipeBetween(customActivePipeType,customPipeStart,{x:p.x,z:p.z});customPipeStart=null;customStatus(`${CUSTOM_PIPE_LABELS[customActivePipeType]} 생성 완료 · 다음 시작점을 클릭하세요.`);}return;}
  selectCustomObjectAt(event);
});

// 기존 TransformControls 저장 이벤트는 selectedMovableObject만 처리하므로
// 직접 설계 객체 드래그가 끝났을 때 별도로 저장한다.
transformControls.addEventListener('dragging-changed',function(event){if(!event.value&&farmDesignState?.design_mode==='custom'&&customSelectedObject&&transformControls.object===customSelectedObject){saveCustomLayout();customStatus('시설물 위치 저장 완료');}});


function startCustomFarmDesign() {
  writeFarmDesignState({
    site_id: MQTT_SITE_ID,
    design_mode: 'custom',
    design_completed: false,
    template: null,
    facilities: [],
    pipes: [],
    zones: [],
    updated_at: new Date().toISOString()
  });

  closeFarmDesignStartModal();
  document.body.classList.add('custom-design-mode');
  setCustomDesignUiState(true);
  document.getElementById('systemStatus').textContent = '설계 준비';
  restoreCustomLayout();

  // GLB 없이 실제 농장 경계와 위성지도만 복원한다.
  restore3DFarmBoundary();
  initMainSatelliteMap();

  // 위치가 아직 없으면 사용자가 바로 농장 위치부터 지정할 수 있게 연다.
  const geo = readFarmGeo();
  if (!geo?.boundary?.length) {
    setTimeout(openFarmMapEditor, 150);
  }
}

function startStandard400Design() {
  writeFarmDesignState({
    site_id: MQTT_SITE_ID,
    design_mode: 'standard_400',
    design_completed: true,
    template: 'standard_400_v1',
    facilities: [],
    pipes: [],
    zones: Array.from({length: ZONE_COUNT}, (_, i) => i + 1),
    updated_at: new Date().toISOString()
  });

  closeFarmDesignStartModal();
  loadStandardFarmTemplate();
}

async function bootstrapFarmDesign() {
  document.getElementById('systemStatus').textContent = '로딩 중';
  await hydrateFarmDesignFromServer();
  farmDesignState = readFarmDesignState();

  document.getElementById('farmDesignCustomButton')
    ?.addEventListener('click', startCustomFarmDesign);
  document.getElementById('farmDesignTemplateButton')
    ?.addEventListener('click', startStandard400Design);

  if (!farmDesignState) {
    document.getElementById('systemStatus').textContent = '설계 필요';
    openFarmDesignStartModal();
    return;
  }

  updateFarmDesignBadge();

  if (farmDesignState.design_mode === 'custom') {
    document.body.classList.add('custom-design-mode');
    setCustomDesignUiState(!farmDesignState.design_completed);
    document.getElementById('systemStatus').textContent = farmDesignState.design_completed ? '설계 완료' : '설계 중';
    customHardwareBindings=readCustomHardwareBindings();
    restoreCustomLayout();
    restore3DFarmBoundary();
    initMainSatelliteMap();
    if (farmDesignState.design_completed) {
      await Promise.allSettled([
        syncCustomValveStates(),
        syncCustomSoilStates()
      ]);
      startCustomValvePolling();
    }
    return;
  }

  await loadStandardFarmTemplate();
}




/* =========================================================
   REAL MQTT STATE / CONTROL
   Site 46 / Device 36 -> ZONE 01
========================================================= */

async function loadMqttState() {

  const response =
    await fetch(
      `${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${MQTT_DEVICE_ID}`,
      {
        credentials: 'include',
        cache: 'no-store'
      }
    );

  const data =
    await response.json();

  if (
    !response.ok ||
    !data.ok
  ) {
    throw new Error(
      data.error ||
      'MQTT 상태 조회 실패'
    );
  }

  const entities =
    Array.isArray(data.entities)
      ? data.entities
      : [];

  const soil =
    entities.find(
      function(entity) {
        return (
          entity.domain === 'sensor' &&
          (
            String(entity.name || '').includes('토양') ||
            String(entity.name || '').toLowerCase().includes('soil')
          )
        );
      }
    );

  const relay =
    entities.find(
      function(entity) {
        return (
          entity.domain === 'switch' &&
          (
            String(entity.name || '').includes('릴레이') ||
            String(entity.name || '').toLowerCase().includes('relay')
          )
        );
      }
    );

  mqttRelayEntityId =
    relay && relay.entity_id
      ? relay.entity_id
      : null;

  const zone =
    zones[MQTT_ZONE];

  if (zone) {

    if (soil) {

      const soilValue =
        Number(
          soil.value
        );

      if (
        Number.isFinite(
          soilValue
        )
      ) {

        zone.soilMoisture =
          soilValue;

      }

    }

    if (relay) {

      const relayValue =
        relay.value;

      const relayOn =
        typeof relayValue === 'boolean'
          ? relayValue
          : (
              String(
                relayValue ?? ''
              )
                .trim()
                .toLowerCase() === 'on' ||

              String(
                relayValue ?? ''
              )
                .trim()
                .toLowerCase() === 'true' ||

              String(
                relayValue ?? ''
              )
                .trim() === '1'
            );

      setZoneState(
        MQTT_ZONE,
        relayOn
      );

    }

    if (
      selectedZone ===
      MQTT_ZONE
    ) {

      updateZonePanel(
        MQTT_ZONE
      );

    }

  }

  document.getElementById(
    'systemStatus'
  ).textContent =

    data.online
      ? `Site ${MQTT_SITE_ID} / Device ${MQTT_DEVICE_ID} 연결됨`
      : `Site ${MQTT_SITE_ID} / Device ${MQTT_DEVICE_ID} 오프라인`;

}


async function setMqttRelay(
  state
) {

  if (!mqttRelayEntityId) {

    await loadMqttState();

  }

  if (!mqttRelayEntityId) {

    throw new Error(
      '릴레이 MQTT 엔티티를 찾을 수 없습니다.'
    );

  }

  const response =
    await fetch(
      `${API_BASE}/entity-command`,
      {
        method: 'POST',

        headers: {
          'Content-Type':
            'application/json'
        },

        credentials:
          'include',

        cache:
          'no-store',

        body:
          JSON.stringify({
            site_id:
              MQTT_SITE_ID,

            device_id:
              MQTT_DEVICE_ID,

            entity_id:
              mqttRelayEntityId,

            state
          })
      }
    );

  const data =
    await response.json();

  if (
    !response.ok ||
    !data.ok
  ) {

    throw new Error(
      data.error ||
      '릴레이 제어 실패'
    );

  }

  /*
    명령 성공만으로 3D 상태를 바꾸지 않는다.
    실제 MQTT state를 다시 읽어서 표시한다.
  */

  await new Promise(
    function(resolve) {
      setTimeout(
        resolve,
        400
      );
    }
  );

  await loadMqttState();

  return data;

}


/* =========================================================
   SET ZONE VISUAL STATE

   이 함수는 "실제 상태 표시" 담당.

   나중에 MQTT 상태를 받으면:

   setZoneState(7, true)

   형태로 호출한다.
========================================================= */

function setZoneState(
  zoneNumber,
  isOn
) {


  const zone =
    zones[zoneNumber];


  if (!zone) {

    return;

  }



  /* =====================================================
     ON
  ===================================================== */

  if (isOn) {


    if (
      zone.valveMaterial
    ) {

      zone.valveMaterial
        .color
        .set(
          0x00ff44
        );

    }


    if (
      zone.drip1Material
    ) {

      zone.drip1Material
        .color
        .set(
          0x00aaff
        );

    }


    if (
      zone.drip2Material
    ) {

      zone.drip2Material
        .color
        .set(
          0x00aaff
        );

    }


    zone.state =
      true;

  }



  /* =====================================================
     OFF
  ===================================================== */

  else {


    if (

      zone.valveMaterial &&

      zone.valveOriginalColor

    ) {

      zone.valveMaterial
        .color
        .copy(
          zone.valveOriginalColor
        );

    }


    if (

      zone.drip1Material &&

      zone.drip1OriginalColor

    ) {

      zone.drip1Material
        .color
        .copy(
          zone.drip1OriginalColor
        );

    }


    if (

      zone.drip2Material &&

      zone.drip2OriginalColor

    ) {

      zone.drip2Material
        .color
        .copy(
          zone.drip2OriginalColor
        );

    }


    zone.state =
      false;

  }



  /*
   현재 선택 중인 구역이라면
   정보창도 즉시 갱신
  */

  if (
    selectedZone ===
    zoneNumber
  ) {

    updateZonePanel(
      zoneNumber
    );

  }

}



/* =========================================================
   UPDATE INFORMATION PANEL
========================================================= */

function updateZonePanel(
  zoneNumber
) {


  const zone =
    zones[zoneNumber];


  if (!zone) {

    return;

  }



  /* -------------------------------------------------------
     TITLE
  ------------------------------------------------------- */

  document.getElementById(
    'zoneTitle'
  ).textContent =

    `${zoneNumber}구역`;



  /* -------------------------------------------------------
     STATE
  ------------------------------------------------------- */

  const stateElement =
    document.getElementById(
      'zoneState'
    );


  if (
    zone.state
  ) {

    stateElement.textContent =
      '관수 중';


    stateElement.className =
      'on';

  }

  else {

    stateElement.textContent =
      '정지';


    stateElement.className =
      'off';

  }



  /* -------------------------------------------------------
     SOIL
  ------------------------------------------------------- */

  document.getElementById(
    'soilValue'
  ).textContent =

    zone.soilMoisture === null

      ? '-'

      : `${zone.soilMoisture.toFixed(1)} %`;



  /* -------------------------------------------------------
     LAST WATER
  ------------------------------------------------------- */

  document.getElementById(
    'lastWater'
  ).textContent =

    zone.lastWater === null

      ? '-'

      : zone.lastWater;



  /* -------------------------------------------------------
     ZONE NUMBER
  ------------------------------------------------------- */

  document.getElementById(
    'zoneNumberValue'
  ).textContent =

    `ZONE ${padZone(zoneNumber)}`;

}



/* =========================================================
   SELECT ZONE
========================================================= */

function selectZone(
  zoneNumber
) {


  if (
    !zones[zoneNumber]
  ) {

    return;

  }


  selectedZone =
    zoneNumber;


  updateZonePanel(
    zoneNumber
  );


  document.getElementById(
    'zonePanel'
  ).style.display =
    'block';


  const selectedBinding =
    farmBindings.find(
      function(item) {
        return Number(item.zone_number) === Number(zoneNumber);
      }
    );

  document.getElementById(
    'message'
  ).textContent =

    selectedBinding

      ? `Site ${MQTT_SITE_ID} / Device ${selectedBinding.device_id} 실제 MQTT 연결`

      : `${zoneNumber}구역에 배치된 ESP32가 없습니다.`;


  console.log(

    'ZONE SELECTED:',

    zoneNumber

  );

}



/* =========================================================
   CLOSE PANEL
========================================================= */

document.getElementById(
  'closePanel'
).addEventListener(

  'click',

  function() {


    document.getElementById(
      'zonePanel'
    ).style.display =
      'none';


    selectedZone =
      null;

  }

);



/* =========================================================
   SAVED BINDING -> REAL RELAY CONTROL
========================================================= */

async function setBoundZoneRelay(
  zoneNumber,
  state
) {

  if (!farmBindings.length) {
    await loadFarmBindings();
  }

  const binding =
    farmBindings.find(
      function(item) {
        return Number(item.zone_number) === Number(zoneNumber);
      }
    );

  if (!binding) {
    throw new Error(`${zoneNumber}구역에 배치된 ESP32가 없습니다.`);
  }

  if (!binding.relay_entity_id) {
    throw new Error(`${zoneNumber}구역의 릴레이 엔티티를 찾을 수 없습니다.`);
  }

  const response =
    await fetch(
      `${API_BASE}/entity-command`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        cache: 'no-store',
        body: JSON.stringify({
          site_id: MQTT_SITE_ID,
          device_id: Number(binding.device_id),
          entity_id: binding.relay_entity_id,
          state
        })
      }
    );

  const data =
    await response.json();

  if (!response.ok || !data.ok) {
    throw new Error(
      data.error ||
      `${zoneNumber}구역 릴레이 제어 실패`
    );
  }

  await new Promise(
    function(resolve) {
      setTimeout(resolve, 400);
    }
  );

  await loadBoundZoneStates();

  return data;
}


/* =========================================================
   REAL WATER START / STOP
   DB에 저장된 구역 ↔ ESP32 배치를 기준으로 제어
========================================================= */

document.getElementById(
  'startWater'
).addEventListener(
  'click',
  async function() {

    if (selectedZone === null) {
      return;
    }

    const message =
      document.getElementById('message');

    try {

      message.textContent =
        `${selectedZone}구역 관수 시작 명령 전송 중...`;

      await setBoundZoneRelay(
        selectedZone,
        'on'
      );

      message.textContent =
        `${selectedZone}구역 관수 시작 완료`;

    }

    catch (error) {

      console.error(
        '[3D MQTT] 관수 시작 실패:',
        error
      );

      message.textContent =
        error.message ||
        '관수 시작 실패';

    }

  }
);


document.getElementById(
  'stopWater'
).addEventListener(
  'click',
  async function() {

    if (selectedZone === null) {
      return;
    }

    const message =
      document.getElementById('message');

    try {

      message.textContent =
        `${selectedZone}구역 관수 정지 명령 전송 중...`;

      await setBoundZoneRelay(
        selectedZone,
        'off'
      );

      const now =
        new Date();

      zones[selectedZone].lastWater =
        now.toLocaleTimeString(
          'ko-KR',
          {
            hour: '2-digit',
            minute: '2-digit'
          }
        );

      updateZonePanel(
        selectedZone
      );

      message.textContent =
        `${selectedZone}구역 관수 정지 완료`;

    }

    catch (error) {

      console.error(
        '[3D MQTT] 관수 정지 실패:',
        error
      );

      message.textContent =
        error.message ||
        '관수 정지 실패';

    }

  }
);


/* =========================================================
   3D ESP32 POSITION EDITOR
========================================================= */
let farmEditMode=false;
let selectedPlacementDeviceId=null;
let farmBindings=[];
let farmUnassignedDevices=[];

function farmEditorStatus(text){
  const el=document.getElementById('farmEditorStatus');
  if(el) el.textContent=text||'';
}
function escapeFarmText(v){
  return String(v??'').replaceAll('&','&amp;').replaceAll('<','&lt;').replaceAll('>','&gt;').replaceAll('"','&quot;').replaceAll("'","&#039;");
}
async function loadFarmBindings(){
  const r=await fetch(`${API_BASE}/3d-bindings?site_id=${MQTT_SITE_ID}`,{credentials:'include',cache:'no-store'});
  const d=await r.json();
  if(!r.ok||!d.ok) throw new Error(d.error||'3D ESP32 배치 조회 실패');
  farmBindings=Array.isArray(d.bindings)?d.bindings:[];
  farmUnassignedDevices=Array.isArray(d.unassigned_devices)?d.unassigned_devices:[];
  renderFarmEditor();
  return d;
}
function renderFarmEditor(){
  const u=document.getElementById('farmUnassignedDevices');
  const b=document.getElementById('farmCurrentBindings');
  if(!u||!b)return;
  u.innerHTML=farmUnassignedDevices.length?farmUnassignedDevices.map(d=>{
    const sel=Number(d.device_id)===Number(selectedPlacementDeviceId);
    const name=d.device_name||`ESP32 #${d.device_id}`;
    return `<button type="button" class="farm-device-card${sel?' selected':''}" data-farm-device-id="${Number(d.device_id)}"><div class="farm-device-name">${escapeFarmText(name)}</div><div class="farm-device-meta">Device ${Number(d.device_id)} · ${d.online?'온라인':'오프라인'}</div></button>`;
  }).join(''):'<div class="farm-empty">미배치 ESP32가 없습니다.</div>';
  b.innerHTML=farmBindings.length?farmBindings.map(x=>{
    const name=x.device_name||`ESP32 #${x.device_id}`;
    return `<div class="farm-binding-card"><div class="farm-binding-row"><div><div class="farm-device-name">${Number(x.zone_number)}구역 · ${escapeFarmText(name)}</div><div class="farm-device-meta">Device ${Number(x.device_id)} · ${x.online?'온라인':'오프라인'}</div></div><button type="button" class="farm-binding-remove" data-farm-remove-zone="${Number(x.zone_number)}">배치 해제</button></div></div>`;
  }).join(''):'<div class="farm-empty">아직 배치된 ESP32가 없습니다.</div>';

  u.querySelectorAll('[data-farm-device-id]').forEach(btn=>btn.addEventListener('click',()=>{
    selectedPlacementDeviceId=Number(btn.dataset.farmDeviceId);
    renderFarmEditor();
    farmEditorStatus(`Device ${selectedPlacementDeviceId} 선택됨 · 배치할 관수 구역을 클릭하세요.`);
  }));
  b.querySelectorAll('[data-farm-remove-zone]').forEach(btn=>btn.addEventListener('click',async()=>{
    const z=Number(btn.dataset.farmRemoveZone);
    try{
      farmEditorStatus(`${z}구역 배치 해제 중...`);
      const r=await fetch(`${API_BASE}/3d-bindings/${z}?site_id=${MQTT_SITE_ID}`,{method:'DELETE',credentials:'include',cache:'no-store'});
      const d=await r.json();
      if(!r.ok||!d.ok)throw new Error(d.error||'배치 해제 실패');
      selectedPlacementDeviceId=null;
      await loadFarmBindings();
      farmEditorStatus(`${z}구역 배치를 해제했습니다.`);
    }catch(e){console.error(e);farmEditorStatus(e.message||'배치 해제 실패');}
  }));
}
async function saveFarmBinding(zoneNumber){
  if(!selectedPlacementDeviceId){farmEditorStatus('먼저 미배치 ESP32를 선택하세요.');return false;}
  const deviceId=selectedPlacementDeviceId;
  farmEditorStatus(`Device ${deviceId} → ${zoneNumber}구역 저장 중...`);
  const r=await fetch(`${API_BASE}/3d-bindings`,{
    method:'POST',headers:{'Content-Type':'application/json'},credentials:'include',cache:'no-store',
    body:JSON.stringify({site_id:MQTT_SITE_ID,zone_number:zoneNumber,device_id:deviceId})
  });
  const d=await r.json();
  if(!r.ok||!d.ok)throw new Error(d.error||'ESP32 배치 저장 실패');
  selectedPlacementDeviceId=null;
  await loadFarmBindings();
  farmEditorStatus(`Device ${deviceId}를 ${zoneNumber}구역에 배치했습니다.`);
  return true;
}
async function toggleFarmEditMode(){
  farmEditMode=!farmEditMode;
  document.body.classList.toggle('farm-edit-mode',farmEditMode);
  document.getElementById('farmEditor').classList.toggle('open',farmEditMode);
  document.getElementById('farmEditButton').textContent=farmEditMode?'편집 종료':'편집';
  selectedPlacementDeviceId=null;

  if (!farmEditMode) {

    transformControls.detach();
    transformHelper.visible =
      false;

    selectedMovableObject = null;

    renderMovableObjectList();
    updateObjectMoveControls();

  }

  if(farmEditMode){
    try{farmEditorStatus('ESP32 정보를 불러오는 중...');await loadFarmBindings();farmEditorStatus('미배치 ESP32를 선택한 뒤 관수 구역을 클릭하세요.');}
    catch(e){console.error(e);farmEditorStatus(e.message||'배치 조회 실패');}
  }
}
document.getElementById('farmEditButton').addEventListener('click',toggleFarmEditMode);

/* =========================================================
   POINTER DOWN
========================================================= */

renderer.domElement
  .addEventListener(

    'pointerdown',

    function(event) {


      pointerDownX =
        event.clientX;


      pointerDownY =
        event.clientY;

    }

  );



/* =========================================================
   POINTER UP

   이제 클릭한다고 ON/OFF하지 않는다.

   오직 "구역 선택"만 한다.
========================================================= */

renderer.domElement
  .addEventListener(

    'pointerup',

    async function(event) {


      if (transformDragging) {
        return;
      }

      // 직접 설계/장치 연결 모드의 클릭은 전용 pointerup 핸들러가 처리한다.
      // 기존 400평 Zone raycast가 같은 클릭을 다시 처리하지 않도록 차단한다.
      if (
        farmDesignState?.design_mode === 'custom' &&
        (
          document.body.classList.contains('custom-device-connect-mode') ||
          document.body.classList.contains('custom-design-editing')
        )
      ) {
        return;
      }


      /* ---------------------------------------------------
         DRAG CHECK
      --------------------------------------------------- */

      const moveX =
        Math.abs(

          event.clientX -

          pointerDownX

        );


      const moveY =
        Math.abs(

          event.clientY -

          pointerDownY

        );


      if (

        moveX > 5 ||

        moveY > 5

      ) {

        return;

      }



      /* ---------------------------------------------------
         SCREEN → THREE COORDINATE
      --------------------------------------------------- */

      const rect =

        renderer.domElement
          .getBoundingClientRect();



      pointer.x =

        (
          (
            event.clientX -

            rect.left
          )

          /

          rect.width

        ) * 2 - 1;



      pointer.y =

        -(

          (
            event.clientY -

            rect.top
          )

          /

          rect.height

        ) * 2 + 1;



      /* ---------------------------------------------------
         RAYCAST
      --------------------------------------------------- */

      raycaster.setFromCamera(

        pointer,

        camera

      );


      /* ---------------------------------------------------
         EDIT MODE: MOVE SELECTED FACILITY
         선택된 시설물이 있으면 구역 선택보다 먼저 위치 이동
      --------------------------------------------------- */

      if (
        farmEditMode &&
        selectedMovableObject
      ) {

        if (!objectMoveGround) {
          objectMoveGround =
            new THREE.Plane(
              new THREE.Vector3(0, 1, 0),
              -(farmRoot ? farmRoot.position.y : 0)
            );
        }

        const worldPoint =
          new THREE.Vector3();

        const hitGround =
          raycaster.ray.intersectPlane(
            objectMoveGround,
            worldPoint
          );

        if (hitGround) {

          const parent =
            selectedMovableObject.parent;

          const localPoint =
            parent
              ? parent.worldToLocal(
                  worldPoint.clone()
                )
              : worldPoint;

          selectedMovableObject.position.x =
            localPoint.x;

          selectedMovableObject.position.z =
            localPoint.z;

          saveObjectLayout();
          updateObjectMoveControls();

          farmEditorStatus(
            `${selectedMovableObject.name} 위치 저장 완료 · X ${selectedMovableObject.position.x.toFixed(2)} / Z ${selectedMovableObject.position.z.toFixed(2)}`
          );

          return;
        }
      }



      /*
        클릭 대상은 전체 scene이 아니라
        실제 16개 구역의 밸브/관수라인만 검사한다.

        바닥/작물/기타 GLB Mesh가 앞에서 raycast에 잡혀
        zoneNumber 탐색이 막히는 문제를 피한다.
      */

      const clickableObjects =
        [];


      for (

        let zoneNumber = 1;

        zoneNumber <= ZONE_COUNT;

        zoneNumber++

      ) {

        const zone =
          zones[zoneNumber];


        if (!zone) {

          continue;

        }


        if (zone.valve) {

          clickableObjects.push(
            zone.valve
          );

        }


        if (zone.drip1) {

          clickableObjects.push(
            zone.drip1
          );

        }


        if (zone.drip2) {

          clickableObjects.push(
            zone.drip2
          );

        }

      }


      const intersects =

        raycaster.intersectObjects(

          clickableObjects,

          true

        );



      if (
        !intersects.length
      ) {

        return;

      }



      /* ===================================================
         FIND ZONE
      =================================================== */

      for (
        const hit of intersects
      ) {


        let obj =
          hit.object;


        while (obj) {


          if (

            obj.userData &&

            obj.userData.zoneNumber

          ) {


            const zoneNumber =

              Number(

                obj.userData
                  .zoneNumber

              );


            /*
             핵심 변경:

             기존:
             setZone(...)

             현재:
             selectZone(...)
            */

            if (farmEditMode) {

              try {

                await saveFarmBinding(
                  zoneNumber
                );

              }

              catch (error) {

                console.error(
                  '[3D EDIT] 배치 저장 실패:',
                  error
                );

                farmEditorStatus(
                  error.message ||
                  'ESP32 배치 저장 실패'
                );

              }

              return;

            }


            selectZone(
              zoneNumber
            );


            return;

          }


          obj =
            obj.parent;

        }

      }

    }

  );



/* =========================================================
   DEBUG FUNCTIONS

   브라우저 콘솔에서 사용 가능:

   selectZone(7)

   setZoneState(7,true)

   setZoneState(7,false)
========================================================= */

window.selectZone =
  selectZone;


window.setZoneState =
  setZoneState;


window.zones =
  zones;



/* =========================================================
   RESIZE
   Unified resize handler is defined immediately before the render loop below.
========================================================= */


/* =========================================================
   RENDER LOOP
========================================================= */


function refitTrue3DView() {
  const saved = readFarmGeo();
  if (saved && Array.isArray(saved.boundary) && saved.boundary.length >= 3) {
    fitThreeCameraToRealFarm(saved.boundary);
  }
}

function syncThreeViewport({ refitDesktop = false } = {}) {
  const vv = window.visualViewport;

  // On mobile Safari/Chrome the visible viewport changes when browser chrome
  // expands/collapses.  Make the viewer, CSS3D and WebGL use the same height.
  if (vv && window.matchMedia('(max-width: 700px)').matches) {
    document.documentElement.style.setProperty(
      '--konggomul-vvh',
      `${Math.max(1, Math.round(vv.height))}px`
    );
  } else {
    document.documentElement.style.removeProperty('--konggomul-vvh');
  }

  const rect = container.getBoundingClientRect();
  const w = Math.max(1, Math.round(rect.width));
  const h = Math.max(1, Math.round(rect.height));

  renderer.setSize(w, h, false);
  cssRenderer.setSize(w, h);

  renderer.domElement.style.width = `${w}px`;
  renderer.domElement.style.height = `${h}px`;
  cssRenderer.domElement.style.width = `${w}px`;
  cssRenderer.domElement.style.height = `${h}px`;

  camera.aspect = w / h;
  camera.updateProjectionMatrix();

  if (
    refitDesktop &&
    !window.matchMedia('(max-width: 700px)').matches
  ) {
    setTimeout(refitTrue3DView, 80);
  }
}

window.addEventListener('resize', function() {
  syncThreeViewport({ refitDesktop: true });
}, { passive: true });

window.addEventListener('orientationchange', function() {
  setTimeout(() => syncThreeViewport({ refitDesktop: false }), 120);
}, { passive: true });

if (window.visualViewport) {
  window.visualViewport.addEventListener('resize', function() {
    syncThreeViewport({ refitDesktop: false });
  }, { passive: true });

  // iOS/Android browser bars can change visual viewport offset without a
  // normal window resize. Keep both renderer layers locked together.
  window.visualViewport.addEventListener('scroll', function() {
    syncThreeViewport({ refitDesktop: false });
  }, { passive: true });
}

syncThreeViewport({ refitDesktop: false });

function animate() {


  requestAnimationFrame(
    animate
  );


  enforceFarmCameraSafety();
  controls.update();

  cssRenderer.render(
    scene,
    camera
  );

  renderer.render(
    scene,
    camera
  );

}


animate();



/* =========================================================
   SAVED 3D BINDINGS -> LIVE MQTT STATE
========================================================= */
async function loadBoundZoneStates(){
  let d;
  try{
    const r=await fetch(`${API_BASE}/3d-bindings?site_id=${MQTT_SITE_ID}`,{credentials:'include',cache:'no-store'});
    d=await r.json();
    if(!r.ok||!d.ok)return;
  }catch(e){return;}
  const bindings=Array.isArray(d.bindings)?d.bindings:[];
  for(const binding of bindings){
    const z=Number(binding.zone_number), deviceId=Number(binding.device_id);
    if(!zones[z]||!deviceId)continue;
    try{
      const r=await fetch(`${API_BASE}/sensors?site_id=${MQTT_SITE_ID}&device_id=${deviceId}`,{credentials:'include',cache:'no-store'});
      const state=await r.json();
      if(!r.ok||!state.ok)continue;
      const entities=Array.isArray(state.entities)?state.entities:[];
      const soil=entities.find(e=>e.entity_id===binding.soil_entity_id)||entities.find(e=>e.domain==='sensor'&&(String(e.name||'').includes('토양')||String(e.name||'').toLowerCase().includes('soil')));
      const relay=entities.find(e=>e.entity_id===binding.relay_entity_id)||entities.find(e=>e.domain==='switch'&&(String(e.name||'').includes('릴레이')||String(e.name||'').toLowerCase().includes('relay')||String(e.name||'').includes('관수')));
      if(soil){const v=Number(soil.value);if(Number.isFinite(v))zones[z].soilMoisture=v;}
      if(relay){const v=relay.value;const on=typeof v==='boolean'?v:['on','true','1'].includes(String(v??'').trim().toLowerCase());setZoneState(z,on);}
      if(selectedZone===z)updateZonePanel(z);
    }catch(e){console.error(`[3D MQTT] ${z}구역 상태 조회 실패:`,e);}
  }
}
// ============================================================
// 3D LIVE MQTT WEBSOCKET (existing REST polling remains fallback)
// Each socket is scoped to one authorized site/device on the server.
// ============================================================
const farm3dLiveSockets = new Map();
let farm3dLiveStopped = false;
const FARM3D_WS_BASE = '/api/seller/smartfarm/esp32/ws';

// 자동온도/자동습도 목록의 전원 버튼 표시만 기존 3D MQTT 상태와 동기화한다.
// 전원 제어 API, 객체 연결, 위치 버튼, 자동화 상태는 변경하지 않는다.
function syncExtra3dRelayButtonFromLive(deviceId, entities, online) {
  if (!online || !Array.isArray(entities) || !extra3dRows.length) return;
  const bindings = Object.values(readCustomHardwareBindings() || {})
    .filter(b => b?.object_type === 'power_switch' && Number(b.device_id) === Number(deviceId));
  if (!bindings.length) return;
  const switches = entities.filter(e => e.domain === 'switch');
  const normalize = value => String(value ?? '').trim().toLowerCase();
  for (const row of extra3dRows) {
    // 제어 버튼과 동일한 연결 규칙: 장치 번호 또는 릴레이 엔티티로 실제 전원 객체 특정.
    const matching = bindings.filter(b => Number(row.relay_device_id) === Number(b.device_id) ||
      [b.entity_id, b.selected_entity_id].some(id => id && id === row.relay_entity_id));
    const physical = matching.length === 1 ? matching[0] :
      (bindings.length === 1 && Number(row.relay_device_id) === Number(deviceId) ? bindings[0] : null);
    if (!physical) continue;
    const relay = switches.find(e => e.entity_id === physical.entity_id || e.entity_id === physical.selected_entity_id);
    if (!relay) continue;
    const value = normalize(relay.value ?? relay.state);
    const state = ['on', 'true', '1'].includes(value) ? 'on' :
      ['off', 'false', '0'].includes(value) ? 'off' : null;
    if (!state) continue;
    row.relay_state = state;
    if (userOperationMode !== 'extra-automation' || extra3dKind === 'nutrient') continue;
    const button = document.getElementById('userOperationList')
      ?.querySelector(`[data-extra-row="${Number(row.id)}"] [data-extra-relay]`);
    if (!button) continue;
    button.textContent = state === 'on' ? '전원 OFF' : '전원 ON';
    button.classList.toggle('danger', state === 'on');
    button.classList.toggle('primary', state !== 'on');
    button.disabled = Boolean(row.active);
  }
}

function farm3dApplyLiveState(message) {
  if (farm3dLiveStopped || !message || message.type !== 'state' ||
      Number(message.site_id) !== MQTT_SITE_ID) return;
  const deviceId = Number(message.device_id);
  if (!Number.isInteger(deviceId) || deviceId <= 0) return;
  const entities = Array.isArray(message.entities) ? message.entities : [];
  const online = message.online !== false;
  const soilFallback = entities.find(e => e.domain === 'sensor' &&
    (String(e.name || '').includes('토양') || String(e.name || '').toLowerCase().includes('soil')));
  const relayFallback = entities.find(e => e.domain === 'switch' &&
    (String(e.name || '').includes('릴레이') || String(e.name || '').toLowerCase().includes('relay') || String(e.name || '').includes('관수')));

  if (farmDesignState?.design_mode !== 'custom') {
    for (const binding of farmBindings) {
      if (Number(binding.device_id) !== deviceId) continue;
      const zoneNumber = Number(binding.zone_number);
      if (!zones[zoneNumber]) continue;
      const soil = entities.find(e => e.entity_id === binding.soil_entity_id) || soilFallback;
      const relay = entities.find(e => e.entity_id === binding.relay_entity_id) || relayFallback;
      if (online && soil) {
        const value = Number(soil.value);
        if (Number.isFinite(value)) zones[zoneNumber].soilMoisture = value;
      }
      if (online && relay) setZoneState(zoneNumber, parseRelayOn(relay.value));
      if (selectedZone === zoneNumber) updateZonePanel(zoneNumber);
    }
    // Preserve the original fixed test-zone behavior, only for its own device.
    if (deviceId === MQTT_DEVICE_ID && zones[MQTT_ZONE]) {
      if (online && soilFallback && Number.isFinite(Number(soilFallback.value)))
        zones[MQTT_ZONE].soilMoisture = Number(soilFallback.value);
      if (online && relayFallback) {
        mqttRelayEntityId = relayFallback.entity_id || mqttRelayEntityId;
        setZoneState(MQTT_ZONE, parseRelayOn(relayFallback.value));
      }
      if (selectedZone === MQTT_ZONE) updateZonePanel(MQTT_ZONE);
    }
  }

  if (farmDesignState?.design_mode === 'custom' && farmDesignState?.design_completed) {
    customHardwareBindings = readCustomHardwareBindings();
    for (const valve of getCustomValveObjects()) {
      const binding = customHardwareBindings[valve.userData.customId];
      if (Number(binding?.device_id) !== deviceId || !binding?.entity_id) continue;
      const entity = entities.find(e => e.entity_id === binding.entity_id);
      if (!online) {
        customValveRuntime.delete(Number(valve.userData.customId));
        drawValveStatus(valve, 'offline');
      } else if (entity) {
        const on = parseRelayOn(entity.value);
        customValveRuntime.set(Number(valve.userData.customId), {on, value: entity.value, updatedAt: Date.now()});
        drawValveStatus(valve, on ? 'on' : 'off');
      }
    }
    applyCustomClimatePowerLiveState(deviceId,entities,online);

    acceptExtra3dRelayLive(deviceId,entities,online);
    for (const soilObject of getCustomSoilObjects()) {
      const binding = customHardwareBindings[soilObject.userData.customId];
      if (Number(binding?.device_id) !== deviceId || !binding?.entity_id) continue;
      const entity = entities.find(e => e.entity_id === binding.entity_id);
      if (!online) {
        customSoilRuntime.delete(Number(soilObject.userData.customId));
        drawSoilStatus(soilObject, 'offline');
      } else if (entity) {
        const value = normalizeSoilValue(entity);
        if (value === null) drawSoilStatus(soilObject, 'nodata');
        else {
          customSoilRuntime.set(Number(soilObject.userData.customId), {value, updatedAt: Date.now(), entity});
          drawSoilStatus(soilObject, 'value', value);
        }
      }
    }
  }
}

function farm3dOpenLiveSocket(deviceId) {
  if (farm3dLiveStopped || farm3dLiveSockets.has(deviceId)) return;
  const entry = {socket: null, retryTimer: null, retryMs: 1000};
  farm3dLiveSockets.set(deviceId, entry);
  const connect = () => {
    if (farm3dLiveStopped || farm3dLiveSockets.get(deviceId) !== entry) return;
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    const url = `${protocol}//${window.location.host}${FARM3D_WS_BASE}?site_id=${encodeURIComponent(MQTT_SITE_ID)}&device_id=${encodeURIComponent(deviceId)}`;
    try {
      const socket = new WebSocket(url);
      entry.socket = socket;
      socket.onopen = () => { entry.retryMs = 1000; };
      socket.onmessage = event => {
        try { farm3dApplyLiveState(JSON.parse(event.data)); }
        catch (error) { console.warn('[3D WS] invalid message', error); }
      };
      socket.onclose = () => {
        if (farm3dLiveStopped || farm3dLiveSockets.get(deviceId) !== entry) return;
        entry.retryTimer = setTimeout(connect, entry.retryMs);
        entry.retryMs = Math.min(entry.retryMs * 2, 30000);
      };
      socket.onerror = () => socket.close();
    } catch (error) {
      console.warn('[3D WS] connection failed', deviceId, error);
      entry.retryTimer = setTimeout(connect, entry.retryMs);
      entry.retryMs = Math.min(entry.retryMs * 2, 30000);
    }
  };
  connect();
}

function farm3dSyncLiveSockets() {
  if (farm3dLiveStopped) return;
  const ids = new Set();
  if (farmDesignState?.design_mode === 'custom') {
    const bindings = readCustomHardwareBindings();
    for (const binding of Object.values(bindings || {})) {
      const id = Number(binding?.device_id);
      if (Number.isInteger(id) && id > 0) ids.add(id);
    }
  } else {
    for (const binding of farmBindings) {
      const id = Number(binding.device_id);
      if (Number.isInteger(id) && id > 0) ids.add(id);
    }
    // Legacy zone-01 binding remains supported.
    if (MQTT_SITE_ID === 46 && Number.isInteger(MQTT_DEVICE_ID) && MQTT_DEVICE_ID > 0) ids.add(MQTT_DEVICE_ID);
  }
  for (const [id, entry] of farm3dLiveSockets) {
    if (ids.has(id)) continue;
    farm3dLiveSockets.delete(id);
    if (entry.retryTimer) clearTimeout(entry.retryTimer);
    if (entry.socket) entry.socket.close();
  }
  for (const id of ids) farm3dOpenLiveSocket(id);
}
const farm3dLiveDiscoveryTimer = setInterval(farm3dSyncLiveSockets, 4000);
farm3dSyncLiveSockets();
const farm3dStopLiveSockets = () => {
  farm3dLiveStopped = true;
  clearInterval(farm3dLiveDiscoveryTimer);
  for (const entry of farm3dLiveSockets.values()) {
    if (entry.retryTimer) clearTimeout(entry.retryTimer);
    if (entry.socket) entry.socket.close();
  }
  farm3dLiveSockets.clear();
};
// React effect cleanup is outside start(); keep the cleanup callable there.
stopFarm3dLiveSockets = farm3dStopLiveSockets;


// 최초 바인딩 상태는 bootstrapFarmDesign() -> loadStandardFarmTemplate()에서
// 화면 공개 전에 1회 완료한다. 이후 갱신만 아래 interval이 담당한다.
setInterval(function() {
  if (farmDesignState?.design_mode !== 'custom' && standardTemplateLoaded) {
    loadBoundZoneStates();
  }
}, 3000);

// =========================================================
// INITIAL VIEW REVEAL
// 새로고침 시 설계 복원/객체 생성/최초 장치 상태가 화면에 조립되는 과정을
// 사용자에게 노출하지 않고, 준비된 결과만 한 번에 보여준다.
// =========================================================
await bootstrapFarmDesign();

// 실제 농장 위성지도가 있는 경우 Kakao idle -> 경계 정렬 -> 카메라 fit까지 기다린다.
// 네트워크 문제로 idle이 오지 않아도 최대 4초 뒤에는 기존 3D를 표시한다.
const initialGeo = readFarmGeo();
if (initialGeo?.boundary?.length >= 3) {
  await new Promise((resolve) => {
    const startedAt = Date.now();
    const waitForSatellite = () => {
      const mapEl = document.getElementById('mainSatelliteMap');
      if (mapEl?.style.visibility === 'visible' || Date.now() - startedAt >= 4000) {
        resolve();
        return;
      }
      requestAnimationFrame(waitForSatellite);
    };
    waitForSatellite();
  });
}

// Three 렌더러가 최종 상태를 두 프레임 그린 뒤 한 번에 공개한다.
await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));

const readyRoot = document.getElementById('smartfarm3dAccessRoot');
const readyLoading = document.getElementById('smartfarm3dAccessLoading');
if (readyRoot) readyRoot.style.visibility = 'visible';
if (readyLoading) readyLoading.style.display = 'none';
    };

    // React must not own #mainSatelliteMap.
    // CSS3DObject reparents this element internally. If React renders it,
    // browser back/unmount can make React remove it from the wrong parent.
    const viewerElement = document.getElementById('viewer');
    if (viewerElement && !document.getElementById('mainSatelliteMap')) {
      mainSatelliteMapElement = document.createElement('div');
      mainSatelliteMapElement.id = 'mainSatelliteMap';
      viewerElement.appendChild(mainSatelliteMapElement);
    }

    start().catch((error) => {
      console.error('[콩꼬물 3D] 초기화 실패:', error);
      const status = document.getElementById('systemStatus');
      if (status) status.textContent = '초기화 실패';
    });

    return () => {
      disposed = true;
      if (stopFarm3dLiveSockets) stopFarm3dLiveSockets();

      // This node is intentionally outside React ownership.
      // Remove it from whichever parent CSS3DRenderer currently uses.
      if (mainSatelliteMapElement && mainSatelliteMapElement.parentNode) {
        mainSatelliteMapElement.parentNode.removeChild(mainSatelliteMapElement);
      }
      mainSatelliteMapElement = null;

      document.title = previousTitle;
      document.body.className = previousBodyClassName;
    };
  }, []);

  return (
    <>
      <style>{PAGE_CSS}</style>
      <style>{`
#customAutomationButton{background:rgba(3,105,161,.94)}
#customDesignActions{height:auto!important;flex-wrap:wrap;max-width:calc(100vw - 430px)}
#customDesignActions button{flex-shrink:0}
#customTemperatureButton,#customHumidityButton,#customNutrientButton{background:rgba(3,105,161,.94)}
@media(max-width:700px){#customDesignActions{max-width:none!important;flex-wrap:wrap!important;height:auto!important;bottom:calc(8px + env(safe-area-inset-bottom))!important}#customDesignActions button{flex:1 1 calc(25% - 8px)!important;min-width:0!important;font-size:10px!important}}
@media(max-width:700px){#customDesignActions{left:8px!important;right:8px!important;gap:5px!important}#customDesignActions button{flex:1 1 0!important;width:auto!important;padding:0 3px!important;font-size:10px!important}}
/* Mobile: two non-overlapping rows for five controls. */
@media(max-width:700px){
  #farmMapButton,#farmDesignResetButton{
    position:fixed!important;top:auto!important;bottom:calc(60px + env(safe-area-inset-bottom))!important;
    width:calc((100vw - 24px)/2)!important;height:42px!important;
    font-size:12px!important;z-index:90!important;
  }
  #farmMapButton{left:8px!important;right:auto!important}
  #farmDesignResetButton{left:calc(16px + (100vw - 24px)/2)!important;right:auto!important}
  #customDesignActions{
    position:fixed!important;top:auto!important;bottom:calc(8px + env(safe-area-inset-bottom))!important;
    left:8px!important;right:8px!important;width:calc(100vw - 16px)!important;
    height:42px!important;gap:8px!important;z-index:90!important;
    justify-content:stretch!important;
  }
  body.custom-design-mode.custom-design-completed #customDesignActions{display:flex!important}
  body.custom-design-mode.custom-design-editing #customDesignActions{display:none!important}
  #customDesignActions button{
    flex:1 1 0!important;min-width:0!important;width:0!important;height:42px!important;
    padding:0 3px!important;font-size:11px!important;white-space:nowrap!important;
  }
  .custom-valve-operation-hint{bottom:calc(112px + env(safe-area-inset-bottom))!important}
  #customDesignerPanel,#customDevicePanel,#farmEditor,#zonePanel{
    bottom:calc(112px + env(safe-area-inset-bottom))!important;
  }
  #userOperationPanel{top:auto!important;bottom:calc(112px + env(safe-area-inset-bottom))!important;
    max-height:min(65dvh,560px)!important;}
}
#userOperationPanel{position:fixed;right:12px;top:168px;z-index:180;width:min(460px,calc(100vw - 24px));max-height:min(70vh,620px);display:none;overflow:hidden;border:1px solid rgba(255,255,255,.18);border-radius:16px;background:rgba(15,23,42,.97);color:#fff;box-shadow:0 14px 44px rgba(0,0,0,.38)}
#userOperationPanel.open{display:flex;flex-direction:column}
.user-op-head{display:flex;align-items:center;justify-content:space-between;padding:12px 14px;border-bottom:1px solid rgba(255,255,255,.12)}
#userOperationTitle{font-size:15px;font-weight:900}
#userOperationClose{border:0;border-radius:8px;background:rgba(255,255,255,.10);color:#fff;width:32px;height:32px;font-size:18px;cursor:pointer}
#userOperationList{overflow:auto;padding:8px;display:grid;gap:7px}
.user-op-item{display:grid;grid-template-columns:1fr auto;gap:10px;align-items:center;padding:11px 12px;border:1px solid rgba(255,255,255,.12);border-radius:12px;background:rgba(255,255,255,.06)}
.user-op-main{min-width:0}.user-op-name{font-size:13px;font-weight:900;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.user-op-meta{margin-top:3px;font-size:11px;color:rgba(255,255,255,.66);white-space:normal}
.user-op-state{font-size:12px;font-weight:900;padding:6px 8px;border-radius:999px;background:rgba(255,255,255,.09);white-space:nowrap}
.user-op-state.on{background:#166534}.user-op-state.off{background:#334155}.user-op-state.sensor{background:#075985}.user-op-state.bad{background:#92400e}
.user-op-actions{grid-column:1/-1;display:flex;gap:6px;flex-wrap:wrap}
.user-op-actions button{flex:1;border:1px solid rgba(255,255,255,.14);border-radius:9px;background:rgba(255,255,255,.08);color:#fff;min-height:36px;font-size:11px;font-weight:800;cursor:pointer}
.user-op-actions button.primary{background:#0369a1}.user-op-actions button.danger{background:#991b1b}
.user-op-empty{padding:22px 12px;text-align:center;font-size:12px;color:rgba(255,255,255,.62)}
@media(max-width:700px){#userOperationPanel{top:auto;bottom:calc(65px + env(safe-area-inset-bottom));left:8px;right:8px;width:auto;max-height:48vh}}
`}</style>
      <div
        id="smartfarm3dAccessLoading"
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 99999,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: '#e9eef2',
          color: '#475569',
          fontSize: '14px',
          fontWeight: 600,
        }}
      >
        농장 불러오는 중...
      </div>
      <div id="smartfarm3dAccessRoot" style={{ visibility: 'hidden' }}>
<div id="farmDesignStartModal" aria-modal="true" role="dialog">
  <div className="farm-design-start-card">
    <div className="farm-design-start-title">콩꼬물 스마트팜 초기 설계</div>
    <div className="farm-design-start-desc">
      실제 농장 위치와 경계를 기준으로 스마트팜을 설계합니다.<br />
      직접 설계는 빈 농장에서 시작하고, 표준형은 현재 400평 관수시스템을 그대로 불러옵니다.
    </div>
    <div className="farm-design-choice-grid">
      <button id="farmDesignCustomButton" className="farm-design-choice" type="button">
        <strong>＋ 새로 직접 설계</strong>
        <span>빈 농장에서 물탱크·펌프·필터·밸브·ESP32·센서와 배관을 직접 생성하고 배치합니다.</span>
      </button>
      <button id="farmDesignTemplateButton" className="farm-design-choice primary" type="button">
        <strong>400평 표준형으로 시작</strong>
        <span>현재 완성된 관수시스템 GLB와 16개 구역, 실제 ESP32/MQTT 릴레이 연동을 그대로 사용합니다.</span>
      </button>
    </div>
  </div>
</div>
<div id="farmDesignBadge"></div>

<div id="customDesignActions">
  <button id="customDesignEditButton" type="button">설계 편집</button>
  <button id="customDeviceConnectButton" type="button">장치 연결</button>
  <button id="customAutomationButton" type="button">+ 자동관수</button>
  <button id="customTemperatureButton" type="button">+ 자동온도</button>
  <button id="customHumidityButton" type="button">+ 자동습도</button>
  <button id="customNutrientButton" type="button">+ 자동양액</button>
</div>
<section id="userOperationPanel" aria-label="자동관수 관리">
  <div className="user-op-head"><div id="userOperationTitle">자동관수</div><button id="userOperationClose" type="button" aria-label="닫기">×</button></div>
  <div id="userOperationList"></div>
</section>

<aside id="customDevicePanel">
  <div className="custom-device-title">실제 장치 연결</div>
  <div className="custom-device-help">3D에서 ESP32·토양센서·밸브를 클릭한 뒤 실제 장치를 연결하세요. 기존 콩꼬물 ESP32 API를 그대로 사용합니다.</div>
  <div className="custom-device-section">선택한 3D 객체</div>
  <div id="customDeviceSelected" className="custom-device-card"><strong>선택 없음</strong><span>연결할 3D 객체를 클릭하세요.</span></div>
  <div className="custom-device-section">실제 ESP32</div>
  <select id="customHardwareDeviceSelect" className="custom-device-select"><option value="">ESP32를 선택하세요</option></select>
  <div id="customEntitySection" style={{display: 'none'}}>
    <div className="custom-device-section">센서 / 릴레이 Entity</div>
    <select id="customHardwareEntitySelect" className="custom-device-select"><option value="">Entity를 선택하세요</option></select>
  </div>
  <div className="custom-device-actions">
    <button id="customHardwareSave" className="primary" type="button">연결 저장</button>
    <button id="customHardwareUnbind" className="danger" type="button">연결 해제</button>
    <button id="customRelayOn" type="button" style={{display: 'none'}}>릴레이 ON</button>
    <button id="customRelayOff" type="button" style={{display: 'none'}}>릴레이 OFF</button>
    <button id="customDevicePanelClose" type="button">닫기</button>
    <button id="customHardwareRefresh" type="button">상태 새로고침</button>
  </div>
  <div id="customDeviceStatus">3D 객체를 선택하세요.</div>
</aside>

<aside id="customDesignerPanel">
  <div className="custom-designer-title">스마트팜 직접 설계</div>
  <div className="custom-designer-help">시설물을 선택한 뒤 농장 위를 클릭해 배치하세요. 1 Three.js unit = 실제 1m입니다.</div>
  <div className="custom-designer-section">시설물 추가</div>
  <div className="custom-tool-grid" id="customFacilityTools">
    <button type="button" data-facility="tank">물탱크</button>
    <button type="button" data-facility="pump">펌프</button>
    <button type="button" data-facility="filter">필터</button>
    <button type="button" data-facility="valve">밸브</button>
    <button type="button" data-facility="esp32">ESP32</button>
    <button type="button" data-facility="soil_sensor">토양센서</button>
    <button type="button" data-facility="climate_sensor">온습도센서</button>
    <button type="button" data-facility="power_switch">전원버튼</button>
    <button type="button" data-facility="nutrient_mixer">양액기</button>
    <button type="button" data-facility="cctv">CCTV</button>
  </div>
  <div className="custom-designer-section">배관 그리기</div>
  <div className="custom-pipe-row" id="customPipeTools">
    <button type="button" data-pipe="main">메인배관</button>
    <button type="button" data-pipe="branch">분기관</button>
    <button type="button" data-pipe="drip">점적호스</button>
  </div>
  <div className="custom-designer-help" style={{marginTop: '7px'}}>배관은 시작점 클릭 → 끝점 클릭으로 생성합니다.</div>
  <div className="custom-designer-section">선택 객체 편집</div>
  <div className="custom-edit-grid">
    <button id="customMoveButton" type="button">이동축</button>
    <button id="customRotateLeft" type="button">↶ 15°</button>
    <button id="customRotateRight" type="button">↷ 15°</button>
    <button id="customDeleteButton" className="custom-danger" type="button">삭제</button>
    <button id="customCancelTool" type="button">도구 취소</button>
    <button id="customSaveButton" className="custom-primary" type="button">설계 저장 · 완료</button>
  </div>
  <div className="custom-designer-section">현재 설계 객체</div>
  <div id="customObjectList" className="custom-object-list"></div>
  <div id="customDesignerStatus">시설물을 선택하거나 배관 도구를 선택하세요.</div>
</aside>



<button id="farmEditButton" type="button">편집</button>
<button id="farmMapButton" type="button">농장 위치 설정</button>
<button id="farmDesignResetButton" type="button">설계 다시 선택</button>

<div id="farmMapModal">
  <div className="farm-map-shell">
    <div className="farm-map-toolbar">
      <strong>실제 농장 경계 설정</strong>
      <input id="farmAddressSearch" type="text" placeholder="주소 또는 장소 검색" />
      <button id="farmAddressSearchButton" type="button">검색</button>
      <button id="farmBoundaryDrawButton" type="button" className="primary">경계 그리기</button>
      <button id="farmBoundarySaveButton" type="button" className="primary">경계 저장</button>
      <button id="farmBoundaryClearButton" type="button" className="danger">경계 삭제</button>
      <button id="farmMapCloseButton" type="button">닫기</button>
    </div>
    <div id="farmKakaoMap"></div>
    <div id="farmMapInfo">위성지도에서 실제 농장을 찾은 뒤 ‘경계 그리기’를 눌러 농지 외곽을 클릭하세요.</div>
  </div>
</div>

<aside id="farmEditor">
  <div className="farm-editor-title">ESP32 관수 위치 편집</div>
  <div className="farm-editor-help">미배치 ESP32를 선택한 뒤 3D 농장의 원하는 관수 구역을 클릭하세요.</div>
  <div className="farm-editor-section-title">미배치 ESP32</div>
  <div id="farmUnassignedDevices" className="farm-device-list"></div>
  <div className="farm-editor-section-title">현재 배치</div>
  <div id="farmCurrentBindings" className="farm-device-list"></div>
  
  <div id="objectMovePanel">
    <div className="object-editor-title">관수시스템 전체 배치</div>
    <div className="farm-editor-help">
      관수시스템 전체를 중심점 기준으로 회전하고 실제 농장 위치에 맞게 이동합니다.
    </div>
    <div className="object-move-actions">
      <button id="farmRootRotateLeft" type="button">↶ 전체 15°</button>
      <button id="farmRootTransformReset" type="button">배치 초기화</button>
      <button id="farmRootRotateRight" type="button">↷ 전체 15°</button>
    </div>
    <div className="object-move-actions">
      <button id="farmRootMoveLeft" type="button">← 1m</button>
      <button id="farmRootMoveForward" type="button">↑ 1m</button>
      <button id="farmRootMoveRight" type="button">1m →</button>
    </div>
    <div className="object-move-actions">
      <button id="farmRootMoveBack" type="button">↓ 1m</button>
      <button id="farmRootTransformMode" type="button">이동축 표시</button>
      <button id="farmRootCenterView" type="button">중심 보기</button>
    </div>
    <div id="farmRootTransformInfo" className="object-move-info">
      관수시스템 전체 배치를 조정할 수 있습니다.
    </div>

    <div className="object-editor-title" style={{marginTop: '14px'}}>3D 시설물 위치 편집</div>
    <div className="farm-editor-help">
      물탱크·펌프·센서·밸브·스프링클러 등 시설물을 선택하고
      시설물을 선택하면 이동축이 표시됩니다. 축을 마우스/터치로 직접 잡아 드래그하거나,
      기존처럼 3D 화면의 원하는 땅을 클릭해 이동할 수 있습니다.
      위치는 이 농장(site_id)별로 저장되어 새로고침 후에도 유지됩니다.
    </div>
    <div id="movableObjectList"></div>
    <div className="object-move-actions">
      <button id="objectRotateLeft" type="button" disabled>↶ 15°</button>
      <button id="objectReset" type="button" disabled>위치 초기화</button>
      <button id="objectRotateRight" type="button" disabled>↷ 15°</button>
    </div>
    <div id="objectMoveInfo" className="object-move-info">이동할 시설물을 선택하세요.</div>
  </div>
  <div id="farmEditorStatus"></div>

</aside>




<div id="cctvPreviewModal" role="dialog" aria-modal="true" aria-label="CCTV 실시간보기">
  <div className="cctv-preview-shell">
    <div className="cctv-preview-head">
      <div id="cctvPreviewTitle" className="cctv-preview-title">CCTV 실시간보기</div>
      <div className="cctv-preview-actions">
        <button id="cctvPreviewRefresh" type="button">새로고침</button>
        <button id="cctvPreviewClose" type="button">닫기</button>
      </div>
    </div>
    <div className="cctv-preview-body">
      <img id="cctvPreviewImage" alt="CCTV 실시간보기" />
      <iframe id="cctvPreviewFrame" title="CCTV 실시간보기" allow="autoplay; fullscreen; picture-in-picture" allowFullScreen style={{display:'none',width:'100%',height:'100%',border:0,background:'#000'}} />
      <div id="cctvPreviewMessage">카메라 연결 중...</div>
    </div>
  </div>
</div>

<div id="viewer"></div>


{/* =======================================================
     TITLE
======================================================== */}

<div id="titleBox">

  <div className="title">
    농장
  </div>

  <div id="systemStatus">
    GLB 로딩 중...
  </div>

</div>


{/* =======================================================
     ZONE INFORMATION
======================================================== */}

<div id="zonePanel">

  <div id="zoneHeader">

    <div id="zoneTitle">
      1구역
    </div>

    <button id="closePanel">
      ×
    </button>

  </div>


  <div id="zoneContent">


    <div className="stateBox">

      <div className="stateLabel">
        현재 관수 상태
      </div>

      <div
        id="zoneState"
        className="off">

        정지

      </div>

    </div>


    <div className="infoRow">

      <div className="infoLabel">
        토양수분
      </div>

      <div
        id="soilValue"
        className="infoValue">

        -

      </div>

    </div>


    <div className="infoRow">

      <div className="infoLabel">
        마지막 관수
      </div>

      <div
        id="lastWater"
        className="infoValue">

        -

      </div>

    </div>


    <div className="infoRow">

      <div className="infoLabel">
        구역 번호
      </div>

      <div
        id="zoneNumberValue"
        className="infoValue">

        -

      </div>

    </div>


    <div className="controlButtons">

      <button id="startWater">
        관수 시작
      </button>

      <button id="stopWater">
        관수 정지
      </button>

    </div>


    <div id="message">

      1구역 실제 MQTT 연결

    </div>


  </div>

</div>


<div id="help">
  밸브 또는 관수라인을 선택하세요
</div>







<div className="custom-valve-operation-hint">밸브를 클릭하면 실제 관수를 ON / OFF 합니다</div>
      </div>
    </>
  );
}
